// Connections page strings: the MCP servers section, the header link, the scoped account
// notice (UX WS-03, WS-06) and the Connected agents section and chat card copy.
//
// Namespace `connections`; owner T2-UI2 (one namespace for the whole Connections slice).
// The 24-language values are DES2's approved set, DES1 final:
// org/work/DES2/handoff/connections-final-24.md. Placeholders are `{name}`, filled with
// `formatCopy`. Protocol, tool and auth identifiers are never translated here.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  // The direction is in the name: servers Poneva calls, not agents that call Poneva.
  mcpServersTitle: 'Custom connections',
  mcpServersSubtitle: 'Connect your own tool server. Poneva asks before it changes anything.',
  // The trust line, a separate line after the subtitle.
  mcpServersApproval: 'You approve every change. Reads follow each server’s reading choice.',
  mcpServersAdd: 'Add connection',
  // The Connections header link (rendered with an arrow).
  connectedAgentsLink: 'Connected agents',
  // Inside My connections, when the workspace has no account-connection service.
  accountConnectionsUnavailable: 'Account connections aren’t available in this workspace yet.',
  // The Connected agents section (T2-F1 copy, one namespace): title, subtitle with {company}.
  connectedAgentsTitle: 'Connected agents',
  connectedAgentsSubtitle: 'Agents that read {company}’s knowledge through Poneva.',
  // DES2 WS-12: the same line while the company's name is not known yet.
  connectedAgentsSubtitleAny: 'Agents that read your company’s knowledge through Poneva.',
  connectedAgentsSuggest: 'If you allow it, they suggest updates for a person to review.',
  connectedAgentsList: 'Connected now',
  // agent_access_disabled in agent-connections/format.ts.
  connectingAgentsOff: 'Connecting agents isn’t available for this workspace yet.',
  // Chat progress line while the Connected agents state is read (tool-cards.tsx).
  checkingConnectedAgents: 'Checking connected agents…',
  // The chat card's failure line for the Connected agents tool, with a link to Connections setup (no retry).
  connectedAgentsLoadFailed: 'Connected agents could not be loaded.',
  connectedAgentsOpen: 'Open Connected agents',
  // DES1 HC-08: agent-connections/format.ts (status, times, counts, errors, address warnings).
  agentTimeNotYet: 'not yet',
  agentTimeUnknown: 'at an unknown time',
  agentTimeJustNow: 'just now',
  agentTimeOnDate: 'on {date}',
  agentErrorNotFound: 'This agent is no longer connected. The list shows the current state.',
  agentErrorScopeUnavailable: 'That access is not available in this workspace.',
  agentErrorTooManyKeys: 'You have as many access keys as Poneva allows. Revoke one first.',
  agentErrorInvalidInput: 'Check the key’s name and try again.',
  agentNotRecorded: 'Not recorded',
  agentMethodKey: 'Access key',
  agentMethodSignedIn: 'Signed in',
  agentStateReconnect: 'Reconnect needed',
  agentStateReady: 'Ready',
  agentStateKeyUnused: 'Created — not used yet',
  agentStateWaiting: 'Authorized — waiting for first read',
  agentLastUsed: 'Last used {when}',
  agentKeyExpires: 'Access key · expires {date}',
  agentSignInEnds: 'Signed in · sign-in ends {date}',
  agentsNone: 'No external agents connected yet',
  agentsConnectedCount: 'one:agent connected|other:agents connected',
  agentsWaitingCount: 'one:waiting|other:waiting',
  agentsReconnectCount: 'one:to reconnect|other:to reconnect',
  addressLocalNetwork:
    'Only reachable on this network. Agents that run in the cloud can’t use this address.',
  addressLocalComputer:
    'Only reachable from this computer. Agents that run in the cloud can’t use this address.',
  addressUnreachable:
    'Poneva can’t reach this address right now, so external agents probably can’t either.',
  // WS-12 (DES2, sole writer): the rest of Connected agents and its chat card. `{app}` is the
  // name the agent reported, `{company}` the company name. Protocol words (MCP, OAuth, JSON,
  // Authorization, Bearer) stay as they are. The external agent's setup prompt stays English.
  agentUnnamed: 'Unnamed agent',
  agentCompanyFallback: 'your company',
  agentErrorUnreadable: 'Poneva returned a response this page could not read. Try again.',
  agentsLoadFailed: 'Could not load your external agents.',
  agentsLoading: 'Loading external agents…',
  agentsConnectedShort: 'one:connected|other:connected',
  agentsAddressTitle: 'MCP address',
  agentsPrivacy:
    'Agents act as you and only ever see {company}. They read approved knowledge, never private chats, personal memory or private files. Anything more stays off unless you allow it.',
  agentsSetupHide: 'Hide setup',
  agentsSetupAnother: 'Connect another agent',
  agentsHelpInChat: 'Get help in chat',
  // Sent as the person's own chat message.
  agentsHelpStarter: 'Help me connect an external agent to Poneva over MCP.',
  agentNoticeReady: '{app} read Poneva knowledge. The connection is ready.',
  agentNoticeAuthorized:
    '{app} is authorized. Ask it a question to finish checking the connection.',
  agentScopeUnconfirmed:
    'We couldn’t confirm the change for {app}. The details below show what Poneva has now.',
  agentScopeNotChanged: '{app}’s access did not change. Try again.',
  agentConfirmRevokeKey:
    'Revoke the access key “{app}”? Any agent using it stops reading {company} knowledge at once.',
  agentConfirmDisconnect:
    'Disconnect {app}? {app} will no longer be able to read {company} knowledge. Your Poneva sign-in is not affected.',
  agentKeyRevoked: 'The access key “{app}” is revoked.',
  agentDisconnected: '{app} is disconnected.',
  agentDisconnectUnconfirmed:
    'We couldn’t confirm that {app} was disconnected. The list shows the current state.',
  agentDisconnectFailed: '{app} is still connected. Try again.',
  // The setup steps.
  setupTitle: 'Connect an external agent',
  setupManualLabel: 'Or set it up yourself',
  setupAddTitle: 'Add Poneva as an MCP server',
  setupAddBody:
    'In the agent’s MCP or connector settings, add a remote MCP server (Streamable HTTP) with the address above.',
  setupConfigToggle: 'Using a config file?',
  setupConfigBody: 'Agents that list their MCP servers in a JSON file take this:',
  setupSignInTitle: 'Sign in',
  // “Allow” is the button on Poneva’s sign-in page.
  setupSignInBody:
    'The agent opens Poneva in your browser. Check the company and what the agent may do, then choose Allow.',
  setupSignInFallback:
    'If the sign-in window closes by itself or never shows Poneva, or the agent asks for a token or API key, use an access key instead.',
  setupTryTitle: 'Try it',
  setupTryAsk: 'Ask the agent:',
  // A question the person asks their agent.
  setupSampleQuestion: 'What is our current product direction? Cite the source.',
  setupTryNote: 'It shows as Ready here after its first read.',
  setupPromptTitle: 'Let your coding agent set it up',
  setupPromptBody:
    'Paste this prompt into Claude Code, Codex, or another coding agent. It includes your MCP address and guides the agent through setup. You approve access when you sign in.',
  // Copy buttons: `{what}` is one of the copy… objects below.
  copyAction: 'Copy',
  copiedAction: 'Copied',
  copyNamed: 'Copy {what}',
  copiedNamed: 'Copied {what}',
  copiedStatus: 'Copied {what}.',
  copyManualHint:
    'Copying isn’t available in this browser. The text is selected; copy it with your keyboard.',
  copyPromptShow: 'View prompt',
  copyPromptHide: 'Hide prompt',
  copyWhatAddress: 'the Poneva MCP address',
  copyWhatConfig: 'the MCP server configuration',
  copyWhatSample: 'the sample question',
  copyWhatSampleFor: 'the sample question for {app}',
  copyWhatPrompt: 'setup prompt',
  copyWhatKey: 'the access key for {app}',
  // Access keys.
  keyCreateAction: 'Create an access key',
  keySecret:
    'Treat it like a password. Paste it only into an agent’s MCP, connector or secret settings, never into a website or code that others can open.',
  keyFormNote:
    'For an agent that can’t sign in. It reads {company} knowledge as you until it expires or you revoke it; changing your password or leaving the company also ends it.',
  keyName: 'Name',
  keyNamePlaceholder: 'e.g. Nightly report job',
  keyLifetime: 'Expires after',
  keyDays: 'one:day|other:days',
  keyAlsoAllow: 'Also allow',
  keyCreating: 'Creating…',
  keyCreate: 'Create key',
  keyCancel: 'Cancel',
  keyUnconfirmed:
    'We couldn’t confirm the key was created. If it shows in the list, revoke it and create a new one.',
  keyNotCreated: 'The access key was not created. Try again.',
  keyNewTitle: 'Access key for {app}',
  keyCopyNow: 'Copy it now. Poneva shows it only once and stores only a fingerprint of it.',
  // `{token}`, `{apiKey}` and `{header}` show in bold: the field names an agent's settings use.
  keyTokenRule: 'A {token} or {apiKey} field takes the key alone.',
  keyTermToken: 'token',
  keyTermApiKey: 'API key',
  keyHeaderRule:
    'A {header} field takes the name {name} and the value {value}, with your key in place of {key}.',
  keyTermHeader: 'header',
  keyEndsOn:
    'It stops working on {date}, when you revoke it, and when you change your password or leave the company.',
  keyEndsExpiry:
    'It stops working when it expires, when you revoke it, and when you change your password or leave the company.',
  keyDone: 'Done',
  // A listed agent.
  scopeProposeTitle: 'Suggest knowledge updates',
  scopeProposeDescribe:
    '{app} can suggest page changes. A company publisher reviews each one before anything changes.',
  scopeProposeConfirmOn:
    'Let {app} suggest knowledge updates for {company}? Each suggestion waits in Knowledge → For review until a company publisher approves it. Nothing changes on its own.',
  scopeProposeConfirmOff:
    'Stop {app} from suggesting knowledge updates? Suggestions it already sent stay in review.',
  scopeProposeEnabled:
    '{app} can now suggest knowledge updates. If the option doesn’t appear in {app} yet, restart it.',
  scopeProposeDisabled: '{app} can no longer suggest knowledge updates.',
  scopeLibraryReadTitle: 'Read your Poneva work',
  scopeLibraryReadDescribe:
    '{app} can find and read the chats, Library, files, meetings, Skills, agents and Apps you can open in Poneva, and read from your connected services. It can also read the company’s shopping assistants and what shoppers said to them.',
  scopeLibraryReadConfirmOn:
    'Let {app} read your Poneva work in {company}? It finds and reads what you can already open in Poneva, as you.',
  scopeLibraryReadConfirmOff:
    'Stop {app} from reading your Poneva work? It can then no longer save work or ask Poneva either.',
  scopeLibraryReadEnabled:
    '{app} can now read your Poneva work. If the option doesn’t appear in {app} yet, restart it.',
  scopeLibraryReadDisabled: '{app} can no longer read your Poneva work.',
  scopeLibraryWriteTitle: 'Save work and ask Poneva',
  scopeLibraryWriteDescribe:
    '{app} can save work to your Library, where new work stays private to you, and save new versions of work you can edit (earlier versions stay in History). It can save drafts of Skills and of the company’s shopping assistant designs, which colleagues who can edit them see; only a person publishes them in Poneva. It can also ask Poneva in private chats, which uses credits. It can also start reads of the company’s web shop to repair a shopping assistant and ask it test questions; both use company credits.',
  scopeLibraryWriteConfirmOn:
    'Let {app} save work to your Library in {company}, including new versions of work you can edit and drafts colleagues can see, and ask Poneva for you? Asking uses credits. It can also read your Poneva work. Shop reads and test questions for shopping assistants use company credits too.',
  scopeLibraryWriteConfirmOff:
    'Stop {app} from saving work and asking Poneva? What it already saved stays in your Library.',
  scopeLibraryWriteEnabled:
    '{app} can now save work and ask Poneva. If the option doesn’t appear in {app} yet, restart it.',
  scopeLibraryWriteDisabled: '{app} can no longer save work or ask Poneva.',
  agentReadAccess:
    'Read company knowledge: The company profile and the approved pages of the company knowledge wiki. Chats, files, meetings and personal memory are not included.',
  agentHintKeyStale:
    'Poneva’s address changed after this key was made, so Poneva refuses it. Revoke it and create a new key.',
  agentHintStale:
    'Poneva’s address changed after {app} connected, so Poneva refuses it. Disconnect it, then connect {app} again with the address above.',
  agentHintKeyWaiting: 'It shows as Ready once an agent uses the key.',
  agentHintWaiting: 'Ask {app} a question to finish checking the connection.',
  agentHintTryAsking: 'Try asking:',
  agentFactCreated: 'Created',
  agentFactConnected: 'Connected',
  agentFactExpires: 'Expires',
  agentFactSignInEnds: 'Sign-in ends',
  agentFactAccess: 'Access',
  agentNameNote:
    'The name is the one the agent reported; Poneva can’t verify it. Disconnect any agent you don’t recognise.',
  agentSignInNote: 'When its sign-in ends, the agent asks you to sign in again.',
  agentWorking: 'Working…',
  agentRevokeKey: 'Revoke key',
  agentDisconnect: 'Disconnect',
  // The chat card.
  cardAgentsLabel: 'Your connected agents',
  // `{more}` is cardMoreCount with its number: “and 2 more in Connections”.
  cardMore: 'and {more}',
  cardMoreCount: 'one:more in Connections|other:more in Connections',
  cardOpenSetup: 'Open setup',
  cardChecking: 'Checking your external agents…',
  cardTryAgain: 'Try again',
} as const;

export type ConnectionsCopyKey = keyof typeof en;
export type ConnectionsCopy = Readonly<Record<ConnectionsCopyKey, string>>;

const others: Record<string, Record<ConnectionsCopyKey, string>> = {
  bg: {
    mcpServersTitle: 'Собствени връзки',
    mcpServersSubtitle:
      'Свържете собствен сървър с инструменти. Poneva пита, преди да промени каквото и да е.',
    mcpServersApproval:
      'Вие одобрявате всяка промяна. За четенето важи изборът, който сте направили за всеки сървър.',
    mcpServersAdd: 'Добавяне на връзка',
    connectedAgentsLink: 'Свързани агенти',
    accountConnectionsUnavailable:
      'Връзките с акаунти все още не са налични в това работно пространство.',
    connectedAgentsTitle: 'Свързани агенти',
    connectedAgentsSubtitle: 'Агенти, които четат знанията на {company} чрез Poneva.',
    connectedAgentsSuggest: 'Ако разрешите, те предлагат промени, които човек преглежда.',
    connectedAgentsList: 'Свързани сега',
    connectingAgentsOff: 'Свързването на агенти все още не е налично за това работно пространство.',
    checkingConnectedAgents: 'Проверка на свързаните агенти…',
    connectedAgentsLoadFailed: 'Свързаните агенти не можаха да бъдат заредени.',
    connectedAgentsOpen: 'Отваряне на Свързани агенти',
    agentTimeNotYet: 'все още не',
    agentTimeUnknown: 'в неизвестен момент',
    agentTimeJustNow: 'току-що',
    agentTimeOnDate: 'на {date}',
    agentErrorNotFound: 'Този агент вече не е свързан. Списъкът показва текущото състояние.',
    agentErrorScopeUnavailable: 'Този достъп не е наличен в това работно пространство.',
    agentErrorTooManyKeys:
      'Имате толкова ключове за достъп, колкото позволява Poneva. Първо отменете един.',
    agentErrorInvalidInput: 'Проверете името на ключа и опитайте отново.',
    agentNotRecorded: 'Не е записано',
    agentMethodKey: 'Ключ за достъп',
    agentMethodSignedIn: 'С вход в акаунт',
    agentStateReconnect: 'Нужно е повторно свързване',
    agentStateReady: 'Готов',
    agentStateKeyUnused: 'Създаден – още не е използван',
    agentStateWaiting: 'Упълномощен – чака първо четене',
    agentLastUsed: 'Последно използван {when}',
    agentKeyExpires: 'Ключ за достъп · изтича на {date}',
    agentSignInEnds: 'С вход в акаунт · входът изтича на {date}',
    agentsNone: 'Все още няма свързани външни агенти',
    agentsConnectedCount: 'one:свързан агент|other:свързани агента',
    agentsWaitingCount: 'one:чака|other:чакат',
    agentsReconnectCount: 'one:за повторно свързване|other:за повторно свързване',
    addressLocalNetwork:
      'Достъпен е само в тази мрежа. Агентите, които работят в облака, не могат да използват този адрес.',
    addressLocalComputer:
      'Достъпен е само от този компютър. Агентите, които работят в облака, не могат да използват този адрес.',
    addressUnreachable:
      'Poneva не може да достигне този адрес в момента, така че външните агенти вероятно също не могат.',
    agentUnnamed: 'Агент без име',
    agentCompanyFallback: 'вашата компания',
    agentErrorUnreadable:
      'Poneva върна отговор, който тази страница не може да прочете. Опитайте отново.',
    agentsLoadFailed: 'Външните ви агенти не можаха да бъдат заредени.',
    agentsLoading: 'Зареждане на външните агенти…',
    agentsConnectedShort: 'one:свързан|other:свързани',
    agentsAddressTitle: 'MCP адрес',
    agentsPrivacy:
      'Агентите действат от ваше име и виждат само данни на {company}. Те четат одобрени знания, никога частни чатове, лична памет или частни файлове. Всичко останало остава изключено, освен ако не го разрешите.',
    agentsSetupHide: 'Скриване на настройката',
    agentsSetupAnother: 'Свързване на друг агент',
    agentsHelpInChat: 'Помощ в чата',
    agentsHelpStarter: 'Помогни ми да свържа външен агент с Poneva чрез MCP.',
    agentNoticeReady: '{app} прочете знания от Poneva. Връзката е готова.',
    agentNoticeAuthorized:
      '{app} е упълномощен. Задайте му въпрос, за да завършите проверката на връзката.',
    agentScopeUnconfirmed:
      'Не успяхме да потвърдим промяната за {app}. Данните по-долу показват какво има Poneva в момента.',
    agentScopeNotChanged: 'Достъпът на {app} не беше променен. Опитайте отново.',
    agentConfirmRevokeKey:
      'Да се отмени ли ключът за достъп „{app}“? Всеки агент, който го използва, веднага спира да чете знанията на {company}.',
    agentConfirmDisconnect:
      'Да се прекъсне ли връзката с {app}? {app} вече няма да може да чете знанията на {company}. Вашият вход в Poneva не се засяга.',
    agentKeyRevoked: 'Ключът за достъп „{app}“ е отменен.',
    agentDisconnected: 'Връзката с {app} е прекъсната.',
    agentDisconnectUnconfirmed:
      'Не успяхме да потвърдим, че връзката с {app} е прекъсната. Списъкът показва текущото състояние.',
    agentDisconnectFailed: '{app} все още е свързан. Опитайте отново.',
    setupTitle: 'Свързване на външен агент',
    setupManualLabel: 'Или го настройте сами',
    setupAddTitle: 'Добавете Poneva като MCP сървър',
    setupAddBody:
      'В настройките за MCP или конектори на агента добавете отдалечен MCP сървър (Streamable HTTP) с адреса по-горе.',
    setupConfigToggle: 'Използвате конфигурационен файл?',
    setupConfigBody: 'Агентите, които изброяват MCP сървърите си в JSON файл, използват това:',
    setupSignInTitle: 'Влезте',
    setupSignInBody:
      'Агентът отваря Poneva във вашия браузър. Проверете компанията и какво може да прави агентът, след което изберете „Разрешете“.',
    setupSignInFallback:
      'Ако прозорецът за вход се затвори сам или изобщо не покаже Poneva, или агентът поиска токен или API ключ, използвайте вместо това ключ за достъп.',
    setupTryTitle: 'Изпробвайте',
    setupTryAsk: 'Попитайте агента:',
    setupSampleQuestion: 'Каква е текущата посока на нашия продукт? Посочи източника.',
    setupTryNote: 'След първото четене тук ще се показва като „Готов“.',
    setupPromptTitle: 'Оставете агента си за програмиране да го настрои',
    setupPromptBody:
      'Поставете тази подкана в Claude Code, Codex или друг агент за програмиране. Тя съдържа вашия MCP адрес и води агента през настройката. Одобрявате достъпа, когато влезете.',
    copyAction: 'Копиране',
    copiedAction: 'Копирано',
    copyNamed: 'Копиране на {what}',
    copiedNamed: 'Копирано: {what}',
    copiedStatus: 'Копирано: {what}.',
    copyManualHint:
      'Копирането не е налично в този браузър. Текстът е маркиран; копирайте го с клавиатурата.',
    copyPromptShow: 'Преглед на подканата',
    copyPromptHide: 'Скриване на подканата',
    copyWhatAddress: 'MCP адреса на Poneva',
    copyWhatConfig: 'конфигурацията на MCP сървъра',
    copyWhatSample: 'примерния въпрос',
    copyWhatSampleFor: 'примерния въпрос за {app}',
    copyWhatPrompt: 'подканата за настройка',
    copyWhatKey: 'ключа за достъп за {app}',
    keyCreateAction: 'Създаване на ключ за достъп',
    keySecret:
      'Пазете го като парола. Поставяйте го само в настройките за MCP, конектори или тайни на агент, никога в уебсайт или код, който други могат да отворят.',
    keyFormNote:
      'За агент, който не може да влезе. Ключът чете знанията на {company} от ваше име, докато не изтече или не го отмените; смяната на паролата ви или напускането на компанията също го прекратява.',
    keyName: 'Име',
    keyNamePlaceholder: 'напр. Нощен отчет',
    keyLifetime: 'Изтича след',
    keyDays: 'one:ден|other:дни',
    keyAlsoAllow: 'Разрешете също',
    keyCreating: 'Създаване…',
    keyCreate: 'Създаване на ключ',
    keyCancel: 'Отказ',
    keyUnconfirmed:
      'Не успяхме да потвърдим, че ключът е създаден. Ако се появи в списъка, отменете го и създайте нов.',
    keyNotCreated: 'Ключът за достъп не беше създаден. Опитайте отново.',
    keyNewTitle: 'Ключ за достъп за {app}',
    keyCopyNow: 'Копирайте го сега. Poneva го показва само веднъж и пази само негов отпечатък.',
    keyTokenRule: 'Поле {token} или {apiKey} приема само ключа.',
    keyTermToken: 'токен',
    keyTermApiKey: 'API ключ',
    keyHeaderRule:
      'Поле {header} приема името {name} и стойността {value}, като на мястото на {key} стои вашият ключ.',
    keyTermHeader: 'заглавка',
    keyEndsOn:
      'Спира да работи на {date}, както и ако го отмените, смените паролата си или напуснете компанията.',
    keyEndsExpiry:
      'Спира да работи, когато изтече, както и ако го отмените, смените паролата си или напуснете компанията.',
    keyDone: 'Готово',
    scopeProposeTitle: 'Предлагане на промени в знанията',
    scopeProposeDescribe:
      '{app} може да предлага промени в страници. Човек с права за публикуване в компанията преглежда всяка от тях, преди нещо да се промени.',
    scopeProposeConfirmOn:
      'Да се разреши ли на {app} да предлага промени в знанията на {company}? Всяко предложение чака в Знания → За преглед, докато човек с права за публикуване в компанията не го одобри. Нищо не се променя само.',
    scopeProposeConfirmOff:
      'Да спре ли {app} да предлага промени в знанията? Вече изпратените предложения остават за преглед.',
    scopeProposeEnabled:
      '{app} вече може да предлага промени в знанията. Ако опцията още не се показва в {app}, рестартирайте го.',
    scopeProposeDisabled: '{app} вече не може да предлага промени в знанията.',
    agentReadAccess:
      'Четене на знанията на компанията: профилът на компанията и одобрените страници със знания. Чатовете, файловете, срещите и личната памет не са включени.',
    agentHintKeyStale:
      'Адресът на Poneva се промени след създаването на този ключ, затова Poneva го отказва. Отменете го и създайте нов ключ.',
    agentHintStale:
      'Адресът на Poneva се промени, след като {app} се свърза, затова Poneva го отказва. Прекъснете връзката с него, след това свържете {app} отново с адреса по-горе.',
    agentHintKeyWaiting: 'Ще се показва като „Готов“, щом агент използва ключа.',
    agentHintWaiting: 'Задайте въпрос на {app}, за да завършите проверката на връзката.',
    agentHintTryAsking: 'Опитайте да попитате:',
    agentFactCreated: 'Създаден',
    agentFactConnected: 'Свързан',
    agentFactExpires: 'Изтича',
    agentFactSignInEnds: 'Входът изтича',
    agentFactAccess: 'Достъп',
    agentNameNote:
      'Името е това, което агентът е съобщил; Poneva не може да го провери. Прекъснете връзката с всеки агент, който не разпознавате.',
    agentSignInNote: 'Когато входът му изтече, агентът ще ви помоли да влезете отново.',
    agentWorking: 'Обработване…',
    agentRevokeKey: 'Отмяна на ключа',
    agentDisconnect: 'Прекъсване на връзката',
    cardAgentsLabel: 'Вашите свързани агенти',
    cardMore: 'и още {more}',
    cardMoreCount: 'one:във Връзки|other:във Връзки',
    cardOpenSetup: 'Отваряне на настройката',
    cardChecking: 'Проверка на външните ви агенти…',
    cardTryAgain: 'Опитайте отново',
    connectedAgentsSubtitleAny: 'Агенти, които четат знанията на вашата компания чрез Poneva.',
    scopeLibraryReadTitle: 'Четене на работата ви в Poneva',
    scopeLibraryReadDescribe:
      '{app} може да намира и чете чатовете, Библиотеката, файловете, срещите, Skills, агентите и приложенията, които можете да отворите в Poneva, както и да чете от свързаните ви услуги. Може също да чете асистентите за пазаруване на компанията и какво са им казали купувачите.',
    scopeLibraryReadConfirmOn:
      'Да се разреши ли на {app} да чете работата ви в Poneva в {company}? Той намира и чете от ваше име това, което вече можете да отворите в Poneva.',
    scopeLibraryReadConfirmOff:
      'Да спре ли {app} да чете работата ви в Poneva? Тогава той няма да може и да запазва работа или да пита Poneva.',
    scopeLibraryReadEnabled:
      '{app} вече може да чете работата ви в Poneva. Ако опцията още не се показва в {app}, рестартирайте го.',
    scopeLibraryReadDisabled: '{app} вече не може да чете работата ви в Poneva.',
    scopeLibraryWriteTitle: 'Запазване на работа и въпроси към Poneva',
    scopeLibraryWriteDescribe:
      '{app} може да запазва работа във вашата Библиотека, където новата работа остава лична за вас, и да запазва нови версии на работа, която можете да редактирате (предишните версии остават в История). Може да запазва чернови на Skills и на дизайни на асистентите за пазаруване на компанията, видими за колегите, които могат да ги редактират; само човек ги публикува в Poneva. Може също да пита Poneva в лични чатове, което използва кредити. Може също да стартира четене на уеб магазина на компанията, за да поправи асистент за пазаруване, и да му задава тестови въпроси; и двете използват кредити на компанията.',
    scopeLibraryWriteConfirmOn:
      'Да се разреши ли на {app} да запазва работа във вашата Библиотека в {company}, включително нови версии на работа, която можете да редактирате, и чернови, които колегите могат да видят, както и да пита Poneva вместо вас? Въпросите използват кредити. Той може също да чете работата ви в Poneva. Четенията на магазина и тестовите въпроси към асистентите за пазаруване също използват кредити на компанията.',
    scopeLibraryWriteConfirmOff:
      'Да спре ли {app} да запазва работа и да пита Poneva? Това, което вече е запазил, остава във вашата Библиотека.',
    scopeLibraryWriteEnabled:
      '{app} вече може да запазва работа и да пита Poneva. Ако опцията още не се показва в {app}, рестартирайте го.',
    scopeLibraryWriteDisabled: '{app} вече не може да запазва работа или да пита Poneva.',
  },
  cs: {
    mcpServersTitle: 'Vlastní připojení',
    mcpServersSubtitle: 'Připojte vlastní server s nástroji. Poneva se zeptá, než cokoli změní.',
    mcpServersApproval:
      'Každou změnu schvalujete. Pro čtení platí to, co jste zvolili u každého serveru.',
    mcpServersAdd: 'Přidat připojení',
    connectedAgentsLink: 'Připojení agenti',
    accountConnectionsUnavailable:
      'Připojení účtů zatím v tomto pracovním prostoru nejsou k dispozici.',
    connectedAgentsTitle: 'Připojení agenti',
    connectedAgentsSubtitle: 'Agenti, kteří čtou znalosti společnosti {company} přes Poneva.',
    connectedAgentsSuggest: 'Pokud to povolíte, navrhují změny ke kontrole člověkem.',
    connectedAgentsList: 'Připojeno nyní',
    connectingAgentsOff: 'Připojování agentů zatím není pro tento pracovní prostor k dispozici.',
    checkingConnectedAgents: 'Kontrolují se připojení agenti…',
    connectedAgentsLoadFailed: 'Připojené agenty se nepodařilo načíst.',
    connectedAgentsOpen: 'Otevřít Připojené agenty',
    agentTimeNotYet: 'zatím ne',
    agentTimeUnknown: 'v neznámém čase',
    agentTimeJustNow: 'právě teď',
    agentTimeOnDate: 'dne {date}',
    agentErrorNotFound: 'Tento agent už není připojen. Seznam ukazuje aktuální stav.',
    agentErrorScopeUnavailable: 'Tento přístup není v tomto pracovním prostoru k dispozici.',
    agentErrorTooManyKeys:
      'Máte tolik přístupových klíčů, kolik Poneva povoluje. Nejprve jeden zrušte.',
    agentErrorInvalidInput: 'Zkontrolujte název klíče a zkuste to znovu.',
    agentNotRecorded: 'Nezaznamenáno',
    agentMethodKey: 'Přístupový klíč',
    agentMethodSignedIn: 'Přihlášeno',
    agentStateReconnect: 'Je potřeba znovu připojit',
    agentStateReady: 'Připraveno',
    agentStateKeyUnused: 'Vytvořeno — zatím nepoužito',
    agentStateWaiting: 'Autorizováno — čeká na první čtení',
    agentLastUsed: 'Naposledy použito {when}',
    agentKeyExpires: 'Přístupový klíč · platnost vyprší {date}',
    agentSignInEnds: 'Přihlášeno · přihlášení vyprší {date}',
    agentsNone: 'Zatím nejsou připojeni žádní externí agenti',
    agentsConnectedCount:
      'one:agent připojen|few:agenti připojeni|many:agenta připojeno|other:agentů připojeno',
    agentsWaitingCount: 'one:čeká|few:čekají|many:čeká|other:čeká',
    agentsReconnectCount:
      'one:k opětovnému připojení|few:k opětovnému připojení|many:k opětovnému připojení|other:k opětovnému připojení',
    addressLocalNetwork:
      'Dostupné jen v této síti. Agenti běžící v cloudu tuto adresu nemohou použít.',
    addressLocalComputer:
      'Dostupné jen z tohoto počítače. Agenti běžící v cloudu tuto adresu nemohou použít.',
    addressUnreachable:
      'Poneva se k této adrese teď nemůže připojit, takže externí agenti nejspíš také ne.',
    agentUnnamed: 'Agent bez názvu',
    agentCompanyFallback: 'vaše společnost',
    agentErrorUnreadable:
      'Poneva vrátila odpověď, kterou tato stránka nedokázala přečíst. Zkuste to znovu.',
    agentsLoadFailed: 'Vaše externí agenty se nepodařilo načíst.',
    agentsLoading: 'Načítání externích agentů…',
    agentsConnectedShort: 'one:připojen|few:připojeni|many:připojeno|other:připojeno',
    agentsAddressTitle: 'MCP adresa',
    agentsPrivacy:
      'Agenti jednají vaším jménem a vidí jen společnost {company}. Čtou schválené znalosti, nikdy soukromé chaty, osobní paměť ani soukromé soubory. Cokoli dalšího zůstává vypnuté, dokud to nepovolíte.',
    agentsSetupHide: 'Skrýt nastavení',
    agentsSetupAnother: 'Připojit dalšího agenta',
    agentsHelpInChat: 'Pomoc v chatu',
    agentsHelpStarter: 'Pomoz mi připojit externího agenta k Poneva přes MCP.',
    agentNoticeReady: 'Agent {app} přečetl znalosti z Poneva. Připojení je připraveno.',
    agentNoticeAuthorized:
      'Agent {app} je autorizován. Položte mu otázku a dokončete tak kontrolu připojení.',
    agentScopeUnconfirmed:
      'Změnu pro agenta {app} se nepodařilo potvrdit. Údaje níže ukazují, co má Poneva nyní.',
    agentScopeNotChanged: 'Přístup agenta {app} se nezměnil. Zkuste to znovu.',
    agentConfirmRevokeKey:
      'Zrušit přístupový klíč „{app}“? Každý agent, který ho používá, okamžitě přestane číst znalosti společnosti {company}.',
    agentConfirmDisconnect:
      'Odpojit agenta {app}? {app} už nebude moci číst znalosti společnosti {company}. Vašeho přihlášení do Poneva se to netýká.',
    agentKeyRevoked: 'Přístupový klíč „{app}“ byl zrušen.',
    agentDisconnected: 'Agent {app} byl odpojen.',
    agentDisconnectUnconfirmed:
      'Nepodařilo se potvrdit, že byl agent {app} odpojen. Seznam ukazuje aktuální stav.',
    agentDisconnectFailed: 'Agent {app} je stále připojen. Zkuste to znovu.',
    setupTitle: 'Připojit externího agenta',
    setupManualLabel: 'Nebo to nastavte sami',
    setupAddTitle: 'Přidejte Poneva jako MCP server',
    setupAddBody:
      'V nastavení MCP nebo konektorů agenta přidejte vzdálený MCP server (Streamable HTTP) s adresou výše.',
    setupConfigToggle: 'Používáte konfigurační soubor?',
    setupConfigBody: 'Agentům, kteří mají své MCP servery v souboru JSON, zadejte toto:',
    setupSignInTitle: 'Přihlaste se',
    setupSignInBody:
      'Agent otevře Poneva ve vašem prohlížeči. Zkontrolujte společnost a to, co agent smí dělat, a pak zvolte „Povolit“.',
    setupSignInFallback:
      'Pokud se přihlašovací okno samo zavře nebo Poneva vůbec nezobrazí, případně agent požádá o token nebo API klíč, použijte místo toho přístupový klíč.',
    setupTryTitle: 'Vyzkoušejte to',
    setupTryAsk: 'Zeptejte se agenta:',
    setupSampleQuestion: 'Jaké je naše aktuální produktové směřování? Uveď zdroj.',
    setupTryNote: 'Po prvním čtení se zde zobrazí jako „Připraveno“.',
    setupPromptTitle: 'Nechte nastavení na svém agentovi pro programování',
    setupPromptBody:
      'Vložte tento prompt do Claude Code, Codex nebo jiného agenta pro programování. Obsahuje vaši MCP adresu a provede agenta nastavením. Přístup schválíte při přihlášení.',
    copyAction: 'Kopírovat',
    copiedAction: 'Zkopírováno',
    copyNamed: 'Kopírovat {what}',
    copiedNamed: 'Zkopírováno: {what}',
    copiedStatus: 'Zkopírováno: {what}.',
    copyManualHint:
      'Kopírování v tomto prohlížeči není k dispozici. Text je označený; zkopírujte ho klávesnicí.',
    copyPromptShow: 'Zobrazit prompt',
    copyPromptHide: 'Skrýt prompt',
    copyWhatAddress: 'MCP adresu Poneva',
    copyWhatConfig: 'konfiguraci MCP serveru',
    copyWhatSample: 'ukázkový dotaz',
    copyWhatSampleFor: 'ukázkový dotaz pro {app}',
    copyWhatPrompt: 'prompt pro nastavení',
    copyWhatKey: 'přístupový klíč pro {app}',
    keyCreateAction: 'Vytvořit přístupový klíč',
    keySecret:
      'Zacházejte s ním jako s heslem. Vkládejte ho jen do nastavení MCP, konektorů nebo tajných údajů agenta, nikdy na web nebo do kódu, který mohou otevřít ostatní.',
    keyFormNote:
      'Pro agenta, který se nemůže přihlásit. Klíč čte znalosti společnosti {company} vaším jménem, dokud nevyprší nebo ho nezrušíte; ukončí ho také změna hesla nebo váš odchod ze společnosti.',
    keyName: 'Název',
    keyNamePlaceholder: 'např. Noční přehled',
    keyLifetime: 'Vyprší za',
    keyDays: 'one:den|few:dny|many:dne|other:dní',
    keyAlsoAllow: 'Povolit také',
    keyCreating: 'Vytváření…',
    keyCreate: 'Vytvořit klíč',
    keyCancel: 'Zrušit',
    keyUnconfirmed:
      'Nepodařilo se potvrdit, že byl klíč vytvořen. Pokud se zobrazí v seznamu, zrušte ho a vytvořte nový.',
    keyNotCreated: 'Přístupový klíč nebyl vytvořen. Zkuste to znovu.',
    keyNewTitle: 'Přístupový klíč pro {app}',
    keyCopyNow: 'Zkopírujte si ho hned. Poneva ho zobrazí jen jednou a ukládá si jen jeho otisk.',
    keyTokenRule: 'Do pole {token} nebo {apiKey} zadejte jen samotný klíč.',
    keyTermToken: 'token',
    keyTermApiKey: 'API klíč',
    keyHeaderRule:
      'Do pole {header} zadejte název {name} a hodnotu {value}, kde místo {key} vložíte svůj klíč.',
    keyTermHeader: 'hlavička',
    keyEndsOn:
      'Přestane fungovat {date}, když ho zrušíte, a také když si změníte heslo nebo odejdete ze společnosti.',
    keyEndsExpiry:
      'Přestane fungovat, když vyprší, když ho zrušíte, a také když si změníte heslo nebo odejdete ze společnosti.',
    keyDone: 'Hotovo',
    scopeProposeTitle: 'Navrhovat úpravy znalostí',
    scopeProposeDescribe:
      'Agent {app} může navrhovat změny stránek. Každou z nich před provedením zkontroluje osoba s právem publikovat ve společnosti.',
    scopeProposeConfirmOn:
      'Povolit agentovi {app} navrhovat úpravy znalostí společnosti {company}? Každý návrh čeká v sekci Znalosti → Ke kontrole, dokud ho neschválí osoba s právem publikovat ve společnosti. Nic se nezmění samo.',
    scopeProposeConfirmOff:
      'Zakázat agentovi {app} navrhovat úpravy znalostí? Návrhy, které už odeslal, zůstanou ke kontrole.',
    scopeProposeEnabled:
      'Agent {app} teď může navrhovat úpravy znalostí. Pokud se možnost v {app} ještě nezobrazuje, restartujte ho.',
    scopeProposeDisabled: 'Agent {app} už nemůže navrhovat úpravy znalostí.',
    agentReadAccess:
      'Čtení znalostí společnosti: profil společnosti a schválené stránky znalostí. Chaty, soubory, schůzky a osobní paměť nejsou zahrnuty.',
    agentHintKeyStale:
      'Adresa Poneva se změnila po vytvoření tohoto klíče, proto ho Poneva odmítá. Zrušte ho a vytvořte nový klíč.',
    agentHintStale:
      'Adresa Poneva se změnila po připojení agenta {app}, proto ho Poneva odmítá. Odpojte ho a pak {app} připojte znovu s adresou výše.',
    agentHintKeyWaiting: 'Jakmile agent klíč použije, zobrazí se jako „Připraveno“.',
    agentHintWaiting: 'Položte agentovi {app} otázku a dokončete tak kontrolu připojení.',
    agentHintTryAsking: 'Zkuste se zeptat:',
    agentFactCreated: 'Vytvořeno',
    agentFactConnected: 'Připojeno',
    agentFactExpires: 'Platnost vyprší',
    agentFactSignInEnds: 'Přihlášení vyprší',
    agentFactAccess: 'Přístup',
    agentNameNote:
      'Název uvedl sám agent; Poneva ho nemůže ověřit. Odpojte každého agenta, kterého neznáte.',
    agentSignInNote: 'Když přihlášení vyprší, agent vás požádá o nové přihlášení.',
    agentWorking: 'Probíhá…',
    agentRevokeKey: 'Zrušit klíč',
    agentDisconnect: 'Odpojit',
    cardAgentsLabel: 'Vaši připojení agenti',
    cardMore: 'a {more}',
    cardMoreCount:
      'one:další v sekci Připojení|few:další v sekci Připojení|many:dalšího v sekci Připojení|other:dalších v sekci Připojení',
    cardOpenSetup: 'Otevřít nastavení',
    cardChecking: 'Kontrolují se vaši externí agenti…',
    cardTryAgain: 'Zkusit znovu',
    connectedAgentsSubtitleAny: 'Agenti, kteří čtou znalosti vaší společnosti přes Poneva.',
    scopeLibraryReadTitle: 'Číst vaši práci v Poneva',
    scopeLibraryReadDescribe:
      'Agent {app} může vyhledávat a číst chaty, Knihovnu, soubory, schůzky, Skilly, agenty a aplikace, které můžete v Poneva otevřít, a číst z vašich připojených služeb. Může také číst nákupní asistenty společnosti a to, co jim řekli nakupující.',
    scopeLibraryReadConfirmOn:
      'Povolit agentovi {app} číst vaši práci v Poneva ve společnosti {company}? Vyhledává a čte to, co už můžete v Poneva otevřít, a jedná přitom jako vy.',
    scopeLibraryReadConfirmOff:
      'Zakázat agentovi {app} číst vaši práci v Poneva? Pak už také nebude moci ukládat práci ani se ptát Ponevy.',
    scopeLibraryReadEnabled:
      'Agent {app} teď může číst vaši práci v Poneva. Pokud se možnost v {app} ještě nezobrazuje, restartujte ho.',
    scopeLibraryReadDisabled: 'Agent {app} už nemůže číst vaši práci v Poneva.',
    scopeLibraryWriteTitle: 'Ukládat práci a ptát se Ponevy',
    scopeLibraryWriteDescribe:
      'Agent {app} může ukládat práci do vaší Knihovny, kde nová práce zůstává soukromá, jen pro vás, a ukládat nové verze práce, kterou můžete upravovat (dřívější verze zůstávají v sekci Historie). Může ukládat koncepty Skillů a návrhů nákupních asistentů společnosti, které vidí kolegové, kteří je mohou upravovat; v Poneva je zveřejňuje jen člověk. Také se může ptát Ponevy v soukromých chatech, což spotřebovává kredity. Může také spustit čtení webového obchodu společnosti, aby opravil nákupního asistenta, a klást mu testovací otázky; obojí využívá kredity společnosti.',
    scopeLibraryWriteConfirmOn:
      'Povolit agentovi {app} ukládat práci do vaší Knihovny ve společnosti {company}, včetně nových verzí práce, kterou můžete upravovat, a konceptů, které mohou vidět kolegové, a ptát se Ponevy za vás? Dotazy spotřebovávají kredity. Bude také moci číst vaši práci v Poneva. Čtení obchodu a testovací otázky pro nákupní asistenty také využívají kredity společnosti.',
    scopeLibraryWriteConfirmOff:
      'Zakázat agentovi {app} ukládat práci a ptát se Ponevy? Co už uložil, zůstane ve vaší Knihovně.',
    scopeLibraryWriteEnabled:
      'Agent {app} teď může ukládat práci a ptát se Ponevy. Pokud se možnost v {app} ještě nezobrazuje, restartujte ho.',
    scopeLibraryWriteDisabled: 'Agent {app} už nemůže ukládat práci ani se ptát Ponevy.',
  },
  da: {
    mcpServersTitle: 'Egne forbindelser',
    mcpServersSubtitle: 'Forbind din egen værktøjsserver. Poneva spørger, før den ændrer noget.',
    mcpServersApproval:
      'Du godkender hver ændring. Til læsning gælder det, du har valgt for hver server.',
    mcpServersAdd: 'Tilføj forbindelse',
    connectedAgentsLink: 'Forbundne agenter',
    accountConnectionsUnavailable:
      'Kontoforbindelser er endnu ikke tilgængelige i dette arbejdsområde.',
    connectedAgentsTitle: 'Forbundne agenter',
    connectedAgentsSubtitle: 'Agenter, der læser {company}s viden gennem Poneva.',
    connectedAgentsSuggest:
      'Hvis du tillader det, foreslår de opdateringer, som en person gennemgår.',
    connectedAgentsList: 'Forbundet nu',
    connectingAgentsOff:
      'Forbindelse af agenter er endnu ikke tilgængelig for dette arbejdsområde.',
    checkingConnectedAgents: 'Tjekker forbundne agenter…',
    connectedAgentsLoadFailed: 'Forbundne agenter kunne ikke indlæses.',
    connectedAgentsOpen: 'Åbn Forbundne agenter',
    agentTimeNotYet: 'endnu ikke',
    agentTimeUnknown: 'på et ukendt tidspunkt',
    agentTimeJustNow: 'for et øjeblik siden',
    agentTimeOnDate: 'den {date}',
    agentErrorNotFound: 'Denne agent er ikke længere forbundet. Listen viser den aktuelle status.',
    agentErrorScopeUnavailable: 'Den adgang er ikke tilgængelig i dette arbejdsområde.',
    agentErrorTooManyKeys:
      'Du har så mange adgangsnøgler, som Poneva tillader. Tilbagekald en først.',
    agentErrorInvalidInput: 'Tjek nøglens navn, og prøv igen.',
    agentNotRecorded: 'Ikke registreret',
    agentMethodKey: 'Adgangsnøgle',
    agentMethodSignedIn: 'Logget ind',
    agentStateReconnect: 'Skal forbindes igen',
    agentStateReady: 'Klar',
    agentStateKeyUnused: 'Oprettet — ikke brugt endnu',
    agentStateWaiting: 'Godkendt — venter på første læsning',
    agentLastUsed: 'Sidst brugt {when}',
    agentKeyExpires: 'Adgangsnøgle · udløber den {date}',
    agentSignInEnds: 'Logget ind · login udløber den {date}',
    agentsNone: 'Ingen eksterne agenter forbundet endnu',
    agentsConnectedCount: 'one:agent forbundet|other:agenter forbundet',
    agentsWaitingCount: 'one:venter|other:venter',
    agentsReconnectCount: 'one:skal forbindes igen|other:skal forbindes igen',
    addressLocalNetwork:
      'Kan kun nås på dette netværk. Agenter, der kører i skyen, kan ikke bruge denne adresse.',
    addressLocalComputer:
      'Kan kun nås fra denne computer. Agenter, der kører i skyen, kan ikke bruge denne adresse.',
    addressUnreachable:
      'Poneva kan ikke nå denne adresse lige nu, så eksterne agenter kan sandsynligvis heller ikke.',
    agentUnnamed: 'Unavngiven agent',
    agentCompanyFallback: 'din virksomhed',
    agentErrorUnreadable: 'Poneva sendte et svar, som denne side ikke kunne læse. Prøv igen.',
    agentsLoadFailed: 'Dine eksterne agenter kunne ikke indlæses.',
    agentsLoading: 'Indlæser eksterne agenter…',
    agentsConnectedShort: 'one:forbundet|other:forbundet',
    agentsAddressTitle: 'MCP-adresse',
    agentsPrivacy:
      'Agenter handler på dine vegne og ser kun {company}. De læser godkendt viden, aldrig private chats, personlig hukommelse eller private filer. Alt andet forbliver slået fra, medmindre du tillader det.',
    agentsSetupHide: 'Skjul opsætning',
    agentsSetupAnother: 'Forbind en anden agent',
    agentsHelpInChat: 'Få hjælp i chatten',
    agentsHelpStarter: 'Hjælp mig med at forbinde en ekstern agent til Poneva via MCP.',
    agentNoticeReady: '{app} har læst viden fra Poneva. Forbindelsen er klar.',
    agentNoticeAuthorized:
      '{app} er godkendt. Stil den et spørgsmål for at afslutte tjekket af forbindelsen.',
    agentScopeUnconfirmed:
      'Vi kunne ikke bekræfte ændringen for {app}. Oplysningerne nedenfor viser, hvad Poneva har nu.',
    agentScopeNotChanged: 'Adgangen for {app} blev ikke ændret. Prøv igen.',
    agentConfirmRevokeKey:
      'Tilbagekald adgangsnøglen »{app}«? Alle agenter, der bruger den, holder straks op med at læse {company}s viden.',
    agentConfirmDisconnect:
      'Afbryd forbindelsen til {app}? {app} vil ikke længere kunne læse {company}s viden. Dit Poneva-login påvirkes ikke.',
    agentKeyRevoked: 'Adgangsnøglen »{app}« er tilbagekaldt.',
    agentDisconnected: 'Forbindelsen til {app} er afbrudt.',
    agentDisconnectUnconfirmed:
      'Vi kunne ikke bekræfte, at forbindelsen til {app} blev afbrudt. Listen viser den aktuelle status.',
    agentDisconnectFailed: '{app} er stadig forbundet. Prøv igen.',
    setupTitle: 'Forbind en ekstern agent',
    setupManualLabel: 'Eller konfigurer den selv',
    setupAddTitle: 'Tilføj Poneva som MCP-server',
    setupAddBody:
      'Tilføj en fjern-MCP-server (Streamable HTTP) med adressen ovenfor i agentens MCP- eller connectorindstillinger.',
    setupConfigToggle: 'Bruger du en konfigurationsfil?',
    setupConfigBody: 'Agenter, der angiver deres MCP-servere i en JSON-fil, skal have dette:',
    setupSignInTitle: 'Log ind',
    setupSignInBody:
      'Agenten åbner Poneva i din browser. Tjek virksomheden, og hvad agenten må gøre, og vælg derefter »Tillad«.',
    setupSignInFallback:
      'Hvis loginvinduet lukker af sig selv eller aldrig viser Poneva, eller hvis agenten beder om et token eller en API-nøgle, skal du bruge en adgangsnøgle i stedet.',
    setupTryTitle: 'Prøv det',
    setupTryAsk: 'Spørg agenten:',
    setupSampleQuestion: 'Hvad er vores nuværende produktretning? Angiv kilden.',
    setupTryNote: 'Den vises som »Klar« her efter første læsning.',
    setupPromptTitle: 'Lad din kodeagent sætte det op',
    setupPromptBody:
      'Indsæt denne prompt i Claude Code, Codex eller en anden kodeagent. Den indeholder din MCP-adresse og guider agenten gennem opsætningen. Du godkender adgangen, når du logger ind.',
    copyAction: 'Kopiér',
    copiedAction: 'Kopieret',
    copyNamed: 'Kopiér {what}',
    copiedNamed: 'Kopieret: {what}',
    copiedStatus: 'Kopieret: {what}.',
    copyManualHint:
      'Kopiering er ikke tilgængelig i denne browser. Teksten er markeret; kopiér den med tastaturet.',
    copyPromptShow: 'Vis prompt',
    copyPromptHide: 'Skjul prompt',
    copyWhatAddress: 'Ponevas MCP-adresse',
    copyWhatConfig: 'MCP-serverkonfigurationen',
    copyWhatSample: 'eksempelspørgsmålet',
    copyWhatSampleFor: 'eksempelspørgsmålet til {app}',
    copyWhatPrompt: 'opsætningsprompten',
    copyWhatKey: 'adgangsnøglen til {app}',
    keyCreateAction: 'Opret en adgangsnøgle',
    keySecret:
      'Behandl den som en adgangskode. Indsæt den kun i en agents MCP-, connector- eller hemmelighedsindstillinger, aldrig på et website eller i kode, som andre kan åbne.',
    keyFormNote:
      'Til en agent, der ikke kan logge ind. Nøglen læser {company}s viden på dine vegne, indtil den udløber, eller du tilbagekalder den. Den ophører også, hvis du skifter adgangskode eller forlader virksomheden.',
    keyName: 'Navn',
    keyNamePlaceholder: 'f.eks. Natlig rapportkørsel',
    keyLifetime: 'Udløber efter',
    keyDays: 'one:dag|other:dage',
    keyAlsoAllow: 'Tillad også',
    keyCreating: 'Opretter…',
    keyCreate: 'Opret nøgle',
    keyCancel: 'Annuller',
    keyUnconfirmed:
      'Vi kunne ikke bekræfte, at nøglen blev oprettet. Hvis den vises på listen, så tilbagekald den, og opret en ny.',
    keyNotCreated: 'Adgangsnøglen blev ikke oprettet. Prøv igen.',
    keyNewTitle: 'Adgangsnøgle til {app}',
    keyCopyNow: 'Kopiér den nu. Poneva viser den kun én gang og gemmer kun et fingeraftryk af den.',
    keyTokenRule: 'I feltet {token} eller {apiKey} indsætter du kun selve nøglen.',
    keyTermToken: 'token',
    keyTermApiKey: 'API-nøgle',
    keyHeaderRule:
      'I feltet {header} angiver du navnet {name} og værdien {value}, hvor din nøgle erstatter {key}.',
    keyTermHeader: 'header',
    keyEndsOn:
      'Den holder op med at virke den {date}, når du tilbagekalder den, og når du skifter adgangskode eller forlader virksomheden.',
    keyEndsExpiry:
      'Den holder op med at virke, når den udløber, når du tilbagekalder den, og når du skifter adgangskode eller forlader virksomheden.',
    keyDone: 'Færdig',
    scopeProposeTitle: 'Foreslå opdateringer af viden',
    scopeProposeDescribe:
      '{app} kan foreslå ændringer af sider. En udgiver i virksomheden gennemgår hvert forslag, før noget ændres.',
    scopeProposeConfirmOn:
      'Skal {app} kunne foreslå opdateringer af {company}s viden? Hvert forslag venter under Viden → Til gennemgang, indtil en udgiver i virksomheden godkender det. Intet ændres af sig selv.',
    scopeProposeConfirmOff:
      'Skal {app} stoppe med at foreslå opdateringer af viden? Forslag, den allerede har sendt, forbliver til gennemgang.',
    scopeProposeEnabled:
      '{app} kan nu foreslå opdateringer af viden. Hvis muligheden ikke vises i {app} endnu, så genstart den.',
    scopeProposeDisabled: '{app} kan ikke længere foreslå opdateringer af viden.',
    agentReadAccess:
      'Læs virksomhedens viden: Virksomhedsprofilen og de godkendte vidensider. Chats, filer, møder og personlig hukommelse er ikke inkluderet.',
    agentHintKeyStale:
      'Ponevas adresse er ændret, siden denne nøgle blev oprettet, så Poneva afviser den. Tilbagekald den, og opret en ny nøgle.',
    agentHintStale:
      'Ponevas adresse er ændret, siden {app} blev forbundet, så Poneva afviser den. Afbryd forbindelsen, og forbind derefter {app} igen med adressen ovenfor.',
    agentHintKeyWaiting: 'Den vises som »Klar«, når en agent bruger nøglen.',
    agentHintWaiting: 'Stil {app} et spørgsmål for at afslutte tjekket af forbindelsen.',
    agentHintTryAsking: 'Prøv at spørge:',
    agentFactCreated: 'Oprettet',
    agentFactConnected: 'Forbundet',
    agentFactExpires: 'Udløber',
    agentFactSignInEnds: 'Login udløber',
    agentFactAccess: 'Adgang',
    agentNameNote:
      'Navnet er det, agenten selv har oplyst; Poneva kan ikke bekræfte det. Afbryd forbindelsen til agenter, du ikke genkender.',
    agentSignInNote: 'Når agentens login udløber, beder den dig om at logge ind igen.',
    agentWorking: 'Arbejder…',
    agentRevokeKey: 'Tilbagekald nøgle',
    agentDisconnect: 'Afbryd forbindelse',
    cardAgentsLabel: 'Dine forbundne agenter',
    cardMore: 'og {more}',
    cardMoreCount: 'one:til i Forbindelser|other:til i Forbindelser',
    cardOpenSetup: 'Åbn opsætning',
    cardChecking: 'Tjekker dine eksterne agenter…',
    cardTryAgain: 'Prøv igen',
    connectedAgentsSubtitleAny: 'Agenter, der læser din virksomheds viden gennem Poneva.',
    scopeLibraryReadTitle: 'Læse dit arbejde i Poneva',
    scopeLibraryReadDescribe:
      '{app} kan finde og læse de chats, elementer i Bibliotek, filer, møder, Skills, agenter og apps, du kan åbne i Poneva, og læse fra dine forbundne tjenester. Den kan også læse virksomhedens shoppingassistenter og hvad kunderne har sagt til dem.',
    scopeLibraryReadConfirmOn:
      'Skal {app} kunne læse dit arbejde i Poneva hos {company}? Den finder og læser det, du allerede kan åbne i Poneva, som dig.',
    scopeLibraryReadConfirmOff:
      'Skal {app} stoppe med at læse dit arbejde i Poneva? Så kan den heller ikke længere gemme arbejde eller spørge Poneva.',
    scopeLibraryReadEnabled:
      '{app} kan nu læse dit arbejde i Poneva. Hvis muligheden ikke vises i {app} endnu, så genstart den.',
    scopeLibraryReadDisabled: '{app} kan ikke længere læse dit arbejde i Poneva.',
    scopeLibraryWriteTitle: 'Gemme arbejde og spørge Poneva',
    scopeLibraryWriteDescribe:
      '{app} kan gemme arbejde i dit Bibliotek, hvor nyt arbejde forbliver privat for dig, og gemme nye versioner af arbejde, du kan redigere (tidligere versioner bliver liggende i Historik). Den kan gemme kladder af Skills og af design til virksomhedens shoppingassistenter, som kolleger, der kan redigere dem, kan se; kun et menneske udgiver dem i Poneva. Den kan også spørge Poneva i private chats, hvilket bruger kreditter. Den kan også starte læsninger af virksomhedens webshop for at rette en shoppingassistent og stille den testspørgsmål; begge dele bruger virksomhedens kreditter.',
    scopeLibraryWriteConfirmOn:
      'Skal {app} kunne gemme arbejde i dit Bibliotek hos {company}, herunder nye versioner af arbejde, du kan redigere, og kladder, som kolleger kan se, og spørge Poneva for dig? Spørgsmål bruger kreditter. Den kan også læse dit arbejde i Poneva. Læsninger af webshoppen og testspørgsmål til shoppingassistenter bruger også virksomhedens kreditter.',
    scopeLibraryWriteConfirmOff:
      'Skal {app} stoppe med at gemme arbejde og spørge Poneva? Det, den allerede har gemt, bliver i dit Bibliotek.',
    scopeLibraryWriteEnabled:
      '{app} kan nu gemme arbejde og spørge Poneva. Hvis muligheden ikke vises i {app} endnu, så genstart den.',
    scopeLibraryWriteDisabled: '{app} kan ikke længere gemme arbejde eller spørge Poneva.',
  },
  de: {
    mcpServersTitle: 'Eigene Verbindungen',
    mcpServersSubtitle:
      'Verbinden Sie Ihren eigenen Werkzeugserver. Poneva fragt nach, bevor etwas geändert wird.',
    mcpServersApproval:
      'Sie geben jede Änderung frei. Beim Lesen gilt, was Sie für den jeweiligen Server gewählt haben.',
    mcpServersAdd: 'Verbindung hinzufügen',
    connectedAgentsLink: 'Verbundene Agenten',
    accountConnectionsUnavailable:
      'Kontoverbindungen sind in diesem Arbeitsbereich noch nicht verfügbar.',
    connectedAgentsTitle: 'Verbundene Agenten',
    connectedAgentsSubtitle: 'Agenten, die über Poneva das Wissen von {company} lesen.',
    connectedAgentsSuggest:
      'Wenn Sie es erlauben, schlagen sie Änderungen vor, die eine Person prüft.',
    connectedAgentsList: 'Jetzt verbunden',
    connectingAgentsOff:
      'Das Verbinden von Agenten ist für diesen Arbeitsbereich noch nicht verfügbar.',
    checkingConnectedAgents: 'Verbundene Agenten werden geprüft…',
    connectedAgentsLoadFailed: 'Verbundene Agenten konnten nicht geladen werden.',
    connectedAgentsOpen: 'Verbundene Agenten öffnen',
    agentTimeNotYet: 'noch nicht',
    agentTimeUnknown: 'zu einem unbekannten Zeitpunkt',
    agentTimeJustNow: 'gerade eben',
    agentTimeOnDate: 'am {date}',
    agentErrorNotFound:
      'Dieser Agent ist nicht mehr verbunden. Die Liste zeigt den aktuellen Stand.',
    agentErrorScopeUnavailable: 'Dieser Zugriff ist in diesem Arbeitsbereich nicht verfügbar.',
    agentErrorTooManyKeys:
      'Sie haben bereits so viele Zugriffsschlüssel, wie Poneva erlaubt. Widerrufen Sie zuerst einen.',
    agentErrorInvalidInput: 'Prüfen Sie den Namen des Schlüssels und versuchen Sie es erneut.',
    agentNotRecorded: 'Nicht erfasst',
    agentMethodKey: 'Zugriffsschlüssel',
    agentMethodSignedIn: 'Angemeldet',
    agentStateReconnect: 'Neu verbinden erforderlich',
    agentStateReady: 'Bereit',
    agentStateKeyUnused: 'Erstellt — noch nicht verwendet',
    agentStateWaiting: 'Autorisiert — wartet auf den ersten Lesezugriff',
    agentLastUsed: 'Zuletzt verwendet {when}',
    agentKeyExpires: 'Zugriffsschlüssel · läuft am {date} ab',
    agentSignInEnds: 'Angemeldet · Anmeldung endet am {date}',
    agentsNone: 'Noch keine externen Agenten verbunden',
    agentsConnectedCount: 'one:Agent verbunden|other:Agenten verbunden',
    agentsWaitingCount: 'one:wartend|other:wartend',
    agentsReconnectCount: 'one:neu zu verbinden|other:neu zu verbinden',
    addressLocalNetwork:
      'Nur in diesem Netzwerk erreichbar. Agenten, die in der Cloud laufen, können diese Adresse nicht nutzen.',
    addressLocalComputer:
      'Nur von diesem Computer aus erreichbar. Agenten, die in der Cloud laufen, können diese Adresse nicht nutzen.',
    addressUnreachable:
      'Poneva kann diese Adresse gerade nicht erreichen, externe Agenten daher wahrscheinlich auch nicht.',
    agentUnnamed: 'Unbenannter Agent',
    agentCompanyFallback: 'Ihrem Unternehmen',
    agentErrorUnreadable:
      'Poneva hat eine Antwort gesendet, die diese Seite nicht lesen konnte. Versuchen Sie es erneut.',
    agentsLoadFailed: 'Ihre externen Agenten konnten nicht geladen werden.',
    agentsLoading: 'Externe Agenten werden geladen…',
    agentsConnectedShort: 'one:verbunden|other:verbunden',
    agentsAddressTitle: 'MCP-Adresse',
    agentsPrivacy:
      'Agenten handeln in Ihrem Namen und sehen nur, was zu {company} gehört. Sie lesen freigegebenes Wissen, niemals private Chats, persönliches Gedächtnis oder private Dateien. Alles Weitere bleibt aus, solange Sie es nicht erlauben.',
    agentsSetupHide: 'Einrichtung ausblenden',
    agentsSetupAnother: 'Weiteren Agenten verbinden',
    agentsHelpInChat: 'Hilfe im Chat',
    agentsHelpStarter: 'Hilf mir, einen externen Agenten über MCP mit Poneva zu verbinden.',
    agentNoticeReady: '{app} hat Wissen aus Poneva gelesen. Die Verbindung ist bereit.',
    agentNoticeAuthorized:
      '{app} ist autorisiert. Stellen Sie dem Agenten eine Frage, um die Verbindung abschließend zu prüfen.',
    agentScopeUnconfirmed:
      'Wir konnten die Änderung für {app} nicht bestätigen. Die Angaben unten zeigen den aktuellen Stand in Poneva.',
    agentScopeNotChanged: 'Der Zugriff von {app} wurde nicht geändert. Versuchen Sie es erneut.',
    agentConfirmRevokeKey:
      'Zugriffsschlüssel „{app}“ widerrufen? Jeder Agent, der ihn nutzt, kann das Wissen von {company} sofort nicht mehr lesen.',
    agentConfirmDisconnect:
      '{app} trennen? {app} kann das Wissen von {company} dann nicht mehr lesen. Ihre Anmeldung bei Poneva ist davon nicht betroffen.',
    agentKeyRevoked: 'Der Zugriffsschlüssel „{app}“ wurde widerrufen.',
    agentDisconnected: '{app} wurde getrennt.',
    agentDisconnectUnconfirmed:
      'Wir konnten nicht bestätigen, dass {app} getrennt wurde. Die Liste zeigt den aktuellen Stand.',
    agentDisconnectFailed: '{app} ist noch verbunden. Versuchen Sie es erneut.',
    setupTitle: 'Externen Agenten verbinden',
    setupManualLabel: 'Oder selbst einrichten',
    setupAddTitle: 'Poneva als MCP-Server hinzufügen',
    setupAddBody:
      'Fügen Sie in den MCP- oder Connector-Einstellungen des Agenten einen Remote-MCP-Server (Streamable HTTP) mit der Adresse oben hinzu.',
    setupConfigToggle: 'Mit Konfigurationsdatei?',
    setupConfigBody: 'Agenten, die ihre MCP-Server in einer JSON-Datei auflisten, verwenden dies:',
    setupSignInTitle: 'Anmelden',
    setupSignInBody:
      'Der Agent öffnet Poneva in Ihrem Browser. Prüfen Sie das Unternehmen und was der Agent tun darf, und wählen Sie dann „Erlauben“.',
    setupSignInFallback:
      'Wenn sich das Anmeldefenster von selbst schließt oder Poneva nie anzeigt oder der Agent nach einem Token oder API-Schlüssel fragt, verwenden Sie stattdessen einen Zugriffsschlüssel.',
    setupTryTitle: 'Ausprobieren',
    setupTryAsk: 'Fragen Sie den Agenten:',
    setupSampleQuestion: 'Was ist unsere aktuelle Produktausrichtung? Nenne die Quelle.',
    setupTryNote: 'Nach dem ersten Lesezugriff wird er hier als „Bereit“ angezeigt.',
    setupPromptTitle: 'Von Ihrem Coding-Agenten einrichten lassen',
    setupPromptBody:
      'Fügen Sie diesen Prompt in Claude Code, Codex oder einen anderen Coding-Agenten ein. Er enthält Ihre MCP-Adresse und führt den Agenten durch die Einrichtung. Den Zugriff geben Sie bei der Anmeldung frei.',
    copyAction: 'Kopieren',
    copiedAction: 'Kopiert',
    copyNamed: '{what} kopieren',
    copiedNamed: '{what} kopiert',
    copiedStatus: '{what} kopiert.',
    copyManualHint:
      'Kopieren ist in diesem Browser nicht verfügbar. Der Text ist markiert; kopieren Sie ihn mit der Tastatur.',
    copyPromptShow: 'Prompt anzeigen',
    copyPromptHide: 'Prompt ausblenden',
    copyWhatAddress: 'MCP-Adresse von Poneva',
    copyWhatConfig: 'MCP-Serverkonfiguration',
    copyWhatSample: 'Beispielfrage',
    copyWhatSampleFor: 'Beispielfrage für {app}',
    copyWhatPrompt: 'Einrichtungs-Prompt',
    copyWhatKey: 'Zugriffsschlüssel für {app}',
    keyCreateAction: 'Zugriffsschlüssel erstellen',
    keySecret:
      'Behandeln Sie ihn wie ein Passwort. Fügen Sie ihn nur in die MCP-, Connector- oder Secret-Einstellungen eines Agenten ein, niemals in eine Website oder in Code, den andere öffnen können.',
    keyFormNote:
      'Für Agenten, die sich nicht anmelden können. Der Schlüssel liest das Wissen von {company} in Ihrem Namen, bis er abläuft oder Sie ihn widerrufen; eine Passwortänderung oder Ihr Austritt aus dem Unternehmen beendet ihn ebenfalls.',
    keyName: 'Bezeichnung',
    keyNamePlaceholder: 'z. B. Nächtlicher Berichtsjob',
    keyLifetime: 'Läuft ab nach',
    keyDays: 'one:Tag|other:Tagen',
    keyAlsoAllow: 'Zusätzlich erlauben',
    keyCreating: 'Wird erstellt…',
    keyCreate: 'Schlüssel erstellen',
    keyCancel: 'Abbrechen',
    keyUnconfirmed:
      'Wir konnten nicht bestätigen, dass der Schlüssel erstellt wurde. Wenn er in der Liste erscheint, widerrufen Sie ihn und erstellen Sie einen neuen.',
    keyNotCreated: 'Der Zugriffsschlüssel wurde nicht erstellt. Versuchen Sie es erneut.',
    keyNewTitle: 'Zugriffsschlüssel für {app}',
    keyCopyNow:
      'Kopieren Sie ihn jetzt. Poneva zeigt ihn nur einmal an und speichert nur einen Fingerabdruck davon.',
    keyTokenRule: 'Ein Feld {token} oder {apiKey} erhält nur den Schlüssel.',
    keyTermToken: 'Token',
    keyTermApiKey: 'API-Schlüssel',
    keyHeaderRule:
      'Ein Feld {header} erhält den Namen {name} und den Wert {value}, wobei Sie {key} durch Ihren Schlüssel ersetzen.',
    keyTermHeader: 'Header',
    keyEndsOn:
      'Er funktioniert ab dem {date} nicht mehr, ebenso wenn Sie ihn widerrufen, Ihr Passwort ändern oder das Unternehmen verlassen.',
    keyEndsExpiry:
      'Er funktioniert nicht mehr, sobald er abläuft, Sie ihn widerrufen, Ihr Passwort ändern oder das Unternehmen verlassen.',
    keyDone: 'Fertig',
    scopeProposeTitle: 'Wissensänderungen vorschlagen',
    scopeProposeDescribe:
      '{app} kann Seitenänderungen vorschlagen. Eine Person mit Veröffentlichungsrechten im Unternehmen prüft jeden Vorschlag, bevor sich etwas ändert.',
    scopeProposeConfirmOn:
      '{app} erlauben, Wissensänderungen bei {company} vorzuschlagen? Jeder Vorschlag wartet unter Wissen → Zur Prüfung, bis eine Person mit Veröffentlichungsrechten ihn freigibt. Von selbst ändert sich nichts.',
    scopeProposeConfirmOff:
      '{app} keine Wissensänderungen mehr vorschlagen lassen? Bereits gesendete Vorschläge bleiben in der Prüfung.',
    scopeProposeEnabled:
      '{app} kann jetzt Wissensänderungen vorschlagen. Wenn die Option in {app} noch nicht erscheint, starten Sie den Agenten neu.',
    scopeProposeDisabled: '{app} kann keine Wissensänderungen mehr vorschlagen.',
    agentReadAccess:
      'Unternehmenswissen lesen: Das Unternehmensprofil und die freigegebenen Wissensseiten. Chats, Dateien, Meetings und persönliches Gedächtnis sind nicht enthalten.',
    agentHintKeyStale:
      'Die Adresse von Poneva hat sich geändert, nachdem dieser Schlüssel erstellt wurde, daher lehnt Poneva ihn ab. Widerrufen Sie ihn und erstellen Sie einen neuen Schlüssel.',
    agentHintStale:
      'Die Adresse von Poneva hat sich geändert, nachdem {app} verbunden wurde, daher lehnt Poneva den Agenten ab. Trennen Sie ihn und verbinden Sie {app} dann erneut mit der Adresse oben.',
    agentHintKeyWaiting:
      'Er wird als „Bereit“ angezeigt, sobald ein Agent den Schlüssel verwendet.',
    agentHintWaiting: 'Stellen Sie {app} eine Frage, um die Verbindung abschließend zu prüfen.',
    agentHintTryAsking: 'Fragen Sie zum Beispiel:',
    agentFactCreated: 'Erstellt',
    agentFactConnected: 'Verbunden',
    agentFactExpires: 'Läuft ab',
    agentFactSignInEnds: 'Anmeldung endet',
    agentFactAccess: 'Zugriff',
    agentNameNote:
      'Den Namen hat der Agent selbst angegeben; Poneva kann ihn nicht prüfen. Trennen Sie jeden Agenten, den Sie nicht kennen.',
    agentSignInNote: 'Wenn die Anmeldung endet, bittet der Agent Sie, sich erneut anzumelden.',
    agentWorking: 'Wird ausgeführt…',
    agentRevokeKey: 'Schlüssel widerrufen',
    agentDisconnect: 'Trennen',
    cardAgentsLabel: 'Ihre verbundenen Agenten',
    cardMore: 'und {more}',
    cardMoreCount: 'one:weiterer in Verbindungen|other:weitere in Verbindungen',
    cardOpenSetup: 'Einrichtung öffnen',
    cardChecking: 'Ihre externen Agenten werden geprüft…',
    cardTryAgain: 'Erneut versuchen',
    connectedAgentsSubtitleAny: 'Agenten, die über Poneva das Wissen Ihres Unternehmens lesen.',
    scopeLibraryReadTitle: 'Ihre Arbeit in Poneva lesen',
    scopeLibraryReadDescribe:
      '{app} kann die Chats, Bibliothekselemente, Dateien, Besprechungen, Skills, Agenten und Apps finden und lesen, die Sie in Poneva öffnen können, und aus Ihren verbundenen Diensten lesen. Der Agent kann auch die Einkaufsassistenten des Unternehmens lesen und das, was Kundinnen und Kunden ihnen gesagt haben.',
    scopeLibraryReadConfirmOn:
      '{app} erlauben, Ihre Arbeit in Poneva bei {company} zu lesen? Der Agent findet und liest in Ihrem Namen, was Sie in Poneva bereits öffnen können.',
    scopeLibraryReadConfirmOff:
      '{app} Ihre Arbeit in Poneva nicht mehr lesen lassen? Dann kann der Agent auch keine Arbeit mehr speichern und Poneva nichts mehr fragen.',
    scopeLibraryReadEnabled:
      '{app} kann jetzt Ihre Arbeit in Poneva lesen. Wenn die Option in {app} noch nicht erscheint, starten Sie den Agenten neu.',
    scopeLibraryReadDisabled: '{app} kann Ihre Arbeit in Poneva nicht mehr lesen.',
    scopeLibraryWriteTitle: 'Arbeit speichern und Poneva fragen',
    scopeLibraryWriteDescribe:
      '{app} kann Arbeit in Ihrer Bibliothek speichern, wo neue Arbeit nur für Sie sichtbar bleibt, und neue Versionen von Arbeit speichern, die Sie bearbeiten können (frühere Versionen bleiben im Verlauf). Der Agent kann Entwürfe von Skills und von Designs der Einkaufsassistenten des Unternehmens speichern; sichtbar sind sie für Kolleginnen und Kollegen, die sie bearbeiten können, und veröffentlichen kann sie in Poneva nur ein Mensch. Außerdem kann er Poneva in privaten Chats fragen, was Credits verbraucht. Er kann auch Lesevorgänge im Webshop des Unternehmens starten, um einen Einkaufsassistenten zu reparieren, und ihm Testfragen stellen; beides verbraucht Credits des Unternehmens.',
    scopeLibraryWriteConfirmOn:
      '{app} erlauben, Arbeit in Ihrer Bibliothek bei {company} zu speichern, darunter neue Versionen von Arbeit, die Sie bearbeiten können, und für Kolleginnen und Kollegen sichtbare Entwürfe, und Poneva für Sie zu fragen? Fragen verbraucht Credits. Der Agent kann auch Ihre Arbeit in Poneva lesen. Auch Shop-Lesevorgänge und Testfragen an Einkaufsassistenten verbrauchen Credits des Unternehmens.',
    scopeLibraryWriteConfirmOff:
      '{app} keine Arbeit mehr speichern und Poneva nicht mehr fragen lassen? Was bereits gespeichert wurde, bleibt in Ihrer Bibliothek.',
    scopeLibraryWriteEnabled:
      '{app} kann jetzt Arbeit speichern und Poneva fragen. Wenn die Option in {app} noch nicht erscheint, starten Sie den Agenten neu.',
    scopeLibraryWriteDisabled:
      '{app} kann keine Arbeit mehr speichern und Poneva nicht mehr fragen.',
  },
  el: {
    mcpServersTitle: 'Προσαρμοσμένες συνδέσεις',
    mcpServersSubtitle:
      'Συνδέστε τον δικό σας διακομιστή εργαλείων. Το Poneva ρωτά πριν αλλάξει οτιδήποτε.',
    mcpServersApproval:
      'Εγκρίνετε κάθε αλλαγή. Για την ανάγνωση ισχύει ό,τι επιλέξατε για κάθε διακομιστή.',
    mcpServersAdd: 'Προσθήκη σύνδεσης',
    connectedAgentsLink: 'Συνδεδεμένοι agents',
    accountConnectionsUnavailable:
      'Οι συνδέσεις λογαριασμών δεν είναι ακόμη διαθέσιμες σε αυτόν τον χώρο εργασίας.',
    connectedAgentsTitle: 'Συνδεδεμένοι agents',
    connectedAgentsSubtitle: 'Agents που διαβάζουν τη γνώση της {company} μέσω του Poneva.',
    connectedAgentsSuggest: 'Αν το επιτρέψετε, προτείνουν ενημερώσεις που ελέγχει ένας άνθρωπος.',
    connectedAgentsList: 'Συνδεδεμένοι τώρα',
    connectingAgentsOff: 'Η σύνδεση agents δεν είναι ακόμη διαθέσιμη για αυτόν τον χώρο εργασίας.',
    checkingConnectedAgents: 'Έλεγχος συνδεδεμένων agents…',
    connectedAgentsLoadFailed: 'Δεν ήταν δυνατή η φόρτωση των συνδεδεμένων agents.',
    connectedAgentsOpen: 'Άνοιγμα Συνδεδεμένων agents',
    agentTimeNotYet: 'όχι ακόμη',
    agentTimeUnknown: 'σε άγνωστη στιγμή',
    agentTimeJustNow: 'μόλις τώρα',
    agentTimeOnDate: 'στις {date}',
    agentErrorNotFound:
      'Αυτός ο agent δεν είναι πλέον συνδεδεμένος. Η λίστα δείχνει την τρέχουσα κατάσταση.',
    agentErrorScopeUnavailable: 'Αυτή η πρόσβαση δεν είναι διαθέσιμη σε αυτόν τον χώρο εργασίας.',
    agentErrorTooManyKeys: 'Έχετε όσα κλειδιά πρόσβασης επιτρέπει το Poneva. Ανακαλέστε πρώτα ένα.',
    agentErrorInvalidInput: 'Ελέγξτε το όνομα του κλειδιού και δοκιμάστε ξανά.',
    agentNotRecorded: 'Δεν έχει καταγραφεί',
    agentMethodKey: 'Κλειδί πρόσβασης',
    agentMethodSignedIn: 'Με σύνδεση λογαριασμού',
    agentStateReconnect: 'Απαιτείται επανασύνδεση',
    agentStateReady: 'Έτοιμος',
    agentStateKeyUnused: 'Δημιουργήθηκε — δεν έχει χρησιμοποιηθεί ακόμη',
    agentStateWaiting: 'Εξουσιοδοτήθηκε — αναμονή για την πρώτη ανάγνωση',
    agentLastUsed: 'Τελευταία χρήση {when}',
    agentKeyExpires: 'Κλειδί πρόσβασης · λήγει στις {date}',
    agentSignInEnds: 'Με σύνδεση λογαριασμού · η σύνδεση λήγει στις {date}',
    agentsNone: 'Δεν έχουν συνδεθεί ακόμη εξωτερικοί agents',
    agentsConnectedCount: 'one:συνδεδεμένος agent|other:συνδεδεμένοι agents',
    agentsWaitingCount: 'one:σε αναμονή|other:σε αναμονή',
    agentsReconnectCount: 'one:για επανασύνδεση|other:για επανασύνδεση',
    addressLocalNetwork:
      'Προσβάσιμη μόνο σε αυτό το δίκτυο. Οι agents που εκτελούνται στο cloud δεν μπορούν να χρησιμοποιήσουν αυτή τη διεύθυνση.',
    addressLocalComputer:
      'Προσβάσιμη μόνο από αυτόν τον υπολογιστή. Οι agents που εκτελούνται στο cloud δεν μπορούν να χρησιμοποιήσουν αυτή τη διεύθυνση.',
    addressUnreachable:
      'Το Poneva δεν μπορεί να φτάσει αυτή τη διεύθυνση αυτή τη στιγμή, οπότε πιθανότατα ούτε οι εξωτερικοί agents μπορούν.',
    agentUnnamed: 'Agent χωρίς όνομα',
    agentCompanyFallback: 'εταιρείας σας',
    agentErrorUnreadable:
      'Το Poneva επέστρεψε μια απάντηση που αυτή η σελίδα δεν μπόρεσε να διαβάσει. Δοκιμάστε ξανά.',
    agentsLoadFailed: 'Δεν ήταν δυνατή η φόρτωση των εξωτερικών agents σας.',
    agentsLoading: 'Φόρτωση εξωτερικών agents…',
    agentsConnectedShort: 'one:συνδεδεμένος|other:συνδεδεμένοι',
    agentsAddressTitle: 'Διεύθυνση MCP',
    agentsPrivacy:
      'Οι agents ενεργούν εκ μέρους σας και βλέπουν μόνο δεδομένα της {company}. Διαβάζουν εγκεκριμένη γνώση, ποτέ ιδιωτικές συνομιλίες, προσωπική μνήμη ή ιδιωτικά αρχεία. Οτιδήποτε επιπλέον μένει απενεργοποιημένο, εκτός αν το επιτρέψετε.',
    agentsSetupHide: 'Απόκρυψη ρύθμισης',
    agentsSetupAnother: 'Σύνδεση άλλου agent',
    agentsHelpInChat: 'Βοήθεια στη συνομιλία',
    agentsHelpStarter: 'Βοήθησέ με να συνδέσω έναν εξωτερικό agent στο Poneva μέσω MCP.',
    agentNoticeReady: 'Ο agent {app} διάβασε γνώση από το Poneva. Η σύνδεση είναι έτοιμη.',
    agentNoticeAuthorized:
      'Ο agent {app} εξουσιοδοτήθηκε. Κάντε του μια ερώτηση για να ολοκληρωθεί ο έλεγχος της σύνδεσης.',
    agentScopeUnconfirmed:
      'Δεν ήταν δυνατή η επιβεβαίωση της αλλαγής για τον agent {app}. Τα παρακάτω στοιχεία δείχνουν τι ισχύει τώρα στο Poneva.',
    agentScopeNotChanged: 'Η πρόσβαση του agent {app} δεν άλλαξε. Δοκιμάστε ξανά.',
    agentConfirmRevokeKey:
      'Ανάκληση του κλειδιού πρόσβασης «{app}»; Όποιος agent το χρησιμοποιεί σταματά αμέσως να διαβάζει τη γνώση της {company}.',
    agentConfirmDisconnect:
      'Αποσύνδεση του agent {app}; Ο {app} δεν θα μπορεί πλέον να διαβάζει τη γνώση της {company}. Η σύνδεσή σας στο Poneva δεν επηρεάζεται.',
    agentKeyRevoked: 'Το κλειδί πρόσβασης «{app}» ανακλήθηκε.',
    agentDisconnected: 'Ο agent {app} αποσυνδέθηκε.',
    agentDisconnectUnconfirmed:
      'Δεν ήταν δυνατό να επιβεβαιωθεί ότι ο agent {app} αποσυνδέθηκε. Η λίστα δείχνει την τρέχουσα κατάσταση.',
    agentDisconnectFailed: 'Ο agent {app} είναι ακόμη συνδεδεμένος. Δοκιμάστε ξανά.',
    setupTitle: 'Σύνδεση εξωτερικού agent',
    setupManualLabel: 'Ή ρυθμίστε τον μόνοι σας',
    setupAddTitle: 'Προσθήκη του Poneva ως διακομιστή MCP',
    setupAddBody:
      'Στις ρυθμίσεις MCP ή συνδέσμων (connectors) του agent, προσθέστε έναν απομακρυσμένο διακομιστή MCP (Streamable HTTP) με την παραπάνω διεύθυνση.',
    setupConfigToggle: 'Χρησιμοποιείτε αρχείο ρυθμίσεων;',
    setupConfigBody:
      'Οι agents που ορίζουν τους διακομιστές MCP τους σε αρχείο JSON χρειάζονται αυτό:',
    setupSignInTitle: 'Σύνδεση',
    setupSignInBody:
      'Ο agent ανοίγει το Poneva στο πρόγραμμα περιήγησής σας. Ελέγξτε την εταιρεία και τι επιτρέπεται να κάνει ο agent και, στη συνέχεια, επιλέξτε «Να επιτρέπεται».',
    setupSignInFallback:
      'Αν το παράθυρο σύνδεσης κλείσει μόνο του ή δεν εμφανίσει ποτέ το Poneva ή αν ο agent ζητήσει token ή κλειδί API, χρησιμοποιήστε αντί γι’ αυτό ένα κλειδί πρόσβασης.',
    setupTryTitle: 'Δοκιμάστε το',
    setupTryAsk: 'Ρωτήστε τον agent:',
    setupSampleQuestion: 'Ποια είναι η τρέχουσα κατεύθυνση του προϊόντος μας; Ανέφερε την πηγή.',
    setupTryNote: 'Μετά την πρώτη ανάγνωση εμφανίζεται εδώ ως «Έτοιμος».',
    setupPromptTitle: 'Αφήστε τον coding agent σας να το ρυθμίσει',
    setupPromptBody:
      'Επικολλήστε αυτό το prompt στο Claude Code, στο Codex ή σε άλλον coding agent. Περιλαμβάνει τη διεύθυνση MCP σας και καθοδηγεί τον agent στη ρύθμιση. Εγκρίνετε την πρόσβαση όταν συνδεθείτε.',
    copyAction: 'Αντιγραφή',
    copiedAction: 'Αντιγράφηκε',
    copyNamed: 'Αντιγραφή: {what}',
    copiedNamed: 'Αντιγράφηκε: {what}',
    copiedStatus: 'Αντιγράφηκε: {what}.',
    copyManualHint:
      'Η αντιγραφή δεν είναι διαθέσιμη σε αυτό το πρόγραμμα περιήγησης. Το κείμενο είναι επιλεγμένο· αντιγράψτε το με το πληκτρολόγιο.',
    copyPromptShow: 'Προβολή prompt',
    copyPromptHide: 'Απόκρυψη prompt',
    copyWhatAddress: 'διεύθυνση MCP του Poneva',
    copyWhatConfig: 'ρύθμιση διακομιστή MCP',
    copyWhatSample: 'ερώτηση-παράδειγμα',
    copyWhatSampleFor: 'ερώτηση-παράδειγμα για {app}',
    copyWhatPrompt: 'prompt ρύθμισης',
    copyWhatKey: 'κλειδί πρόσβασης για {app}',
    keyCreateAction: 'Δημιουργία κλειδιού πρόσβασης',
    keySecret:
      'Χειριστείτε το σαν κωδικό πρόσβασης. Επικολλήστε το μόνο στις ρυθμίσεις MCP, συνδέσμων ή μυστικών ενός agent, ποτέ σε ιστότοπο ή σε κώδικα που μπορούν να ανοίξουν άλλοι.',
    keyFormNote:
      'Για agent που δεν μπορεί να συνδεθεί. Το κλειδί διαβάζει τη γνώση της {company} εκ μέρους σας μέχρι να λήξει ή να το ανακαλέσετε· η αλλαγή του κωδικού σας ή η αποχώρησή σας από την εταιρεία επίσης το τερματίζει.',
    keyName: 'Όνομα',
    keyNamePlaceholder: 'π.χ. Νυχτερινή αναφορά',
    keyLifetime: 'Λήγει μετά από',
    keyDays: 'one:ημέρα|other:ημέρες',
    keyAlsoAllow: 'Επίσης να επιτρέπεται',
    keyCreating: 'Δημιουργία…',
    keyCreate: 'Δημιουργία κλειδιού',
    keyCancel: 'Ακύρωση',
    keyUnconfirmed:
      'Δεν ήταν δυνατό να επιβεβαιωθεί η δημιουργία του κλειδιού. Αν εμφανίζεται στη λίστα, ανακαλέστε το και δημιουργήστε νέο.',
    keyNotCreated: 'Το κλειδί πρόσβασης δεν δημιουργήθηκε. Δοκιμάστε ξανά.',
    keyNewTitle: 'Κλειδί πρόσβασης για {app}',
    keyCopyNow:
      'Αντιγράψτε το τώρα. Το Poneva το εμφανίζει μόνο μία φορά και αποθηκεύει μόνο ένα αποτύπωμά του.',
    keyTokenRule: 'Ένα πεδίο {token} ή {apiKey} δέχεται μόνο το κλειδί.',
    keyTermToken: 'token',
    keyTermApiKey: 'κλειδί API',
    keyHeaderRule:
      'Ένα πεδίο {header} δέχεται το όνομα {name} και την τιμή {value}, με το κλειδί σας στη θέση του {key}.',
    keyTermHeader: 'κεφαλίδα',
    keyEndsOn:
      'Σταματά να λειτουργεί στις {date}, όταν το ανακαλέσετε και όταν αλλάξετε κωδικό πρόσβασης ή αποχωρήσετε από την εταιρεία.',
    keyEndsExpiry:
      'Σταματά να λειτουργεί όταν λήξει, όταν το ανακαλέσετε και όταν αλλάξετε κωδικό πρόσβασης ή αποχωρήσετε από την εταιρεία.',
    keyDone: 'Τέλος',
    scopeProposeTitle: 'Πρόταση ενημερώσεων γνώσης',
    scopeProposeDescribe:
      'Ο agent {app} μπορεί να προτείνει αλλαγές σε σελίδες. Ένας υπεύθυνος δημοσίευσης της εταιρείας ελέγχει κάθε πρόταση πριν αλλάξει οτιδήποτε.',
    scopeProposeConfirmOn:
      'Να επιτρέπεται στον agent {app} να προτείνει ενημερώσεις γνώσης της {company}; Κάθε πρόταση περιμένει στο Γνώση → Για έλεγχο μέχρι να την εγκρίνει ένας υπεύθυνος δημοσίευσης της εταιρείας. Τίποτα δεν αλλάζει από μόνο του.',
    scopeProposeConfirmOff:
      'Διακοπή των προτάσεων ενημερώσεων γνώσης από τον agent {app}; Οι προτάσεις που έχει ήδη στείλει παραμένουν για έλεγχο.',
    scopeProposeEnabled:
      'Ο agent {app} μπορεί πλέον να προτείνει ενημερώσεις γνώσης. Αν η επιλογή δεν εμφανίζεται ακόμη στον {app}, κάντε επανεκκίνηση.',
    scopeProposeDisabled: 'Ο agent {app} δεν μπορεί πλέον να προτείνει ενημερώσεις γνώσης.',
    agentReadAccess:
      'Ανάγνωση γνώσης της εταιρείας: Το προφίλ της εταιρείας και οι εγκεκριμένες σελίδες γνώσης. Δεν περιλαμβάνονται συνομιλίες, αρχεία, συσκέψεις και προσωπική μνήμη.',
    agentHintKeyStale:
      'Η διεύθυνση του Poneva άλλαξε μετά τη δημιουργία αυτού του κλειδιού, οπότε το Poneva το απορρίπτει. Ανακαλέστε το και δημιουργήστε νέο κλειδί.',
    agentHintStale:
      'Η διεύθυνση του Poneva άλλαξε μετά τη σύνδεση του agent {app}, οπότε το Poneva τον απορρίπτει. Αποσυνδέστε τον και, στη συνέχεια, συνδέστε ξανά τον {app} με την παραπάνω διεύθυνση.',
    agentHintKeyWaiting: 'Εμφανίζεται ως «Έτοιμος» μόλις ένας agent χρησιμοποιήσει το κλειδί.',
    agentHintWaiting:
      'Κάντε μια ερώτηση στον agent {app} για να ολοκληρωθεί ο έλεγχος της σύνδεσης.',
    agentHintTryAsking: 'Δοκιμάστε να ρωτήσετε:',
    agentFactCreated: 'Δημιουργήθηκε',
    agentFactConnected: 'Συνδέθηκε',
    agentFactExpires: 'Λήγει',
    agentFactSignInEnds: 'Η σύνδεση λήγει',
    agentFactAccess: 'Πρόσβαση',
    agentNameNote:
      'Το όνομα είναι αυτό που δήλωσε ο agent· το Poneva δεν μπορεί να το επαληθεύσει. Αποσυνδέστε κάθε agent που δεν αναγνωρίζετε.',
    agentSignInNote: 'Όταν λήξει η σύνδεσή του, ο agent σάς ζητά να συνδεθείτε ξανά.',
    agentWorking: 'Σε εξέλιξη…',
    agentRevokeKey: 'Ανάκληση κλειδιού',
    agentDisconnect: 'Αποσύνδεση',
    cardAgentsLabel: 'Οι συνδεδεμένοι agents σας',
    cardMore: 'και {more}',
    cardMoreCount: 'one:ακόμη στις Συνδέσεις|other:ακόμη στις Συνδέσεις',
    cardOpenSetup: 'Άνοιγμα ρύθμισης',
    cardChecking: 'Έλεγχος των εξωτερικών agents σας…',
    cardTryAgain: 'Δοκιμάστε ξανά',
    connectedAgentsSubtitleAny: 'Agents που διαβάζουν τη γνώση της εταιρείας σας μέσω του Poneva.',
    scopeLibraryReadTitle: 'Ανάγνωση της δουλειάς σας στο Poneva',
    scopeLibraryReadDescribe:
      'Ο agent {app} μπορεί να βρίσκει και να διαβάζει τις συνομιλίες, τη Βιβλιοθήκη, τα αρχεία, τις συσκέψεις, τα Skills, τους agents και τις εφαρμογές που μπορείτε να ανοίξετε στο Poneva, καθώς και να διαβάζει από τις συνδεδεμένες υπηρεσίες σας. Μπορεί επίσης να διαβάζει τους βοηθούς αγορών της εταιρείας και όσα είπαν σε αυτούς οι αγοραστές.',
    scopeLibraryReadConfirmOn:
      'Να επιτρέπεται στον agent {app} να διαβάζει τη δουλειά σας στο Poneva στην {company}; Βρίσκει και διαβάζει ό,τι μπορείτε ήδη να ανοίξετε στο Poneva, ως εσείς.',
    scopeLibraryReadConfirmOff:
      'Διακοπή της ανάγνωσης της δουλειάς σας στο Poneva από τον agent {app}; Τότε δεν θα μπορεί ούτε να αποθηκεύει δουλειά ούτε να ρωτά το Poneva.',
    scopeLibraryReadEnabled:
      'Ο agent {app} μπορεί πλέον να διαβάζει τη δουλειά σας στο Poneva. Αν η επιλογή δεν εμφανίζεται ακόμη στον {app}, κάντε επανεκκίνηση.',
    scopeLibraryReadDisabled:
      'Ο agent {app} δεν μπορεί πλέον να διαβάζει τη δουλειά σας στο Poneva.',
    scopeLibraryWriteTitle: 'Αποθήκευση δουλειάς και ερωτήσεις στο Poneva',
    scopeLibraryWriteDescribe:
      'Ο agent {app} μπορεί να αποθηκεύει δουλειά στη Βιβλιοθήκη σας, όπου η νέα δουλειά μένει ιδιωτική, μόνο για εσάς, και να αποθηκεύει νέες εκδόσεις δουλειάς που μπορείτε να επεξεργαστείτε (οι προηγούμενες εκδόσεις μένουν στο Ιστορικό). Μπορεί να αποθηκεύει πρόχειρα για Skills και για σχεδιασμούς των βοηθών αγορών της εταιρείας, τα οποία βλέπουν οι συνάδελφοι που μπορούν να τα επεξεργαστούν· μόνο ένας άνθρωπος τα δημοσιεύει στο Poneva. Μπορεί επίσης να ρωτά το Poneva σε ιδιωτικές συνομιλίες, κάτι που χρησιμοποιεί πιστώσεις. Μπορεί επίσης να ξεκινά αναγνώσεις του ηλεκτρονικού καταστήματος της εταιρείας για να επιδιορθώσει έναν βοηθό αγορών και να του κάνει δοκιμαστικές ερωτήσεις· και τα δύο καταναλώνουν πιστώσεις της εταιρείας.',
    scopeLibraryWriteConfirmOn:
      'Να επιτρέπεται στον agent {app} να αποθηκεύει δουλειά στη Βιβλιοθήκη σας στην {company}, συμπεριλαμβανομένων νέων εκδόσεων δουλειάς που μπορείτε να επεξεργαστείτε και προχείρων που μπορούν να δουν οι συνάδελφοι, και να ρωτά το Poneva για λογαριασμό σας; Οι ερωτήσεις χρησιμοποιούν πιστώσεις. Θα μπορεί επίσης να διαβάζει τη δουλειά σας στο Poneva. Οι αναγνώσεις του καταστήματος και οι δοκιμαστικές ερωτήσεις προς τους βοηθούς αγορών καταναλώνουν επίσης πιστώσεις της εταιρείας.',
    scopeLibraryWriteConfirmOff:
      'Διακοπή της αποθήκευσης δουλειάς και των ερωτήσεων στο Poneva από τον agent {app}; Ό,τι έχει ήδη αποθηκεύσει παραμένει στη Βιβλιοθήκη σας.',
    scopeLibraryWriteEnabled:
      'Ο agent {app} μπορεί πλέον να αποθηκεύει δουλειά και να ρωτά το Poneva. Αν η επιλογή δεν εμφανίζεται ακόμη στον {app}, κάντε επανεκκίνηση.',
    scopeLibraryWriteDisabled:
      'Ο agent {app} δεν μπορεί πλέον να αποθηκεύει δουλειά ούτε να ρωτά το Poneva.',
  },
  es: {
    mcpServersTitle: 'Conexiones personalizadas',
    mcpServersSubtitle:
      'Conecta tu propio servidor de herramientas. Poneva pregunta antes de cambiar nada.',
    mcpServersApproval:
      'Apruebas cada cambio. Para la lectura, se aplica lo que elegiste para cada servidor.',
    mcpServersAdd: 'Añadir conexión',
    connectedAgentsLink: 'Agentes conectados',
    accountConnectionsUnavailable:
      'Las conexiones de cuenta aún no están disponibles en este espacio de trabajo.',
    connectedAgentsTitle: 'Agentes conectados',
    connectedAgentsSubtitle: 'Agentes que leen el conocimiento de {company} a través de Poneva.',
    connectedAgentsSuggest: 'Si lo permites, sugieren cambios para que una persona los revise.',
    connectedAgentsList: 'Conectados ahora',
    connectingAgentsOff: 'Conectar agentes aún no está disponible en este espacio de trabajo.',
    checkingConnectedAgents: 'Comprobando los agentes conectados…',
    connectedAgentsLoadFailed: 'No se pudieron cargar los agentes conectados.',
    connectedAgentsOpen: 'Abrir Agentes conectados',
    agentTimeNotYet: 'todavía no',
    agentTimeUnknown: 'en un momento desconocido',
    agentTimeJustNow: 'justo ahora',
    agentTimeOnDate: 'el {date}',
    agentErrorNotFound: 'Este agente ya no está conectado. La lista muestra el estado actual.',
    agentErrorScopeUnavailable: 'Ese acceso no está disponible en este espacio de trabajo.',
    agentErrorTooManyKeys:
      'Ya tienes todas las claves de acceso que permite Poneva. Revoca una primero.',
    agentErrorInvalidInput: 'Revisa el nombre de la clave e inténtalo de nuevo.',
    agentNotRecorded: 'No registrado',
    agentMethodKey: 'Clave de acceso',
    agentMethodSignedIn: 'Sesión iniciada',
    agentStateReconnect: 'Hay que reconectar',
    agentStateReady: 'Listo',
    agentStateKeyUnused: 'Creada — aún sin usar',
    agentStateWaiting: 'Autorizado — esperando la primera lectura',
    agentLastUsed: 'Último uso {when}',
    agentKeyExpires: 'Clave de acceso · caduca el {date}',
    agentSignInEnds: 'Sesión iniciada · la sesión termina el {date}',
    agentsNone: 'Aún no hay agentes externos conectados',
    agentsConnectedCount: 'one:agente conectado|other:agentes conectados',
    agentsWaitingCount: 'one:en espera|other:en espera',
    agentsReconnectCount: 'one:por reconectar|other:por reconectar',
    addressLocalNetwork:
      'Solo es accesible en esta red. Los agentes que se ejecutan en la nube no pueden usar esta dirección.',
    addressLocalComputer:
      'Solo es accesible desde este equipo. Los agentes que se ejecutan en la nube no pueden usar esta dirección.',
    addressUnreachable:
      'Poneva no puede acceder a esta dirección ahora mismo, así que probablemente los agentes externos tampoco.',
    agentUnnamed: 'Agente sin nombre',
    agentCompanyFallback: 'tu empresa',
    agentErrorUnreadable:
      'Poneva devolvió una respuesta que esta página no pudo leer. Inténtalo de nuevo.',
    agentsLoadFailed: 'No se pudieron cargar tus agentes externos.',
    agentsLoading: 'Cargando agentes externos…',
    agentsConnectedShort: 'one:conectado|other:conectados',
    agentsAddressTitle: 'Dirección MCP',
    agentsPrivacy:
      'Los agentes actúan en tu nombre y solo ven datos de {company}. Leen conocimiento aprobado, nunca chats privados, memoria personal ni archivos privados. Todo lo demás sigue desactivado salvo que lo permitas.',
    agentsSetupHide: 'Ocultar configuración',
    agentsSetupAnother: 'Conectar otro agente',
    agentsHelpInChat: 'Pedir ayuda en el chat',
    agentsHelpStarter: 'Ayúdame a conectar un agente externo a Poneva mediante MCP.',
    agentNoticeReady: '{app} leyó conocimiento de Poneva. La conexión está lista.',
    agentNoticeAuthorized:
      '{app} está autorizado. Hazle una pregunta para terminar de comprobar la conexión.',
    agentScopeUnconfirmed:
      'No pudimos confirmar el cambio para {app}. Los detalles de abajo muestran lo que Poneva tiene ahora.',
    agentScopeNotChanged: 'El acceso de {app} no cambió. Inténtalo de nuevo.',
    agentConfirmRevokeKey:
      '¿Revocar la clave de acceso «{app}»? Cualquier agente que la use dejará de leer el conocimiento de {company} de inmediato.',
    agentConfirmDisconnect:
      '¿Desconectar {app}? {app} ya no podrá leer el conocimiento de {company}. Tu sesión en Poneva no se ve afectada.',
    agentKeyRevoked: 'La clave de acceso «{app}» se ha revocado.',
    agentDisconnected: '{app} se ha desconectado.',
    agentDisconnectUnconfirmed:
      'No pudimos confirmar que {app} se desconectara. La lista muestra el estado actual.',
    agentDisconnectFailed: '{app} sigue conectado. Inténtalo de nuevo.',
    setupTitle: 'Conectar un agente externo',
    setupManualLabel: 'O configúralo tú mismo',
    setupAddTitle: 'Añade Poneva como servidor MCP',
    setupAddBody:
      'En los ajustes de MCP o de conectores del agente, añade un servidor MCP remoto (Streamable HTTP) con la dirección de arriba.',
    setupConfigToggle: '¿Usas un archivo de configuración?',
    setupConfigBody: 'Los agentes que definen sus servidores MCP en un archivo JSON usan esto:',
    setupSignInTitle: 'Inicia sesión',
    setupSignInBody:
      'El agente abre Poneva en tu navegador. Revisa la empresa y lo que puede hacer el agente y, después, elige «Permitir».',
    setupSignInFallback:
      'Si la ventana de inicio de sesión se cierra sola o nunca muestra Poneva, o si el agente pide un token o una clave de API, usa una clave de acceso.',
    setupTryTitle: 'Pruébalo',
    setupTryAsk: 'Pregunta al agente:',
    setupSampleQuestion: '¿Cuál es el rumbo actual de nuestro producto? Cita la fuente.',
    setupTryNote: 'Aparecerá aquí como «Listo» tras su primera lectura.',
    setupPromptTitle: 'Deja que tu agente de programación lo configure',
    setupPromptBody:
      'Pega este prompt en Claude Code, Codex u otro agente de programación. Incluye tu dirección MCP y guía al agente durante la configuración. Apruebas el acceso al iniciar sesión.',
    copyAction: 'Copiar',
    copiedAction: 'Copiado',
    copyNamed: 'Copiar {what}',
    copiedNamed: 'Copiado: {what}',
    copiedStatus: 'Copiado: {what}.',
    copyManualHint:
      'No se puede copiar en este navegador. El texto está seleccionado; cópialo con el teclado.',
    copyPromptShow: 'Ver prompt',
    copyPromptHide: 'Ocultar prompt',
    copyWhatAddress: 'la dirección MCP de Poneva',
    copyWhatConfig: 'la configuración del servidor MCP',
    copyWhatSample: 'la pregunta de ejemplo',
    copyWhatSampleFor: 'la pregunta de ejemplo para {app}',
    copyWhatPrompt: 'el prompt de configuración',
    copyWhatKey: 'la clave de acceso de {app}',
    keyCreateAction: 'Crear una clave de acceso',
    keySecret:
      'Trátala como una contraseña. Pégala solo en los ajustes de MCP, conectores o secretos de un agente, nunca en un sitio web ni en código que otros puedan abrir.',
    keyFormNote:
      'Para agentes que no pueden iniciar sesión. La clave lee el conocimiento de {company} en tu nombre hasta que caduque o la revoques; también deja de funcionar si cambias tu contraseña o dejas la empresa.',
    keyName: 'Nombre',
    keyNamePlaceholder: 'p. ej., Informe nocturno',
    keyLifetime: 'Caduca tras',
    keyDays: 'one:día|other:días',
    keyAlsoAllow: 'Permitir también',
    keyCreating: 'Creando…',
    keyCreate: 'Crear clave',
    keyCancel: 'Cancelar',
    keyUnconfirmed:
      'No pudimos confirmar que se creara la clave. Si aparece en la lista, revócala y crea una nueva.',
    keyNotCreated: 'No se creó la clave de acceso. Inténtalo de nuevo.',
    keyNewTitle: 'Clave de acceso de {app}',
    keyCopyNow: 'Cópiala ahora. Poneva solo la muestra una vez y solo guarda una huella de ella.',
    keyTokenRule: 'Un campo {token} o {apiKey} lleva solo la clave.',
    keyTermToken: 'token',
    keyTermApiKey: 'clave de API',
    keyHeaderRule:
      'Un campo {header} lleva el nombre {name} y el valor {value}, con tu clave en lugar de {key}.',
    keyTermHeader: 'encabezado',
    keyEndsOn:
      'Deja de funcionar el {date}, cuando la revoques y cuando cambies tu contraseña o dejes la empresa.',
    keyEndsExpiry:
      'Deja de funcionar cuando caduque, cuando la revoques y cuando cambies tu contraseña o dejes la empresa.',
    keyDone: 'Hecho',
    scopeProposeTitle: 'Sugerir cambios en el conocimiento',
    scopeProposeDescribe:
      '{app} puede sugerir cambios en páginas. Una persona con permiso de publicación en la empresa revisa cada uno antes de que cambie nada.',
    scopeProposeConfirmOn:
      '¿Permitir que {app} sugiera cambios en el conocimiento de {company}? Cada sugerencia espera en Conocimiento → Para revisar hasta que una persona con permiso de publicación en la empresa la apruebe. Nada cambia por sí solo.',
    scopeProposeConfirmOff:
      '¿Impedir que {app} sugiera cambios en el conocimiento? Las sugerencias que ya envió siguen en revisión.',
    scopeProposeEnabled:
      '{app} ya puede sugerir cambios en el conocimiento. Si la opción aún no aparece en {app}, reinícialo.',
    scopeProposeDisabled: '{app} ya no puede sugerir cambios en el conocimiento.',
    agentReadAccess:
      'Leer el conocimiento de la empresa: el perfil de la empresa y las páginas de conocimiento aprobadas. No incluye chats, archivos, reuniones ni memoria personal.',
    agentHintKeyStale:
      'La dirección de Poneva cambió después de crear esta clave, así que Poneva la rechaza. Revócala y crea una clave nueva.',
    agentHintStale:
      'La dirección de Poneva cambió después de conectar {app}, así que Poneva lo rechaza. Desconéctalo y vuelve a conectar {app} con la dirección de arriba.',
    agentHintKeyWaiting: 'Aparecerá como «Listo» cuando un agente use la clave.',
    agentHintWaiting: 'Haz una pregunta a {app} para terminar de comprobar la conexión.',
    agentHintTryAsking: 'Prueba a preguntar:',
    agentFactCreated: 'Creado',
    agentFactConnected: 'Conectado',
    agentFactExpires: 'Caduca',
    agentFactSignInEnds: 'Fin de la sesión',
    agentFactAccess: 'Acceso',
    agentNameNote:
      'El nombre es el que indicó el agente; Poneva no puede verificarlo. Desconecta cualquier agente que no reconozcas.',
    agentSignInNote: 'Cuando termine su sesión, el agente te pedirá que vuelvas a iniciar sesión.',
    agentWorking: 'Procesando…',
    agentRevokeKey: 'Revocar clave',
    agentDisconnect: 'Desconectar',
    cardAgentsLabel: 'Tus agentes conectados',
    cardMore: 'y {more}',
    cardMoreCount: 'one:más en Conexiones|other:más en Conexiones',
    cardOpenSetup: 'Abrir configuración',
    cardChecking: 'Comprobando tus agentes externos…',
    cardTryAgain: 'Reintentar',
    connectedAgentsSubtitleAny:
      'Agentes que leen el conocimiento de tu empresa a través de Poneva.',
    scopeLibraryReadTitle: 'Leer tu trabajo en Poneva',
    scopeLibraryReadDescribe:
      '{app} puede encontrar y leer los chats, la Biblioteca, los archivos, las reuniones, los Skills, los agentes y las apps que puedes abrir en Poneva, y leer de tus servicios conectados. También puede leer los asistentes de compras de la empresa y lo que los compradores les han dicho.',
    scopeLibraryReadConfirmOn:
      '¿Permitir que {app} lea tu trabajo de {company} en Poneva? Encuentra y lee lo que ya puedes abrir en Poneva, en tu nombre.',
    scopeLibraryReadConfirmOff:
      '¿Impedir que {app} lea tu trabajo en Poneva? Entonces tampoco podrá guardar trabajo ni preguntar a Poneva.',
    scopeLibraryReadEnabled:
      '{app} ya puede leer tu trabajo en Poneva. Si la opción aún no aparece en {app}, reinícialo.',
    scopeLibraryReadDisabled: '{app} ya no puede leer tu trabajo en Poneva.',
    scopeLibraryWriteTitle: 'Guardar trabajo y preguntar a Poneva',
    scopeLibraryWriteDescribe:
      '{app} puede guardar trabajo en tu Biblioteca, donde el trabajo nuevo sigue siendo privado, solo para ti, y guardar nuevas versiones del trabajo que puedes editar (las versiones anteriores se quedan en Historial). Puede guardar borradores de Skills y de diseños de los asistentes de compras de la empresa, que ven los compañeros que pueden editarlos; solo una persona los publica en Poneva. También puede preguntar a Poneva en chats privados, lo que consume créditos. También puede iniciar lecturas de la tienda web de la empresa para reparar un asistente de compras y hacerle preguntas de prueba; ambas cosas usan créditos de la empresa.',
    scopeLibraryWriteConfirmOn:
      '¿Permitir que {app} guarde trabajo en tu Biblioteca de {company}, incluidas nuevas versiones del trabajo que puedes editar y borradores que pueden ver tus compañeros, y pregunte a Poneva por ti? Las preguntas consumen créditos. También podrá leer tu trabajo en Poneva. Las lecturas de la tienda y las preguntas de prueba a los asistentes de compras también usan créditos de la empresa.',
    scopeLibraryWriteConfirmOff:
      '¿Impedir que {app} guarde trabajo y pregunte a Poneva? Lo que ya guardó se queda en tu Biblioteca.',
    scopeLibraryWriteEnabled:
      '{app} ya puede guardar trabajo y preguntar a Poneva. Si la opción aún no aparece en {app}, reinícialo.',
    scopeLibraryWriteDisabled: '{app} ya no puede guardar trabajo ni preguntar a Poneva.',
  },
  et: {
    mcpServersTitle: 'Kohandatud ühendused',
    mcpServersSubtitle: 'Ühenda oma tööriistaserver. Poneva küsib enne, kui midagi muudab.',
    mcpServersApproval:
      'Kinnitad iga muudatuse. Lugemisel kehtib see, mille iga serveri jaoks valisid.',
    mcpServersAdd: 'Lisa ühendus',
    connectedAgentsLink: 'Ühendatud agendid',
    accountConnectionsUnavailable: 'Kontoühendused pole selles tööruumis veel saadaval.',
    connectedAgentsTitle: 'Ühendatud agendid',
    connectedAgentsSubtitle: 'Agendid, kes loevad Poneva kaudu ettevõtte {company} teadmisi.',
    connectedAgentsSuggest: 'Kui lubad, pakuvad nad muudatusi, mille inimene üle vaatab.',
    connectedAgentsList: 'Praegu ühendatud',
    connectingAgentsOff: 'Agentide ühendamine pole selles tööruumis veel saadaval.',
    checkingConnectedAgents: 'Ühendatud agentide kontrollimine…',
    connectedAgentsLoadFailed: 'Ühendatud agente ei õnnestunud laadida.',
    connectedAgentsOpen: 'Ava ühendatud agendid',
    agentTimeNotYet: 'veel mitte',
    agentTimeUnknown: 'teadmata ajal',
    agentTimeJustNow: 'just praegu',
    agentTimeOnDate: '{date}',
    agentErrorNotFound: 'See agent pole enam ühendatud. Loend näitab praegust olekut.',
    agentErrorScopeUnavailable: 'See juurdepääs pole selles tööruumis saadaval.',
    agentErrorTooManyKeys:
      'Teil on nii palju juurdepääsuvõtmeid, kui Poneva lubab. Tühistage esmalt üks.',
    agentErrorInvalidInput: 'Kontrollige võtme nime ja proovige uuesti.',
    agentNotRecorded: 'Pole salvestatud',
    agentMethodKey: 'Juurdepääsuvõti',
    agentMethodSignedIn: 'Sisse logitud',
    agentStateReconnect: 'Vajab uuesti ühendamist',
    agentStateReady: 'Valmis',
    agentStateKeyUnused: 'Loodud – veel kasutamata',
    agentStateWaiting: 'Volitatud – ootab esimest lugemist',
    agentLastUsed: 'Viimati kasutatud {when}',
    agentKeyExpires: 'Juurdepääsuvõti · aegub {date}',
    agentSignInEnds: 'Sisse logitud · sisselogimine lõpeb {date}',
    agentsNone: 'Väliseid agente pole veel ühendatud',
    agentsConnectedCount: 'one:agent ühendatud|other:agenti ühendatud',
    agentsWaitingCount: 'one:ootel|other:ootel',
    agentsReconnectCount: 'one:vajab uuesti ühendamist|other:vajavad uuesti ühendamist',
    addressLocalNetwork:
      'Kättesaadav ainult selles võrgus. Pilves töötavad agendid ei saa seda aadressi kasutada.',
    addressLocalComputer:
      'Kättesaadav ainult sellest arvutist. Pilves töötavad agendid ei saa seda aadressi kasutada.',
    addressUnreachable:
      'Poneva ei jõua praegu selle aadressini, seega ei jõua sinna tõenäoliselt ka välised agendid.',
    agentUnnamed: 'Nimeta agent',
    agentCompanyFallback: 'teie ettevõte',
    agentErrorUnreadable:
      'Poneva saatis vastuse, mida see leht ei suutnud lugeda. Proovige uuesti.',
    agentsLoadFailed: 'Teie väliseid agente ei õnnestunud laadida.',
    agentsLoading: 'Väliste agentide laadimine…',
    agentsConnectedShort: 'one:ühendatud|other:ühendatud',
    agentsAddressTitle: 'MCP-aadress',
    agentsPrivacy:
      'Agendid tegutsevad teie nimel ja näevad ainult ettevõtte {company} andmeid. Nad loevad kinnitatud teadmisi, mitte kunagi privaatseid vestlusi, isiklikku mälu ega privaatseid faile. Kõik muu jääb välja lülitatuks, kui te seda ei luba.',
    agentsSetupHide: 'Peida seadistus',
    agentsSetupAnother: 'Ühenda veel üks agent',
    agentsHelpInChat: 'Abi vestluses',
    agentsHelpStarter: 'Aita mul ühendada väline agent MCP kaudu Ponevaga.',
    agentNoticeReady: '{app} luges Poneva teadmisi. Ühendus on valmis.',
    agentNoticeAuthorized:
      '{app} on volitatud. Esitage talle küsimus, et ühenduse kontroll lõpule viia.',
    agentScopeUnconfirmed:
      'Me ei saanud agendi {app} muudatust kinnitada. Allolevad andmed näitavad, mis Poneval praegu on.',
    agentScopeNotChanged: 'Agendi {app} juurdepääs ei muutunud. Proovige uuesti.',
    agentConfirmRevokeKey:
      'Kas tühistada juurdepääsuvõti „{app}“? Kõik agendid, kes seda kasutavad, lõpetavad kohe ettevõtte {company} teadmiste lugemise.',
    agentConfirmDisconnect:
      'Kas katkestada ühendus agendiga {app}? {app} ei saa enam ettevõtte {company} teadmisi lugeda. See ei mõjuta teie Poneva sisselogimist.',
    agentKeyRevoked: 'Juurdepääsuvõti „{app}“ on tühistatud.',
    agentDisconnected: 'Ühendus agendiga {app} on katkestatud.',
    agentDisconnectUnconfirmed:
      'Me ei saanud kinnitada, et ühendus agendiga {app} katkestati. Loend näitab praegust olekut.',
    agentDisconnectFailed: '{app} on endiselt ühendatud. Proovige uuesti.',
    setupTitle: 'Ühenda väline agent',
    setupManualLabel: 'Või seadistage ise',
    setupAddTitle: 'Lisage Poneva MCP-serverina',
    setupAddBody:
      'Lisage agendi MCP- või konnektoriseadetes kaug-MCP-server (Streamable HTTP) ülaltoodud aadressiga.',
    setupConfigToggle: 'Kas kasutate konfiguratsioonifaili?',
    setupConfigBody: 'Agendid, kes loetlevad oma MCP-serverid JSON-failis, vajavad seda:',
    setupSignInTitle: 'Logige sisse',
    setupSignInBody:
      'Agent avab Poneva teie brauseris. Kontrollige ettevõtet ja seda, mida agent teha tohib, ning valige seejärel „Luba“.',
    setupSignInFallback:
      'Kui sisselogimisaken sulgub iseenesest või ei näita kunagi Ponevat või kui agent küsib tokenit või API-võtit, kasutage selle asemel juurdepääsuvõtit.',
    setupTryTitle: 'Proovige järele',
    setupTryAsk: 'Küsige agendilt:',
    setupSampleQuestion: 'Milline on meie praegune tootesuund? Viita allikale.',
    setupTryNote: 'Pärast esimest lugemist kuvatakse see siin olekuga „Valmis“.',
    setupPromptTitle: 'Laske oma kodeerimisagendil see seadistada',
    setupPromptBody:
      'Kleepige see viip rakendusse Claude Code, Codex või mõnda muusse kodeerimisagenti. See sisaldab teie MCP-aadressi ja juhendab agenti seadistamisel. Juurdepääsu kinnitate sisselogimisel.',
    copyAction: 'Kopeeri',
    copiedAction: 'Kopeeritud',
    copyNamed: 'Kopeeri {what}',
    copiedNamed: 'Kopeeritud: {what}',
    copiedStatus: 'Kopeeritud: {what}.',
    copyManualHint:
      'Selles brauseris ei saa kopeerida. Tekst on valitud; kopeerige see klaviatuuriga.',
    copyPromptShow: 'Kuva viip',
    copyPromptHide: 'Peida viip',
    copyWhatAddress: 'Poneva MCP-aadress',
    copyWhatConfig: 'MCP-serveri konfiguratsioon',
    copyWhatSample: 'näidisküsimus',
    copyWhatSampleFor: 'näidisküsimus agendile {app}',
    copyWhatPrompt: 'seadistusviip',
    copyWhatKey: 'agendi {app} juurdepääsuvõti',
    keyCreateAction: 'Loo juurdepääsuvõti',
    keySecret:
      'Hoidke seda nagu parooli. Kleepige see ainult agendi MCP-, konnektori- või saladuste seadetesse, mitte kunagi veebisaidile ega koodi, mida teised saavad avada.',
    keyFormNote:
      'Agendile, kes ei saa sisse logida. Võti loeb teie nimel ettevõtte {company} teadmisi, kuni see aegub või te selle tühistate; see lõpeb ka siis, kui muudate parooli või lahkute ettevõttest.',
    keyName: 'Nimi',
    keyNamePlaceholder: 'nt Öine aruandetöö',
    keyLifetime: 'Kehtivusaeg',
    keyDays: 'one:päev|other:päeva',
    keyAlsoAllow: 'Luba ka',
    keyCreating: 'Loomine…',
    keyCreate: 'Loo võti',
    keyCancel: 'Loobu',
    keyUnconfirmed:
      'Me ei saanud kinnitada, et võti loodi. Kui see on loendis, tühistage see ja looge uus.',
    keyNotCreated: 'Juurdepääsuvõtit ei loodud. Proovige uuesti.',
    keyNewTitle: 'Juurdepääsuvõti: {app}',
    keyCopyNow:
      'Kopeerige see kohe. Poneva näitab seda ainult ühe korra ja salvestab sellest ainult sõrmejälje.',
    keyTokenRule: 'Väljale {token} või {apiKey} sisestage ainult võti.',
    keyTermToken: 'token',
    keyTermApiKey: 'API-võti',
    keyHeaderRule:
      'Väljale {header} sisestage nimi {name} ja väärtus {value}, kus {key} asemel on teie võti.',
    keyTermHeader: 'päis',
    keyEndsOn:
      'See lakkab töötamast {date}, kui te selle tühistate, ning kui muudate parooli või lahkute ettevõttest.',
    keyEndsExpiry:
      'See lakkab töötamast, kui see aegub, kui te selle tühistate, ning kui muudate parooli või lahkute ettevõttest.',
    keyDone: 'Tehtud',
    scopeProposeTitle: 'Teadmiste muudatuste pakkumine',
    scopeProposeDescribe:
      '{app} saab pakkuda lehtede muudatusi. Ettevõtte avaldaja vaatab iga ettepaneku üle, enne kui midagi muutub.',
    scopeProposeConfirmOn:
      'Kas lubada agendil {app} pakkuda ettevõtte {company} teadmiste muudatusi? Iga ettepanek ootab jaotises Teadmised → Ülevaatamiseks, kuni ettevõtte avaldaja selle kinnitab. Midagi ei muutu iseenesest.',
    scopeProposeConfirmOff:
      'Kas keelata agendil {app} teadmiste muudatuste pakkumine? Juba saadetud ettepanekud jäävad ülevaatusele.',
    scopeProposeEnabled:
      '{app} saab nüüd pakkuda teadmiste muudatusi. Kui valik pole agendis {app} veel nähtav, taaskäivitage see.',
    scopeProposeDisabled: '{app} ei saa enam teadmiste muudatusi pakkuda.',
    agentReadAccess:
      'Ettevõtte teadmiste lugemine: ettevõtte profiil ja kinnitatud teadmuslehed. Vestlused, failid, koosolekud ja isiklik mälu ei ole kaasatud.',
    agentHintKeyStale:
      'Poneva aadress muutus pärast selle võtme loomist, seega Poneva keeldub sellest. Tühistage see ja looge uus võti.',
    agentHintStale:
      'Poneva aadress muutus pärast agendi {app} ühendamist, seega Poneva keeldub sellest. Katkestage ühendus ja ühendage seejärel {app} uuesti ülaltoodud aadressiga.',
    agentHintKeyWaiting: 'See kuvatakse olekuga „Valmis“, kui mõni agent võtit kasutab.',
    agentHintWaiting: 'Esitage agendile {app} küsimus, et ühenduse kontroll lõpule viia.',
    agentHintTryAsking: 'Proovige küsida:',
    agentFactCreated: 'Loodud',
    agentFactConnected: 'Ühendatud',
    agentFactExpires: 'Aegub',
    agentFactSignInEnds: 'Sisselogimine lõpeb',
    agentFactAccess: 'Juurdepääs',
    agentNameNote:
      'Selle nime teatas agent ise; Poneva ei saa seda kontrollida. Katkestage ühendus iga agendiga, keda te ei tunne.',
    agentSignInNote: 'Kui sisselogimine lõpeb, palub agent teil uuesti sisse logida.',
    agentWorking: 'Töötlemine…',
    agentRevokeKey: 'Tühista võti',
    agentDisconnect: 'Katkesta ühendus',
    cardAgentsLabel: 'Teie ühendatud agendid',
    cardMore: 'ja {more}',
    cardMoreCount: 'one:veel jaotises Ühendused|other:veel jaotises Ühendused',
    cardOpenSetup: 'Ava seadistus',
    cardChecking: 'Teie väliste agentide kontrollimine…',
    cardTryAgain: 'Proovi uuesti',
    connectedAgentsSubtitleAny: 'Agendid, kes loevad Poneva kaudu teie ettevõtte teadmisi.',
    scopeLibraryReadTitle: 'Teie töö lugemine Ponevas',
    scopeLibraryReadDescribe:
      '{app} saab leida ja lugeda vestlusi, Raamatukogu üksusi, faile, koosolekuid, Skille, agente ja rakendusi, mida saate Ponevas avada, ning lugeda teie ühendatud teenustest. See saab lugeda ka ettevõtte ostuabilisi ja seda, mida ostjad neile on öelnud.',
    scopeLibraryReadConfirmOn:
      'Kas lubada agendil {app} lugeda teie tööd Ponevas ettevõttes {company}? See leiab ja loeb teie nimel seda, mida saate Ponevas juba avada.',
    scopeLibraryReadConfirmOff:
      'Kas keelata agendil {app} teie töö lugemine Ponevas? Siis ei saa see ka enam tööd salvestada ega Ponevalt küsida.',
    scopeLibraryReadEnabled:
      '{app} saab nüüd lugeda teie tööd Ponevas. Kui valik pole agendis {app} veel nähtav, taaskäivitage see.',
    scopeLibraryReadDisabled: '{app} ei saa enam teie tööd Ponevas lugeda.',
    scopeLibraryWriteTitle: 'Töö salvestamine ja Ponevalt küsimine',
    scopeLibraryWriteDescribe:
      '{app} saab salvestada tööd teie Raamatukokku, kus uus töö jääb privaatseks ja näete seda ainult teie, ning salvestada uusi versioone tööst, mida saate muuta (varasemad versioonid jäävad alles jaotises Ajalugu). See saab salvestada Skillide ja ettevõtte ostuabiliste kujunduste mustandeid, mida näevad kolleegid, kes saavad neid muuta; Ponevas avaldab need ainult inimene. See saab ka küsida Ponevalt privaatsetes vestlustes, mis kulutab krediite. See saab ka käivitada ettevõtte veebipoe lugemisi ostuabilise parandamiseks ja esitada sellele testküsimusi; mõlemad kasutavad ettevõtte krediite.',
    scopeLibraryWriteConfirmOn:
      'Kas lubada agendil {app} salvestada tööd teie Raamatukokku ettevõttes {company}, sealhulgas uusi versioone tööst, mida saate muuta, ja mustandeid, mida kolleegid näha saavad, ning küsida teie eest Ponevalt? Küsimine kulutab krediite. See saab ka teie tööd Ponevas lugeda. Poe lugemised ja ostuabilistele esitatud testküsimused kasutavad samuti ettevõtte krediite.',
    scopeLibraryWriteConfirmOff:
      'Kas keelata agendil {app} töö salvestamine ja Ponevalt küsimine? See, mis on juba salvestatud, jääb teie Raamatukokku.',
    scopeLibraryWriteEnabled:
      '{app} saab nüüd tööd salvestada ja Ponevalt küsida. Kui valik pole agendis {app} veel nähtav, taaskäivitage see.',
    scopeLibraryWriteDisabled: '{app} ei saa enam tööd salvestada ega Ponevalt küsida.',
  },
  fi: {
    mcpServersTitle: 'Omat yhteydet',
    mcpServersSubtitle: 'Yhdistä oma työkalupalvelin. Poneva kysyy ennen kuin muuttaa mitään.',
    mcpServersApproval:
      'Hyväksyt jokaisen muutoksen. Lukemisessa noudatetaan kullekin palvelimelle valitsemaasi asetusta.',
    mcpServersAdd: 'Lisää yhteys',
    connectedAgentsLink: 'Yhdistetyt agentit',
    accountConnectionsUnavailable: 'Tiliyhteydet eivät ole vielä käytettävissä tässä työtilassa.',
    connectedAgentsTitle: 'Yhdistetyt agentit',
    connectedAgentsSubtitle: 'Agentit, jotka lukevat yrityksen {company} tietoja Ponevan kautta.',
    connectedAgentsSuggest: 'Jos sallit sen, ne ehdottavat muutoksia, jotka ihminen tarkistaa.',
    connectedAgentsList: 'Yhdistetty nyt',
    connectingAgentsOff: 'Agenttien yhdistäminen ei ole vielä käytettävissä tässä työtilassa.',
    checkingConnectedAgents: 'Tarkistetaan yhdistettyjä agentteja…',
    connectedAgentsLoadFailed: 'Yhdistettyjä agentteja ei voitu ladata.',
    connectedAgentsOpen: 'Avaa Yhdistetyt agentit',
    agentTimeNotYet: 'ei vielä',
    agentTimeUnknown: 'tuntemattomana ajankohtana',
    agentTimeJustNow: 'juuri nyt',
    agentTimeOnDate: '{date}',
    agentErrorNotFound: 'Tämä agentti ei ole enää yhdistetty. Luettelo näyttää nykyisen tilan.',
    agentErrorScopeUnavailable: 'Tämä käyttöoikeus ei ole käytettävissä tässä työtilassa.',
    agentErrorTooManyKeys:
      'Sinulla on niin monta käyttöavainta kuin Poneva sallii. Peru ensin yksi.',
    agentErrorInvalidInput: 'Tarkista avaimen nimi ja yritä uudelleen.',
    agentNotRecorded: 'Ei tallennettu',
    agentMethodKey: 'Käyttöavain',
    agentMethodSignedIn: 'Kirjautunut sisään',
    agentStateReconnect: 'Yhdistettävä uudelleen',
    agentStateReady: 'Valmis',
    agentStateKeyUnused: 'Luotu – ei vielä käytetty',
    agentStateWaiting: 'Valtuutettu – odottaa ensimmäistä lukua',
    agentLastUsed: 'Viimeksi käytetty {when}',
    agentKeyExpires: 'Käyttöavain · vanhenee {date}',
    agentSignInEnds: 'Kirjautunut sisään · kirjautuminen päättyy {date}',
    agentsNone: 'Ulkoisia agentteja ei ole vielä yhdistetty',
    agentsConnectedCount: 'one:agentti yhdistetty|other:agenttia yhdistetty',
    agentsWaitingCount: 'one:odottaa|other:odottaa',
    agentsReconnectCount: 'one:yhdistettävä uudelleen|other:yhdistettävä uudelleen',
    addressLocalNetwork:
      'Tavoitettavissa vain tässä verkossa. Pilvessä toimivat agentit eivät voi käyttää tätä osoitetta.',
    addressLocalComputer:
      'Tavoitettavissa vain tältä tietokoneelta. Pilvessä toimivat agentit eivät voi käyttää tätä osoitetta.',
    addressUnreachable:
      'Poneva ei tavoita tätä osoitetta juuri nyt, joten ulkoiset agentit eivät todennäköisesti tavoita sitäkään.',
    agentUnnamed: 'Nimetön agentti',
    agentCompanyFallback: 'yrityksesi',
    agentErrorUnreadable:
      'Poneva palautti vastauksen, jota tämä sivu ei pystynyt lukemaan. Yritä uudelleen.',
    agentsLoadFailed: 'Ulkoisia agenttejasi ei voitu ladata.',
    agentsLoading: 'Ladataan ulkoisia agentteja…',
    agentsConnectedShort: 'one:yhdistetty|other:yhdistetty',
    agentsAddressTitle: 'MCP-osoite',
    agentsPrivacy:
      'Agentit toimivat puolestasi ja näkevät vain yrityksen {company} tiedot. Ne lukevat hyväksyttyjä tietoja, eivät koskaan yksityisiä keskusteluja, henkilökohtaista muistia tai yksityisiä tiedostoja. Kaikki muu pysyy pois päältä, ellet salli sitä.',
    agentsSetupHide: 'Piilota käyttöönotto',
    agentsSetupAnother: 'Yhdistä toinen agentti',
    agentsHelpInChat: 'Pyydä apua keskustelussa',
    agentsHelpStarter: 'Auta minua yhdistämään ulkoinen agentti Ponevaan MCP:n kautta.',
    agentNoticeReady: '{app} luki Ponevan tietoja. Yhteys on valmis.',
    agentNoticeAuthorized:
      '{app} on valtuutettu. Esitä sille kysymys, niin yhteyden tarkistus viimeistellään.',
    agentScopeUnconfirmed:
      'Emme voineet vahvistaa agentin {app} muutosta. Alla olevat tiedot näyttävät Ponevan nykyisen tilanteen.',
    agentScopeNotChanged: 'Agentin {app} käyttöoikeus ei muuttunut. Yritä uudelleen.',
    agentConfirmRevokeKey:
      'Perutaanko käyttöavain ”{app}”? Kaikki sitä käyttävät agentit lakkaavat heti lukemasta yrityksen {company} tietoja.',
    agentConfirmDisconnect:
      'Katkaistaanko yhteys agenttiin {app}? {app} ei voi enää lukea yrityksen {company} tietoja. Tämä ei vaikuta Poneva-kirjautumiseesi.',
    agentKeyRevoked: 'Käyttöavain ”{app}” on peruttu.',
    agentDisconnected: 'Yhteys agenttiin {app} on katkaistu.',
    agentDisconnectUnconfirmed:
      'Emme voineet vahvistaa, että yhteys agenttiin {app} katkaistiin. Luettelo näyttää nykyisen tilan.',
    agentDisconnectFailed: '{app} on yhä yhdistetty. Yritä uudelleen.',
    setupTitle: 'Yhdistä ulkoinen agentti',
    setupManualLabel: 'Tai määritä se itse',
    setupAddTitle: 'Lisää Poneva MCP-palvelimeksi',
    setupAddBody:
      'Lisää agentin MCP- tai liitinasetuksissa etä-MCP-palvelin (Streamable HTTP) yllä olevalla osoitteella.',
    setupConfigToggle: 'Käytätkö määritystiedostoa?',
    setupConfigBody:
      'Agentit, jotka luettelevat MCP-palvelimensa JSON-tiedostossa, tarvitsevat tämän:',
    setupSignInTitle: 'Kirjaudu sisään',
    setupSignInBody:
      'Agentti avaa Ponevan selaimessasi. Tarkista yritys ja se, mitä agentti saa tehdä, ja valitse sitten ”Salli”.',
    setupSignInFallback:
      'Jos kirjautumisikkuna sulkeutuu itsestään tai ei koskaan näytä Ponevaa tai agentti pyytää tokenia tai API-avainta, käytä sen sijaan käyttöavainta.',
    setupTryTitle: 'Kokeile',
    setupTryAsk: 'Kysy agentilta:',
    setupSampleQuestion: 'Mikä on tuotteemme nykyinen suunta? Mainitse lähde.',
    setupTryNote: 'Se näkyy täällä tilassa ”Valmis” ensimmäisen lukukerran jälkeen.',
    setupPromptTitle: 'Anna koodausagenttisi määrittää se',
    setupPromptBody:
      'Liitä tämä kehote Claude Codeen, Codexiin tai muuhun koodausagenttiin. Se sisältää MCP-osoitteesi ja opastaa agentin käyttöönoton läpi. Hyväksyt käyttöoikeuden kirjautuessasi.',
    copyAction: 'Kopioi',
    copiedAction: 'Kopioitu',
    copyNamed: 'Kopioi {what}',
    copiedNamed: 'Kopioitu: {what}',
    copiedStatus: 'Kopioitu: {what}.',
    copyManualHint:
      'Kopiointi ei ole käytettävissä tässä selaimessa. Teksti on valittuna; kopioi se näppäimistöllä.',
    copyPromptShow: 'Näytä kehote',
    copyPromptHide: 'Piilota kehote',
    copyWhatAddress: 'Ponevan MCP-osoite',
    copyWhatConfig: 'MCP-palvelimen määritys',
    copyWhatSample: 'esimerkkikysymys',
    copyWhatSampleFor: 'esimerkkikysymys agentille {app}',
    copyWhatPrompt: 'käyttöönottokehote',
    copyWhatKey: 'agentin {app} käyttöavain',
    keyCreateAction: 'Luo käyttöavain',
    keySecret:
      'Käsittele sitä kuin salasanaa. Liitä se vain agentin MCP-, liitin- tai salaisuusasetuksiin, ei koskaan verkkosivustolle tai koodiin, jonka muut voivat avata.',
    keyFormNote:
      'Agentille, joka ei voi kirjautua sisään. Avain lukee yrityksen {company} tietoja puolestasi, kunnes se vanhenee tai perut sen; myös salasanan vaihtaminen tai yrityksestä lähteminen päättää sen.',
    keyName: 'Nimi',
    keyNamePlaceholder: 'esim. Yöllinen raporttiajo',
    keyLifetime: 'Voimassa',
    keyDays: 'one:päivä|other:päivää',
    keyAlsoAllow: 'Salli myös',
    keyCreating: 'Luodaan…',
    keyCreate: 'Luo avain',
    keyCancel: 'Peruuta',
    keyUnconfirmed:
      'Emme voineet vahvistaa avaimen luomista. Jos se näkyy luettelossa, peru se ja luo uusi.',
    keyNotCreated: 'Käyttöavainta ei luotu. Yritä uudelleen.',
    keyNewTitle: 'Käyttöavain: {app}',
    keyCopyNow:
      'Kopioi se nyt. Poneva näyttää sen vain kerran ja tallentaa siitä vain sormenjäljen.',
    keyTokenRule: 'Kenttään {token} tai {apiKey} tulee pelkkä avain.',
    keyTermToken: 'token',
    keyTermApiKey: 'API-avain',
    keyHeaderRule:
      'Kenttään {header} tulee nimi {name} ja arvo {value}, jossa {key} korvataan avaimellasi.',
    keyTermHeader: 'otsake',
    keyEndsOn:
      'Se lakkaa toimimasta {date}, kun perut sen ja kun vaihdat salasanasi tai lähdet yrityksestä.',
    keyEndsExpiry:
      'Se lakkaa toimimasta, kun se vanhenee, kun perut sen ja kun vaihdat salasanasi tai lähdet yrityksestä.',
    keyDone: 'Sulje',
    scopeProposeTitle: 'Ehdota tietomuutoksia',
    scopeProposeDescribe:
      '{app} voi ehdottaa sivumuutoksia. Yrityksen julkaisija tarkistaa jokaisen ennen kuin mitään muuttuu.',
    scopeProposeConfirmOn:
      'Sallitaanko agentin {app} ehdottaa muutoksia yrityksen {company} tietoihin? Jokainen ehdotus odottaa kohdassa Tietämys → Tarkistettavana, kunnes yrityksen julkaisija hyväksyy sen. Mikään ei muutu itsestään.',
    scopeProposeConfirmOff:
      'Estetäänkö agenttia {app} ehdottamasta tietomuutoksia? Jo lähetetyt ehdotukset jäävät tarkistettaviksi.',
    scopeProposeEnabled:
      '{app} voi nyt ehdottaa tietomuutoksia. Jos vaihtoehto ei vielä näy agentissa {app}, käynnistä se uudelleen.',
    scopeProposeDisabled: '{app} ei voi enää ehdottaa tietomuutoksia.',
    agentReadAccess:
      'Yrityksen tietojen lukeminen: yrityksen profiili ja hyväksytyt tietosivut. Keskustelut, tiedostot, kokoukset ja henkilökohtainen muisti eivät sisälly.',
    agentHintKeyStale:
      'Ponevan osoite muuttui tämän avaimen luomisen jälkeen, joten Poneva hylkää sen. Peru se ja luo uusi avain.',
    agentHintStale:
      'Ponevan osoite muuttui sen jälkeen, kun {app} yhdistettiin, joten Poneva hylkää sen. Katkaise yhteys ja yhdistä sitten {app} uudelleen yllä olevalla osoitteella.',
    agentHintKeyWaiting: 'Se näkyy tilassa ”Valmis”, kun jokin agentti käyttää avainta.',
    agentHintWaiting: 'Esitä agentille {app} kysymys, niin yhteyden tarkistus viimeistellään.',
    agentHintTryAsking: 'Kokeile kysyä:',
    agentFactCreated: 'Luotu',
    agentFactConnected: 'Yhdistetty',
    agentFactExpires: 'Vanhenee',
    agentFactSignInEnds: 'Kirjautuminen päättyy',
    agentFactAccess: 'Käyttöoikeus',
    agentNameNote:
      'Nimi on agentin itse ilmoittama; Poneva ei voi vahvistaa sitä. Katkaise yhteys kaikkiin agentteihin, joita et tunnista.',
    agentSignInNote: 'Kun kirjautuminen päättyy, agentti pyytää sinua kirjautumaan uudelleen.',
    agentWorking: 'Käsitellään…',
    agentRevokeKey: 'Peru avain',
    agentDisconnect: 'Katkaise yhteys',
    cardAgentsLabel: 'Yhdistetyt agenttisi',
    cardMore: 'ja {more}',
    cardMoreCount: 'one:muu Yhteydet-osiossa|other:muuta Yhteydet-osiossa',
    cardOpenSetup: 'Avaa käyttöönotto',
    cardChecking: 'Tarkistetaan ulkoisia agenttejasi…',
    cardTryAgain: 'Yritä uudelleen',
    connectedAgentsSubtitleAny: 'Agentit, jotka lukevat yrityksesi tietoja Ponevan kautta.',
    scopeLibraryReadTitle: 'Lue työtäsi Ponevassa',
    scopeLibraryReadDescribe:
      '{app} voi löytää ja lukea chatit, Kirjaston kohteet, tiedostot, kokoukset, Skillit, agentit ja sovellukset, jotka voit avata Ponevassa, sekä lukea yhdistetyistä palveluistasi. Se voi myös lukea yrityksen ostoavustajia ja sen, mitä ostajat ovat niille sanoneet.',
    scopeLibraryReadConfirmOn:
      'Sallitaanko agentin {app} lukea työtäsi Ponevassa yrityksessä {company}? Se löytää ja lukee sinun tunnuksillasi sen, minkä voit jo avata Ponevassa.',
    scopeLibraryReadConfirmOff:
      'Estetäänkö agenttia {app} lukemasta työtäsi Ponevassa? Silloin se ei voi myöskään enää tallentaa työtä tai kysyä Ponevalta.',
    scopeLibraryReadEnabled:
      '{app} voi nyt lukea työtäsi Ponevassa. Jos vaihtoehto ei vielä näy agentissa {app}, käynnistä se uudelleen.',
    scopeLibraryReadDisabled: '{app} ei voi enää lukea työtäsi Ponevassa.',
    scopeLibraryWriteTitle: 'Tallenna työtä ja kysy Ponevalta',
    scopeLibraryWriteDescribe:
      '{app} voi tallentaa työtä Kirjastoosi, jossa uusi työ näkyy vain sinulle, ja tallentaa uusia versioita työstä, jota voit muokata (aiemmat versiot säilyvät kohdassa Historia). Se voi tallentaa Skillien ja yrityksen ostoavustajien ulkoasujen luonnoksia, jotka näkyvät niitä muokata voiville kollegoille; vain ihminen julkaisee ne Ponevassa. Se voi myös kysyä Ponevalta yksityisissä chateissa, mikä kuluttaa krediittejä. Se voi myös käynnistää yrityksen verkkokaupan lukuja ostoavustajan korjaamiseksi ja esittää sille testikysymyksiä; molemmat käyttävät yrityksen krediittejä.',
    scopeLibraryWriteConfirmOn:
      'Sallitaanko agentin {app} tallentaa työtä Kirjastoosi yrityksessä {company}, myös uusia versioita työstä, jota voit muokata, ja luonnoksia, jotka kollegat voivat nähdä, sekä kysyä Ponevalta puolestasi? Kysyminen kuluttaa krediittejä. Se voi myös lukea työtäsi Ponevassa. Kaupan luvut ja ostoavustajille esitetyt testikysymykset käyttävät myös yrityksen krediittejä.',
    scopeLibraryWriteConfirmOff:
      'Estetäänkö agenttia {app} tallentamasta työtä ja kysymästä Ponevalta? Se, minkä se on jo tallentanut, jää Kirjastoosi.',
    scopeLibraryWriteEnabled:
      '{app} voi nyt tallentaa työtä ja kysyä Ponevalta. Jos vaihtoehto ei vielä näy agentissa {app}, käynnistä se uudelleen.',
    scopeLibraryWriteDisabled: '{app} ei voi enää tallentaa työtä tai kysyä Ponevalta.',
  },
  fr: {
    mcpServersTitle: 'Connexions personnalisées',
    mcpServersSubtitle:
      'Connectez votre propre serveur d’outils. Poneva demande avant de modifier quoi que ce soit.',
    mcpServersApproval:
      'Vous approuvez chaque modification. Pour la lecture, c’est le choix fait pour chaque serveur qui s’applique.',
    mcpServersAdd: 'Ajouter une connexion',
    connectedAgentsLink: 'Agents connectés',
    accountConnectionsUnavailable:
      'Les connexions de compte ne sont pas encore disponibles dans cet espace de travail.',
    connectedAgentsTitle: 'Agents connectés',
    connectedAgentsSubtitle: 'Agents qui lisent les connaissances de {company} via Poneva.',
    connectedAgentsSuggest:
      'Si vous l’autorisez, ils proposent des mises à jour qu’une personne vérifie.',
    connectedAgentsList: 'Connectés maintenant',
    connectingAgentsOff:
      'La connexion d’agents n’est pas encore disponible pour cet espace de travail.',
    checkingConnectedAgents: 'Vérification des agents connectés…',
    connectedAgentsLoadFailed: 'Impossible de charger les agents connectés.',
    connectedAgentsOpen: 'Ouvrir Agents connectés',
    agentTimeNotYet: 'pas encore',
    agentTimeUnknown: 'à un moment inconnu',
    agentTimeJustNow: 'à l’instant',
    agentTimeOnDate: 'le {date}',
    agentErrorNotFound: 'Cet agent n’est plus connecté. La liste affiche l’état actuel.',
    agentErrorScopeUnavailable: 'Cet accès n’est pas disponible dans cet espace de travail.',
    agentErrorTooManyKeys:
      'Vous avez atteint le nombre maximal de clés d’accès autorisé par Poneva. Révoquez-en une d’abord.',
    agentErrorInvalidInput: 'Vérifiez le nom de la clé et réessayez.',
    agentNotRecorded: 'Non enregistré',
    agentMethodKey: 'Clé d’accès',
    agentMethodSignedIn: 'Session ouverte',
    agentStateReconnect: 'Reconnexion nécessaire',
    agentStateReady: 'Prêt',
    agentStateKeyUnused: 'Créée — pas encore utilisée',
    agentStateWaiting: 'Autorisé — en attente de la première lecture',
    agentLastUsed: 'Dernière utilisation {when}',
    agentKeyExpires: 'Clé d’accès · expire le {date}',
    agentSignInEnds: 'Session ouverte · la session expire le {date}',
    agentsNone: 'Aucun agent externe connecté pour l’instant',
    agentsConnectedCount: 'one:agent connecté|other:agents connectés',
    agentsWaitingCount: 'one:en attente|other:en attente',
    agentsReconnectCount: 'one:à reconnecter|other:à reconnecter',
    addressLocalNetwork:
      'Accessible uniquement sur ce réseau. Les agents qui s’exécutent dans le cloud ne peuvent pas utiliser cette adresse.',
    addressLocalComputer:
      'Accessible uniquement depuis cet ordinateur. Les agents qui s’exécutent dans le cloud ne peuvent pas utiliser cette adresse.',
    addressUnreachable:
      'Poneva ne peut pas joindre cette adresse pour le moment, et les agents externes ne le peuvent probablement pas non plus.',
    agentUnnamed: 'Agent sans nom',
    agentCompanyFallback: 'votre entreprise',
    agentErrorUnreadable:
      'Poneva a renvoyé une réponse que cette page ne peut pas lire. Réessayez.',
    agentsLoadFailed: 'Impossible de charger vos agents externes.',
    agentsLoading: 'Chargement des agents externes…',
    agentsConnectedShort: 'one:connecté|many:connectés|other:connectés',
    agentsAddressTitle: 'Adresse MCP',
    agentsPrivacy:
      'Les agents agissent en votre nom et ne voient jamais que {company}. Ils lisent les connaissances approuvées, jamais les discussions privées, la mémoire personnelle ni les fichiers privés. Tout le reste reste désactivé, sauf si vous l’autorisez.',
    agentsSetupHide: 'Masquer la configuration',
    agentsSetupAnother: 'Connecter un autre agent',
    agentsHelpInChat: 'Obtenir de l’aide dans le chat',
    agentsHelpStarter: 'Aide-moi à connecter un agent externe à Poneva via MCP.',
    agentNoticeReady: '{app} a lu des connaissances Poneva. La connexion est prête.',
    agentNoticeAuthorized:
      '{app} est autorisé. Posez-lui une question pour terminer la vérification de la connexion.',
    agentScopeUnconfirmed:
      'Nous n’avons pas pu confirmer la modification pour {app}. Les détails ci-dessous indiquent ce que Poneva enregistre actuellement.',
    agentScopeNotChanged: 'L’accès de {app} n’a pas changé. Réessayez.',
    agentConfirmRevokeKey:
      'Révoquer la clé d’accès « {app} » ? Tout agent qui l’utilise cesse immédiatement de lire les connaissances de {company}.',
    agentConfirmDisconnect:
      'Déconnecter {app} ? {app} ne pourra plus lire les connaissances de {company}. Votre session Poneva n’est pas concernée.',
    agentKeyRevoked: 'La clé d’accès « {app} » est révoquée.',
    agentDisconnected: '{app} est déconnecté.',
    agentDisconnectUnconfirmed:
      'Nous n’avons pas pu confirmer la déconnexion de {app}. La liste affiche l’état actuel.',
    agentDisconnectFailed: '{app} est toujours connecté. Réessayez.',
    setupTitle: 'Connecter un agent externe',
    setupManualLabel: 'Ou configurez-le vous-même',
    setupAddTitle: 'Ajouter Poneva comme serveur MCP',
    setupAddBody:
      'Dans les paramètres MCP ou de connecteurs de l’agent, ajoutez un serveur MCP distant (Streamable HTTP) avec l’adresse ci-dessus.',
    setupConfigToggle: 'Vous utilisez un fichier de configuration ?',
    setupConfigBody: 'Pour les agents qui listent leurs serveurs MCP dans un fichier JSON :',
    setupSignInTitle: 'Se connecter',
    setupSignInBody:
      'L’agent ouvre Poneva dans votre navigateur. Vérifiez l’entreprise et ce que l’agent pourra faire, puis choisissez Autoriser.',
    setupSignInFallback:
      'Si la fenêtre de connexion se ferme d’elle-même ou n’affiche jamais Poneva, ou si l’agent demande un jeton ou une clé API, utilisez plutôt une clé d’accès.',
    setupTryTitle: 'Essayer',
    setupTryAsk: 'Demandez à l’agent :',
    setupSampleQuestion: 'Quelle est notre orientation produit actuelle ? Cite la source.',
    setupTryNote: 'Il apparaît ici comme Prêt après sa première lecture.',
    setupPromptTitle: 'Laisser votre agent de code s’en charger',
    setupPromptBody:
      'Collez ce prompt dans Claude Code, Codex ou un autre agent de code. Il contient votre adresse MCP et guide l’agent pendant la configuration. Vous approuvez l’accès lors de la connexion.',
    copyAction: 'Copier',
    copiedAction: 'Copié',
    copyNamed: 'Copier {what}',
    copiedNamed: 'Copié : {what}',
    copiedStatus: 'Copié : {what}.',
    copyManualHint:
      'La copie n’est pas disponible dans ce navigateur. Le texte est sélectionné ; copiez-le avec votre clavier.',
    copyPromptShow: 'Afficher le prompt',
    copyPromptHide: 'Masquer le prompt',
    copyWhatAddress: 'l’adresse MCP de Poneva',
    copyWhatConfig: 'la configuration du serveur MCP',
    copyWhatSample: 'la question d’exemple',
    copyWhatSampleFor: 'la question d’exemple pour {app}',
    copyWhatPrompt: 'le prompt de configuration',
    copyWhatKey: 'la clé d’accès pour {app}',
    keyCreateAction: 'Créer une clé d’accès',
    keySecret:
      'Traitez-la comme un mot de passe. Collez-la uniquement dans les paramètres MCP, de connecteur ou de secrets d’un agent, jamais sur un site web ni dans du code que d’autres peuvent ouvrir.',
    keyFormNote:
      'Pour un agent qui ne peut pas se connecter. Elle lit les connaissances de {company} en votre nom jusqu’à son expiration ou sa révocation ; changer de mot de passe ou quitter l’entreprise y met aussi fin.',
    keyName: 'Nom',
    keyNamePlaceholder: 'p. ex. Rapport nocturne',
    keyLifetime: 'Expire après',
    keyDays: 'one:jour|many:jours|other:jours',
    keyAlsoAllow: 'Autoriser aussi',
    keyCreating: 'Création…',
    keyCreate: 'Créer la clé',
    keyCancel: 'Annuler',
    keyUnconfirmed:
      'Nous n’avons pas pu confirmer la création de la clé. Si elle apparaît dans la liste, révoquez-la et créez-en une nouvelle.',
    keyNotCreated: 'La clé d’accès n’a pas été créée. Réessayez.',
    keyNewTitle: 'Clé d’accès pour {app}',
    keyCopyNow:
      'Copiez-la maintenant. Poneva ne l’affiche qu’une seule fois et n’en conserve qu’une empreinte.',
    keyTokenRule: 'Un champ {token} ou {apiKey} reçoit uniquement la clé.',
    keyTermToken: 'jeton',
    keyTermApiKey: 'clé API',
    keyHeaderRule:
      'Un champ {header} reçoit le nom {name} et la valeur {value}, votre clé remplaçant {key}.',
    keyTermHeader: 'en-tête',
    keyEndsOn:
      'Elle cesse de fonctionner le {date}, lorsque vous la révoquez et lorsque vous changez de mot de passe ou quittez l’entreprise.',
    keyEndsExpiry:
      'Elle cesse de fonctionner à son expiration, lorsque vous la révoquez et lorsque vous changez de mot de passe ou quittez l’entreprise.',
    keyDone: 'Terminé',
    scopeProposeTitle: 'Proposer des mises à jour des connaissances',
    scopeProposeDescribe:
      '{app} peut proposer des modifications de pages. Un éditeur de l’entreprise examine chacune avant toute modification.',
    scopeProposeConfirmOn:
      'Autoriser {app} à proposer des mises à jour des connaissances de {company} ? Chaque proposition attend dans Connaissances → À examiner jusqu’à ce qu’un éditeur de l’entreprise l’approuve. Rien ne change automatiquement.',
    scopeProposeConfirmOff:
      'Empêcher {app} de proposer des mises à jour des connaissances ? Les propositions déjà envoyées restent à examiner.',
    scopeProposeEnabled:
      '{app} peut désormais proposer des mises à jour des connaissances. Si l’option n’apparaît pas encore dans {app}, redémarrez-le.',
    scopeProposeDisabled: '{app} ne peut plus proposer de mises à jour des connaissances.',
    agentReadAccess:
      'Lire les connaissances de l’entreprise : le profil de l’entreprise et les pages de connaissances approuvées. Les discussions, les fichiers, les réunions et la mémoire personnelle ne sont pas inclus.',
    agentHintKeyStale:
      'L’adresse de Poneva a changé après la création de cette clé ; Poneva la refuse donc. Révoquez-la et créez une nouvelle clé.',
    agentHintStale:
      'L’adresse de Poneva a changé après la connexion de {app} ; Poneva le refuse donc. Déconnectez-le, puis reconnectez {app} avec l’adresse ci-dessus.',
    agentHintKeyWaiting: 'Il apparaît comme Prêt dès qu’un agent utilise la clé.',
    agentHintWaiting: 'Posez une question à {app} pour terminer la vérification de la connexion.',
    agentHintTryAsking: 'Essayez de demander :',
    agentFactCreated: 'Créé',
    agentFactConnected: 'Connecté',
    agentFactExpires: 'Expire',
    agentFactSignInEnds: 'Fin de session',
    agentFactAccess: 'Accès',
    agentNameNote:
      'Ce nom est celui que l’agent a indiqué ; Poneva ne peut pas le vérifier. Déconnectez tout agent que vous ne reconnaissez pas.',
    agentSignInNote: 'Quand sa session expire, l’agent vous demande de vous reconnecter.',
    agentWorking: 'En cours…',
    agentRevokeKey: 'Révoquer la clé',
    agentDisconnect: 'Déconnecter',
    cardAgentsLabel: 'Vos agents connectés',
    cardMore: 'et {more}',
    cardMoreCount:
      'one:autre dans Connexions|many:autres dans Connexions|other:autres dans Connexions',
    cardOpenSetup: 'Ouvrir la configuration',
    cardChecking: 'Vérification de vos agents externes…',
    cardTryAgain: 'Réessayer',
    connectedAgentsSubtitleAny:
      'Agents qui lisent les connaissances de votre entreprise via Poneva.',
    scopeLibraryReadTitle: 'Lire votre travail dans Poneva',
    scopeLibraryReadDescribe:
      '{app} peut trouver et lire les chats, la Bibliothèque, les fichiers, les réunions, les Skills, les agents et les apps que vous pouvez ouvrir dans Poneva, et lire les données de vos services connectés. Il peut aussi lire les assistants shopping de l’entreprise et ce que les acheteurs leur ont dit.',
    scopeLibraryReadConfirmOn:
      'Autoriser {app} à lire votre travail dans Poneva pour {company} ? Il trouve et lit ce que vous pouvez déjà ouvrir dans Poneva, en votre nom.',
    scopeLibraryReadConfirmOff:
      'Empêcher {app} de lire votre travail dans Poneva ? Il ne pourra alors plus non plus enregistrer de travail ni interroger Poneva.',
    scopeLibraryReadEnabled:
      '{app} peut désormais lire votre travail dans Poneva. Si l’option n’apparaît pas encore dans {app}, redémarrez-le.',
    scopeLibraryReadDisabled: '{app} ne peut plus lire votre travail dans Poneva.',
    scopeLibraryWriteTitle: 'Enregistrer du travail et interroger Poneva',
    scopeLibraryWriteDescribe:
      '{app} peut enregistrer du travail dans votre Bibliothèque, où le nouveau travail reste visible par vous seul, et enregistrer de nouvelles versions du travail que vous pouvez modifier (les versions précédentes restent dans l’Historique). Il peut enregistrer des brouillons de Skills et de designs des assistants shopping de l’entreprise, que voient les collègues qui peuvent les modifier ; seule une personne les publie dans Poneva. Il peut aussi interroger Poneva dans des chats privés, ce qui consomme des crédits. Il peut aussi lancer des lectures de la boutique en ligne de l’entreprise pour réparer un assistant shopping et lui poser des questions de test ; les deux utilisent des crédits de l’entreprise.',
    scopeLibraryWriteConfirmOn:
      'Autoriser {app} à enregistrer du travail dans votre Bibliothèque pour {company}, y compris de nouvelles versions du travail que vous pouvez modifier et des brouillons visibles par vos collègues, et à interroger Poneva pour vous ? Les questions consomment des crédits. Il peut aussi lire votre travail dans Poneva. Les lectures de la boutique et les questions de test aux assistants shopping utilisent aussi des crédits de l’entreprise.',
    scopeLibraryWriteConfirmOff:
      'Empêcher {app} d’enregistrer du travail et d’interroger Poneva ? Ce qu’il a déjà enregistré reste dans votre Bibliothèque.',
    scopeLibraryWriteEnabled:
      '{app} peut désormais enregistrer du travail et interroger Poneva. Si l’option n’apparaît pas encore dans {app}, redémarrez-le.',
    scopeLibraryWriteDisabled: '{app} ne peut plus enregistrer de travail ni interroger Poneva.',
  },
  ga: {
    mcpServersTitle: 'Ceangail shaincheaptha',
    mcpServersSubtitle:
      'Ceangail do fhreastalaí uirlisí féin. Fiafraíonn Poneva sula n-athraíonn sé aon rud.',
    mcpServersApproval:
      'Ceadaíonn tú gach athrú. Maidir le léamh, is é an rogha a rinne tú do gach freastalaí atá i bhfeidhm.',
    mcpServersAdd: 'Cuir ceangal leis',
    connectedAgentsLink: 'Gníomhairí ceangailte',
    accountConnectionsUnavailable: 'Níl naisc chuntais ar fáil sa spás oibre seo fós.',
    connectedAgentsTitle: 'Gníomhairí ceangailte',
    connectedAgentsSubtitle: 'Gníomhairí a léann eolas {company} trí Poneva.',
    connectedAgentsSuggest:
      'Má cheadaíonn tú é, molann siad nuashonruithe do dhuine le hathbhreithniú.',
    connectedAgentsList: 'Ceangailte anois',
    connectingAgentsOff: 'Níl ceangal gníomhairí ar fáil don spás oibre seo fós.',
    checkingConnectedAgents: 'Ag seiceáil gníomhairí ceangailte…',
    connectedAgentsLoadFailed: 'Níorbh fhéidir gníomhairí ceangailte a lódáil.',
    connectedAgentsOpen: 'Oscail Gníomhairí ceangailte',
    agentTimeNotYet: 'níl fós',
    agentTimeUnknown: 'ag am anaithnid',
    agentTimeJustNow: 'díreach anois',
    agentTimeOnDate: 'ar {date}',
    agentErrorNotFound:
      'Níl an gníomhaire seo ceangailte a thuilleadh. Taispeánann an liosta an staid reatha.',
    agentErrorScopeUnavailable: 'Níl an rochtain sin ar fáil sa spás oibre seo.',
    agentErrorTooManyKeys:
      'Tá an oiread eochracha rochtana agat agus a cheadaíonn Poneva. Cúlghair ceann acu ar dtús.',
    agentErrorInvalidInput: 'Seiceáil ainm na heochrach agus bain triail eile as.',
    agentNotRecorded: 'Níor taifeadadh',
    agentMethodKey: 'Eochair rochtana',
    agentMethodSignedIn: 'Sínithe isteach',
    agentStateReconnect: 'Athcheangal ag teastáil',
    agentStateReady: 'Réidh',
    agentStateKeyUnused: 'Cruthaithe — níor úsáideadh fós',
    agentStateWaiting: 'Údaraithe — ag fanacht leis an gcéad léamh',
    agentLastUsed: 'Úsáidte go deireanach {when}',
    agentKeyExpires: 'Eochair rochtana · téann in éag ar {date}',
    agentSignInEnds: 'Sínithe isteach · críochnaíonn an síniú isteach ar {date}',
    agentsNone: 'Níl aon ghníomhairí seachtracha ceangailte fós',
    agentsConnectedCount:
      'one:gníomhaire ceangailte|two:ghníomhaire ceangailte|few:ghníomhaire ceangailte|many:ngníomhaire ceangailte|other:gníomhaire ceangailte',
    agentsWaitingCount:
      'one:ag fanacht|two:ag fanacht|few:ag fanacht|many:ag fanacht|other:ag fanacht',
    agentsReconnectCount:
      'one:le hathcheangal|two:le hathcheangal|few:le hathcheangal|many:le hathcheangal|other:le hathcheangal',
    addressLocalNetwork:
      'Ní féidir teacht air ach ar an líonra seo. Ní féidir le gníomhairí a ritheann sa néal an seoladh seo a úsáid.',
    addressLocalComputer:
      'Ní féidir teacht air ach ón ríomhaire seo. Ní féidir le gníomhairí a ritheann sa néal an seoladh seo a úsáid.',
    addressUnreachable:
      'Ní féidir le Poneva an seoladh seo a bhaint amach faoi láthair, mar sin is dócha nach féidir le gníomhairí seachtracha é a bhaint amach ach oiread.',
    agentUnnamed: 'Gníomhaire gan ainm',
    agentCompanyFallback: 'do chuideachta',
    agentErrorUnreadable:
      'Thug Poneva freagra nárbh fhéidir leis an leathanach seo a léamh. Bain triail eile as.',
    agentsLoadFailed: 'Níorbh fhéidir do ghníomhairí seachtracha a lódáil.',
    agentsLoading: 'Gníomhairí seachtracha á lódáil…',
    agentsConnectedShort:
      'one:ceangailte|two:ceangailte|few:ceangailte|many:ceangailte|other:ceangailte',
    agentsAddressTitle: 'Seoladh MCP',
    agentsPrivacy:
      'Gníomhaíonn gníomhairí ar do shon agus ní fheiceann siad ach {company}. Léann siad eolas ceadaithe, ach ní léann siad comhráite príobháideacha, cuimhne phearsanta ná comhaid phríobháideacha choíche. Fanann aon rud eile múchta mura gceadaíonn tú é.',
    agentsSetupHide: 'Folaigh an socrú',
    agentsSetupAnother: 'Ceangail gníomhaire eile',
    agentsHelpInChat: 'Faigh cabhair sa chomhrá',
    agentsHelpStarter: 'Cabhraigh liom gníomhaire seachtrach a cheangal le Poneva trí MCP.',
    agentNoticeReady: 'Léigh {app} eolas Poneva. Tá an ceangal réidh.',
    agentNoticeAuthorized:
      'Tá {app} údaraithe. Cuir ceist air chun seiceáil an cheangail a chríochnú.',
    agentScopeUnconfirmed:
      'Níorbh fhéidir linn an t-athrú do {app} a dhearbhú. Taispeánann na sonraí thíos a bhfuil ag Poneva anois.',
    agentScopeNotChanged: 'Níor athraigh rochtain {app}. Bain triail eile as.',
    agentConfirmRevokeKey:
      'An bhfuil tú ag iarraidh an eochair rochtana “{app}” a chúlghairm? Stopann aon ghníomhaire a úsáideann í de léamh eolas {company} láithreach.',
    agentConfirmDisconnect:
      'An bhfuil tú ag iarraidh {app} a dhícheangal? Ní bheidh {app} in ann eolas {company} a léamh a thuilleadh. Ní dhéantar difear do do shíniú isteach Poneva.',
    agentKeyRevoked: 'Tá an eochair rochtana “{app}” cúlghairthe.',
    agentDisconnected: 'Tá {app} dícheangailte.',
    agentDisconnectUnconfirmed:
      'Níorbh fhéidir linn a dhearbhú gur dícheanglaíodh {app}. Taispeánann an liosta an staid reatha.',
    agentDisconnectFailed: 'Tá {app} ceangailte fós. Bain triail eile as.',
    setupTitle: 'Ceangail gníomhaire seachtrach',
    setupManualLabel: 'Nó socraigh é tú féin',
    setupAddTitle: 'Cuir Poneva leis mar fhreastalaí MCP',
    setupAddBody:
      'I socruithe MCP nó nascóra an ghníomhaire, cuir freastalaí MCP cianda (Streamable HTTP) leis a bhfuil an seoladh thuas aige.',
    setupConfigToggle: 'Ag úsáid comhad cumraíochta?',
    setupConfigBody:
      'Glacann gníomhairí a liostaíonn a bhfreastalaithe MCP i gcomhad JSON leis seo:',
    setupSignInTitle: 'Sínigh isteach',
    setupSignInBody:
      'Osclaíonn an gníomhaire Poneva i do bhrabhsálaí. Seiceáil an chuideachta agus cad is féidir leis an ngníomhaire a dhéanamh, ansin roghnaigh Ceadaigh.',
    setupSignInFallback:
      'Má dhúnann an fhuinneog sínithe isteach léi féin nó mura dtaispeánann sí Poneva riamh, nó má iarrann an gníomhaire comhartha nó eochair API, úsáid eochair rochtana ina ionad sin.',
    setupTryTitle: 'Bain triail as',
    setupTryAsk: 'Fiafraigh den ghníomhaire:',
    setupSampleQuestion: 'Cén treo atá ag ár dtáirge faoi láthair? Luaigh an fhoinse.',
    setupTryNote: 'Taispeántar anseo é mar Réidh tar éis a chéad léimh.',
    setupPromptTitle: 'Lig do do ghníomhaire códaithe é a shocrú',
    setupPromptBody:
      'Greamaigh an leid seo i Claude Code, Codex nó gníomhaire códaithe eile. Tá do sheoladh MCP ann agus treoraíonn sé an gníomhaire tríd an socrú. Ceadaíonn tú an rochtain nuair a shíníonn tú isteach.',
    copyAction: 'Cóipeáil',
    copiedAction: 'Cóipeáilte',
    copyNamed: 'Cóipeáil {what}',
    copiedNamed: 'Cóipeáilte: {what}',
    copiedStatus: 'Cóipeáilte: {what}.',
    copyManualHint:
      'Níl cóipeáil ar fáil sa bhrabhsálaí seo. Tá an téacs roghnaithe; cóipeáil é le do mhéarchlár.',
    copyPromptShow: 'Féach ar an leid',
    copyPromptHide: 'Folaigh an leid',
    copyWhatAddress: 'seoladh MCP Poneva',
    copyWhatConfig: 'cumraíocht an fhreastalaí MCP',
    copyWhatSample: 'an cheist shamplach',
    copyWhatSampleFor: 'an cheist shamplach do {app}',
    copyWhatPrompt: 'leid an tsocraithe',
    copyWhatKey: 'an eochair rochtana do {app}',
    keyCreateAction: 'Cruthaigh eochair rochtana',
    keySecret:
      'Caith léi mar a chaithfeá le pasfhocal. Ná greamaigh í ach i socruithe MCP, nascóra nó rúin gníomhaire, agus ná greamaigh choíche i suíomh gréasáin ná i gcód is féidir le daoine eile a oscailt.',
    keyFormNote:
      'Do ghníomhaire nach féidir leis síniú isteach. Léann sí eolas {company} ar do shon go dtí go dtéann sí in éag nó go gcúlghairfidh tú í; cuireann athrú do phasfhocail nó imeacht ón gcuideachta deireadh léi freisin.',
    keyName: 'Ainm',
    keyNamePlaceholder: 'm.sh. Jab tuarascála oíche',
    keyLifetime: 'Téann in éag tar éis',
    keyDays: 'one:lá|two:lá|few:lá|many:lá|other:lá',
    keyAlsoAllow: 'Ceadaigh freisin',
    keyCreating: 'Á cruthú…',
    keyCreate: 'Cruthaigh eochair',
    keyCancel: 'Cealaigh',
    keyUnconfirmed:
      'Níorbh fhéidir linn a dhearbhú gur cruthaíodh an eochair. Má thaispeántar sa liosta í, cúlghair í agus cruthaigh ceann nua.',
    keyNotCreated: 'Níor cruthaíodh an eochair rochtana. Bain triail eile as.',
    keyNewTitle: 'Eochair rochtana do {app}',
    keyCopyNow:
      'Cóipeáil anois í. Ní thaispeánann Poneva í ach uair amháin agus ní stórálann sé ach méarlorg di.',
    keyTokenRule: 'Glacann réimse {token} nó {apiKey} leis an eochair amháin.',
    keyTermToken: 'comhartha',
    keyTermApiKey: 'eochair API',
    keyHeaderRule:
      'Glacann réimse {header} leis an ainm {name} agus leis an luach {value}, le d’eochair in áit {key}.',
    keyTermHeader: 'ceanntásc',
    keyEndsOn:
      'Stopann sí ag obair ar {date}, nuair a chúlghaireann tú í, agus nuair a athraíonn tú do phasfhocal nó nuair a fhágann tú an chuideachta.',
    keyEndsExpiry:
      'Stopann sí ag obair nuair a théann sí in éag, nuair a chúlghaireann tú í, agus nuair a athraíonn tú do phasfhocal nó nuair a fhágann tú an chuideachta.',
    keyDone: 'Déanta',
    scopeProposeTitle: 'Mol nuashonruithe eolais',
    scopeProposeDescribe:
      'Is féidir le {app} athruithe leathanaigh a mholadh. Déanann foilsitheoir de chuid na cuideachta athbhreithniú ar gach ceann sula n-athraítear aon rud.',
    scopeProposeConfirmOn:
      'Ar mhaith leat ligean do {app} nuashonruithe eolais a mholadh do {company}? Fanann gach moladh in Eolas → Le hathbhreithniú go dtí go gceadaíonn foilsitheoir de chuid na cuideachta é. Ní athraíonn aon rud uaidh féin.',
    scopeProposeConfirmOff:
      'An bhfuil tú ag iarraidh stop a chur le {app} nuashonruithe eolais a mholadh? Fanann na moltaí atá seolta aige cheana le hathbhreithniú.',
    scopeProposeEnabled:
      'Is féidir le {app} nuashonruithe eolais a mholadh anois. Mura bhfuil an rogha le feiceáil in {app} fós, atosaigh é.',
    scopeProposeDisabled: 'Ní féidir le {app} nuashonruithe eolais a mholadh a thuilleadh.',
    agentReadAccess:
      'Eolas na cuideachta a léamh: Próifíl na cuideachta agus leathanaigh eolais cheadaithe. Níl comhráite, comhaid, cruinnithe ná cuimhne phearsanta san áireamh.',
    agentHintKeyStale:
      'D’athraigh seoladh Poneva tar éis an eochair seo a dhéanamh, mar sin diúltaíonn Poneva di. Cúlghair í agus cruthaigh eochair nua.',
    agentHintStale:
      'D’athraigh seoladh Poneva tar éis do {app} ceangal, mar sin diúltaíonn Poneva dó. Dícheangail é, ansin ceangail {app} arís leis an seoladh thuas.',
    agentHintKeyWaiting: 'Taispeántar mar Réidh é a luaithe a úsáideann gníomhaire an eochair.',
    agentHintWaiting: 'Cuir ceist ar {app} chun seiceáil an cheangail a chríochnú.',
    agentHintTryAsking: 'Bain triail as an gceist seo:',
    agentFactCreated: 'Cruthaithe',
    agentFactConnected: 'Ceangailte',
    agentFactExpires: 'Téann in éag',
    agentFactSignInEnds: 'Críochnaíonn an síniú isteach',
    agentFactAccess: 'Rochtain',
    agentNameNote:
      'Is é an t-ainm an ceann a thuairiscigh an gníomhaire; ní féidir le Poneva é a fhíorú. Dícheangail aon ghníomhaire nach n-aithníonn tú.',
    agentSignInNote:
      'Nuair a chríochnaíonn a shíniú isteach, iarrann an gníomhaire ort síniú isteach arís.',
    agentWorking: 'Ag obair…',
    agentRevokeKey: 'Cúlghair an eochair',
    agentDisconnect: 'Dícheangail',
    cardAgentsLabel: 'Do ghníomhairí ceangailte',
    cardMore: 'agus {more}',
    cardMoreCount:
      'one:eile i Naisc|two:eile i Naisc|few:eile i Naisc|many:eile i Naisc|other:eile i Naisc',
    cardOpenSetup: 'Oscail an socrú',
    cardChecking: 'Do ghníomhairí seachtracha á seiceáil…',
    cardTryAgain: 'Bain triail eile as',
    connectedAgentsSubtitleAny: 'Gníomhairí a léann eolas do chuideachta trí Poneva.',
    scopeLibraryReadTitle: 'Léigh d’obair in Poneva',
    scopeLibraryReadDescribe:
      'Is féidir le {app} na comhráite, an Leabharlann, na comhaid, na cruinnithe, na Skills, na gníomhairí agus na haipeanna is féidir leat a oscailt in Poneva a aimsiú agus a léamh, agus léamh ó do sheirbhísí ceangailte. Is féidir leis freisin cúntóirí siopadóireachta na cuideachta a léamh agus an méid a dúirt ceannaitheoirí leo.',
    scopeLibraryReadConfirmOn:
      'Ar mhaith leat ligean do {app} d’obair in Poneva a léamh in {company}? Aimsíonn agus léann sé an méid is féidir leat a oscailt in Poneva cheana, mar thusa.',
    scopeLibraryReadConfirmOff:
      'An bhfuil tú ag iarraidh stop a chur le {app} d’obair in Poneva a léamh? Ní bheidh sé in ann obair a shábháil ná ceist a chur ar Poneva ansin ach oiread.',
    scopeLibraryReadEnabled:
      'Is féidir le {app} d’obair in Poneva a léamh anois. Mura bhfuil an rogha le feiceáil in {app} fós, atosaigh é.',
    scopeLibraryReadDisabled: 'Ní féidir le {app} d’obair in Poneva a léamh a thuilleadh.',
    scopeLibraryWriteTitle: 'Sábháil obair agus cuir ceist ar Poneva',
    scopeLibraryWriteDescribe:
      'Is féidir le {app} obair a shábháil i do Leabharlann, áit a bhfanann obair nua príobháideach duit, agus leaganacha nua a shábháil d’obair is féidir leat a chur in eagar (fanann leaganacha níos luaithe sa Stair). Is féidir leis dréachtaí de Skills agus de dhearaí do chúntóirí siopadóireachta na cuideachta a shábháil, agus feiceann na comhghleacaithe ar féidir leo iad a chur in eagar na dréachtaí sin; ní fhoilsíonn ach duine iad in Poneva. Is féidir leis ceist a chur ar Poneva i gcomhráite príobháideacha freisin, rud a úsáideann creidmheasanna. Is féidir leis freisin léamh de shiopa gréasáin na cuideachta a thosú chun cúntóir siopadóireachta a dheisiú agus ceisteanna tástála a chur air; úsáideann an dá rud creidmheasanna na cuideachta.',
    scopeLibraryWriteConfirmOn:
      'Ar mhaith leat ligean do {app} obair a shábháil i do Leabharlann in {company}, lena n-áirítear leaganacha nua d’obair is féidir leat a chur in eagar agus dréachtaí is féidir le comhghleacaithe a fheiceáil, agus ceisteanna a chur ar Poneva ar do shon? Úsáideann ceisteanna creidmheasanna. Beidh sé in ann d’obair in Poneva a léamh freisin. Úsáideann léamh den siopa agus ceisteanna tástála do chúntóirí siopadóireachta creidmheasanna na cuideachta freisin.',
    scopeLibraryWriteConfirmOff:
      'An bhfuil tú ag iarraidh stop a chur le {app} obair a shábháil agus ceisteanna a chur ar Poneva? Fanann an méid atá sábháilte aige cheana i do Leabharlann.',
    scopeLibraryWriteEnabled:
      'Is féidir le {app} obair a shábháil agus ceisteanna a chur ar Poneva anois. Mura bhfuil an rogha le feiceáil in {app} fós, atosaigh é.',
    scopeLibraryWriteDisabled:
      'Ní féidir le {app} obair a shábháil ná ceisteanna a chur ar Poneva a thuilleadh.',
  },
  hr: {
    mcpServersTitle: 'Prilagođene veze',
    mcpServersSubtitle:
      'Povežite vlastiti poslužitelj alata. Poneva pita prije nego išta promijeni.',
    mcpServersApproval:
      'Svaku promjenu odobravate. Za čitanje vrijedi ono što ste odabrali za svaki poslužitelj.',
    mcpServersAdd: 'Dodaj vezu',
    connectedAgentsLink: 'Povezani agenti',
    accountConnectionsUnavailable: 'Veze s računima još nisu dostupne u ovom radnom prostoru.',
    connectedAgentsTitle: 'Povezani agenti',
    connectedAgentsSubtitle: 'Agenti koji putem Poneve čitaju znanje tvrtke {company}.',
    connectedAgentsSuggest: 'Ako dopustite, predlažu izmjene koje pregledava osoba.',
    connectedAgentsList: 'Trenutačno povezani',
    connectingAgentsOff: 'Povezivanje agenata još nije dostupno za ovaj radni prostor.',
    checkingConnectedAgents: 'Provjeravaju se povezani agenti…',
    connectedAgentsLoadFailed: 'Povezane agente nije bilo moguće učitati.',
    connectedAgentsOpen: 'Otvori Povezane agente',
    agentTimeNotYet: 'još ne',
    agentTimeUnknown: 'u nepoznato vrijeme',
    agentTimeJustNow: 'upravo sada',
    agentTimeOnDate: 'dana {date}',
    agentErrorNotFound: 'Ovaj agent više nije povezan. Popis prikazuje trenutačno stanje.',
    agentErrorScopeUnavailable: 'Taj pristup nije dostupan u ovom radnom prostoru.',
    agentErrorTooManyKeys:
      'Imate onoliko pristupnih ključeva koliko Poneva dopušta. Najprije opozovite jedan.',
    agentErrorInvalidInput: 'Provjerite naziv ključa i pokušajte ponovno.',
    agentNotRecorded: 'Nije zabilježeno',
    agentMethodKey: 'Pristupni ključ',
    agentMethodSignedIn: 'Prijavljeno',
    agentStateReconnect: 'Potrebno ponovno povezivanje',
    agentStateReady: 'Spremno',
    agentStateKeyUnused: 'Stvoreno — još nije korišteno',
    agentStateWaiting: 'Autorizirano — čeka prvo čitanje',
    agentLastUsed: 'Zadnji put korišteno {when}',
    agentKeyExpires: 'Pristupni ključ · istječe {date}',
    agentSignInEnds: 'Prijavljeno · prijava istječe {date}',
    agentsNone: 'Još nema povezanih vanjskih agenata',
    agentsConnectedCount: 'one:agent povezan|few:agenta povezana|other:agenata povezano',
    agentsWaitingCount: 'one:čeka|few:čekaju|other:čeka',
    agentsReconnectCount:
      'one:za ponovno povezivanje|few:za ponovno povezivanje|other:za ponovno povezivanje',
    addressLocalNetwork:
      'Dostupno samo u ovoj mreži. Agenti koji rade u oblaku ne mogu koristiti ovu adresu.',
    addressLocalComputer:
      'Dostupno samo s ovog računala. Agenti koji rade u oblaku ne mogu koristiti ovu adresu.',
    addressUnreachable:
      'Poneva trenutačno ne može doći do ove adrese, pa vjerojatno ne mogu ni vanjski agenti.',
    agentUnnamed: 'Agent bez naziva',
    agentCompanyFallback: 'vaše tvrtke',
    agentErrorUnreadable:
      'Poneva je vratila odgovor koji ova stranica ne može pročitati. Pokušajte ponovno.',
    agentsLoadFailed: 'Vaše vanjske agente nije bilo moguće učitati.',
    agentsLoading: 'Učitavaju se vanjski agenti…',
    agentsConnectedShort: 'one:povezan|few:povezana|other:povezano',
    agentsAddressTitle: 'MCP adresa',
    agentsPrivacy:
      'Agenti djeluju u vaše ime i vide samo podatke {company}. Čitaju odobreno znanje, nikad privatne razgovore, osobnu memoriju ni privatne datoteke. Sve ostalo ostaje isključeno ako to ne dopustite.',
    agentsSetupHide: 'Sakrij postavljanje',
    agentsSetupAnother: 'Poveži drugog agenta',
    agentsHelpInChat: 'Zatraži pomoć u razgovoru',
    agentsHelpStarter: 'Pomozi mi povezati vanjskog agenta s Ponevom putem MCP-a.',
    agentNoticeReady: '{app} je pročitao znanje iz Poneve. Veza je spremna.',
    agentNoticeAuthorized:
      '{app} je autoriziran. Postavite mu pitanje kako biste dovršili provjeru veze.',
    agentScopeUnconfirmed:
      'Nismo mogli potvrditi promjenu za {app}. Pojedinosti u nastavku prikazuju što Poneva sada ima.',
    agentScopeNotChanged: 'Pristup za {app} nije promijenjen. Pokušajte ponovno.',
    agentConfirmRevokeKey:
      'Opozvati pristupni ključ „{app}”? Svaki agent koji ga koristi odmah prestaje čitati znanje {company}.',
    agentConfirmDisconnect:
      'Prekinuti vezu s agentom {app}? {app} više neće moći čitati znanje {company}. To ne utječe na vašu prijavu u Ponevu.',
    agentKeyRevoked: 'Pristupni ključ „{app}” je opozvan.',
    agentDisconnected: 'Veza s agentom {app} je prekinuta.',
    agentDisconnectUnconfirmed:
      'Nismo mogli potvrditi da je veza s agentom {app} prekinuta. Popis prikazuje trenutačno stanje.',
    agentDisconnectFailed: '{app} je i dalje povezan. Pokušajte ponovno.',
    setupTitle: 'Povežite vanjskog agenta',
    setupManualLabel: 'Ili ga postavite sami',
    setupAddTitle: 'Dodajte Ponevu kao MCP poslužitelj',
    setupAddBody:
      'U postavkama MCP-a ili konektora agenta dodajte udaljeni MCP poslužitelj (Streamable HTTP) s gornjom adresom.',
    setupConfigToggle: 'Koristite konfiguracijsku datoteku?',
    setupConfigBody: 'Agentima koji svoje MCP poslužitelje navode u JSON datoteci dodajte ovo:',
    setupSignInTitle: 'Prijavite se',
    setupSignInBody:
      'Agent otvara Ponevu u vašem pregledniku. Provjerite tvrtku i što agent smije raditi, a zatim odaberite Dopusti.',
    setupSignInFallback:
      'Ako se prozor za prijavu sam zatvori ili nikad ne prikaže Ponevu, ili agent zatraži token ili API ključ, umjesto toga upotrijebite pristupni ključ.',
    setupTryTitle: 'Isprobajte',
    setupTryAsk: 'Pitajte agenta:',
    setupSampleQuestion: 'Kakav je naš trenutačni smjer razvoja proizvoda? Navedi izvor.',
    setupTryNote: 'Nakon prvog čitanja ovdje se prikazuje kao Spremno.',
    setupPromptTitle: 'Neka ga postavi vaš agent za programiranje',
    setupPromptBody:
      'Zalijepite ovaj upit u Claude Code, Codex ili drugog agenta za programiranje. Sadrži vašu MCP adresu i vodi agenta kroz postavljanje. Pristup odobravate kad se prijavite.',
    copyAction: 'Kopiraj',
    copiedAction: 'Kopirano',
    copyNamed: 'Kopiraj: {what}',
    copiedNamed: 'Kopirano: {what}',
    copiedStatus: 'Kopirano: {what}.',
    copyManualHint:
      'Kopiranje nije dostupno u ovom pregledniku. Tekst je označen; kopirajte ga tipkovnicom.',
    copyPromptShow: 'Prikaži upit',
    copyPromptHide: 'Sakrij upit',
    copyWhatAddress: 'MCP adresa Poneve',
    copyWhatConfig: 'konfiguracija MCP poslužitelja',
    copyWhatSample: 'ogledno pitanje',
    copyWhatSampleFor: 'ogledno pitanje za {app}',
    copyWhatPrompt: 'upit za postavljanje',
    copyWhatKey: 'pristupni ključ za {app}',
    keyCreateAction: 'Stvori pristupni ključ',
    keySecret:
      'Čuvajte ga kao lozinku. Zalijepite ga samo u postavke MCP-a, konektora ili tajni agenta, nikad na web-stranicu ili u kod koji drugi mogu otvoriti.',
    keyFormNote:
      'Za agenta koji se ne može prijaviti. U vaše ime čita znanje {company} dok ne istekne ili ga ne opozovete; prestaje vrijediti i kad promijenite lozinku ili napustite tvrtku.',
    keyName: 'Naziv',
    keyNamePlaceholder: 'npr. Noćno izvješće',
    keyLifetime: 'Istječe nakon',
    keyDays: 'one:dan|few:dana|other:dana',
    keyAlsoAllow: 'Dopusti i',
    keyCreating: 'Stvaranje…',
    keyCreate: 'Stvori ključ',
    keyCancel: 'Odustani',
    keyUnconfirmed:
      'Nismo mogli potvrditi da je ključ stvoren. Ako se prikazuje na popisu, opozovite ga i stvorite novi.',
    keyNotCreated: 'Pristupni ključ nije stvoren. Pokušajte ponovno.',
    keyNewTitle: 'Pristupni ključ za {app}',
    keyCopyNow: 'Kopirajte ga sada. Poneva ga prikazuje samo jednom i sprema samo njegov otisak.',
    keyTokenRule: 'U polje {token} ili {apiKey} unosi se samo ključ.',
    keyTermToken: 'token',
    keyTermApiKey: 'API ključ',
    keyHeaderRule:
      'U polje {header} unosi se naziv {name} i vrijednost {value}, pri čemu {key} zamijenite svojim ključem.',
    keyTermHeader: 'zaglavlje',
    keyEndsOn:
      'Prestaje vrijediti {date}, kad ga opozovete te kad promijenite lozinku ili napustite tvrtku.',
    keyEndsExpiry:
      'Prestaje vrijediti kad istekne, kad ga opozovete te kad promijenite lozinku ili napustite tvrtku.',
    keyDone: 'Gotovo',
    scopeProposeTitle: 'Predlaganje izmjena znanja',
    scopeProposeDescribe:
      '{app} može predlagati izmjene stranica. Urednik tvrtke pregledava svaki prijedlog prije bilo kakve promjene.',
    scopeProposeConfirmOn:
      'Dopustiti agentu {app} da predlaže izmjene znanja {company}? Svaki prijedlog čeka u odjeljku Znanje → Za pregled dok ga urednik tvrtke ne odobri. Ništa se ne mijenja samo od sebe.',
    scopeProposeConfirmOff:
      'Zabraniti agentu {app} predlaganje izmjena znanja? Prijedlozi koje je već poslao ostaju na pregledu.',
    scopeProposeEnabled:
      '{app} sada može predlagati izmjene znanja. Ako se opcija još ne prikazuje u agentu {app}, ponovno ga pokrenite.',
    scopeProposeDisabled: '{app} više ne može predlagati izmjene znanja.',
    agentReadAccess:
      'Čitanje znanja tvrtke: profil tvrtke i odobrene stranice znanja. Razgovori, datoteke, sastanci i osobna memorija nisu uključeni.',
    agentHintKeyStale:
      'Adresa Poneve promijenila se nakon stvaranja ovog ključa pa ga Poneva odbija. Opozovite ga i stvorite novi ključ.',
    agentHintStale:
      'Adresa Poneve promijenila se nakon povezivanja agenta {app} pa ga Poneva odbija. Prekinite vezu, a zatim ponovno povežite {app} s gornjom adresom.',
    agentHintKeyWaiting: 'Prikazuje se kao Spremno čim agent upotrijebi ključ.',
    agentHintWaiting: 'Postavite pitanje agentu {app} kako biste dovršili provjeru veze.',
    agentHintTryAsking: 'Pokušajte pitati:',
    agentFactCreated: 'Stvoreno',
    agentFactConnected: 'Povezano',
    agentFactExpires: 'Istječe',
    agentFactSignInEnds: 'Prijava istječe',
    agentFactAccess: 'Pristup',
    agentNameNote:
      'Naziv je onaj koji je agent prijavio; Poneva ga ne može provjeriti. Prekinite vezu sa svakim agentom kojeg ne prepoznajete.',
    agentSignInNote: 'Kad mu prijava istekne, agent će vas zamoliti da se ponovno prijavite.',
    agentWorking: 'U tijeku…',
    agentRevokeKey: 'Opozovi ključ',
    agentDisconnect: 'Prekini vezu',
    cardAgentsLabel: 'Vaši povezani agenti',
    cardMore: 'i još {more}',
    cardMoreCount: 'one:agent u Vezama|few:agenta u Vezama|other:agenata u Vezama',
    cardOpenSetup: 'Otvori postavljanje',
    cardChecking: 'Provjeravaju se vaši vanjski agenti…',
    cardTryAgain: 'Pokušaj ponovno',
    connectedAgentsSubtitleAny: 'Agenti koji putem Poneve čitaju znanje vaše tvrtke.',
    scopeLibraryReadTitle: 'Čitanje vašeg rada u Ponevi',
    scopeLibraryReadDescribe:
      '{app} može pronalaziti i čitati chatove, Knjižnicu, datoteke, sastanke, Skillove, agente i aplikacije koje možete otvoriti u Ponevi te čitati iz vaših povezanih usluga. Može čitati i asistente za kupnju tvrtke te što su im kupci rekli.',
    scopeLibraryReadConfirmOn:
      'Dopustiti agentu {app} da čita vaš rad u Ponevi u tvrtki {company}? Pronalazi i čita ono što već možete otvoriti u Ponevi, i to kao vi.',
    scopeLibraryReadConfirmOff:
      'Zabraniti agentu {app} čitanje vašeg rada u Ponevi? Tada više neće moći ni spremati rad ni postavljati pitanja Ponevi.',
    scopeLibraryReadEnabled:
      '{app} sada može čitati vaš rad u Ponevi. Ako se opcija još ne prikazuje u agentu {app}, ponovno ga pokrenite.',
    scopeLibraryReadDisabled: '{app} više ne može čitati vaš rad u Ponevi.',
    scopeLibraryWriteTitle: 'Spremanje rada i postavljanje pitanja Ponevi',
    scopeLibraryWriteDescribe:
      '{app} može spremati rad u vašu Knjižnicu, gdje novi rad ostaje privatan, samo za vas, i spremati nove verzije rada koji možete uređivati (ranije verzije ostaju u odjeljku Povijest). Može spremati nacrte Skillova i dizajna asistenata za kupnju tvrtke, koje vide kolege koji ih mogu uređivati; u Ponevi ih objavljuje samo čovjek. Može i postavljati pitanja Ponevi u privatnim chatovima, što troši kredite. Može i pokrenuti čitanja web-trgovine tvrtke radi popravka asistenta za kupnju te mu postavljati probna pitanja; oboje troši kredite tvrtke.',
    scopeLibraryWriteConfirmOn:
      'Dopustiti agentu {app} da sprema rad u vašu Knjižnicu u tvrtki {company}, uključujući nove verzije rada koji možete uređivati i nacrte koje kolege mogu vidjeti, te postavlja pitanja Ponevi umjesto vas? Pitanja troše kredite. Moći će i čitati vaš rad u Ponevi. Čitanja trgovine i probna pitanja asistentima za kupnju također troše kredite tvrtke.',
    scopeLibraryWriteConfirmOff:
      'Zabraniti agentu {app} spremanje rada i postavljanje pitanja Ponevi? Ono što je već spremio ostaje u vašoj Knjižnici.',
    scopeLibraryWriteEnabled:
      '{app} sada može spremati rad i postavljati pitanja Ponevi. Ako se opcija još ne prikazuje u agentu {app}, ponovno ga pokrenite.',
    scopeLibraryWriteDisabled: '{app} više ne može spremati rad ni postavljati pitanja Ponevi.',
  },
  hu: {
    mcpServersTitle: 'Egyéni kapcsolatok',
    mcpServersSubtitle:
      'Csatlakoztassa saját eszközszerverét. A Poneva rákérdez, mielőtt bármit módosítana.',
    mcpServersApproval:
      'Minden változtatást Ön hagy jóvá. Az olvasásra az egyes kiszolgálókhoz választott beállítás vonatkozik.',
    mcpServersAdd: 'Kapcsolat hozzáadása',
    connectedAgentsLink: 'Csatlakoztatott ügynökök',
    accountConnectionsUnavailable: 'A fiókkapcsolatok még nem érhetők el ebben a munkaterületben.',
    connectedAgentsTitle: 'Csatlakoztatott ügynökök',
    connectedAgentsSubtitle:
      'Ügynökök, amelyek a Ponevan keresztül olvassák a(z) {company} tudását.',
    connectedAgentsSuggest:
      'Ha engedélyezi, módosításokat javasolnak, amelyeket egy ember ellenőriz.',
    connectedAgentsList: 'Most csatlakoztatva',
    connectingAgentsOff: 'Az ügynökök csatlakoztatása még nem érhető el ebben a munkaterületben.',
    checkingConnectedAgents: 'Csatlakoztatott ügynökök ellenőrzése…',
    connectedAgentsLoadFailed: 'A csatlakoztatott ügynököket nem sikerült betölteni.',
    connectedAgentsOpen: 'Csatlakoztatott ügynökök megnyitása',
    agentTimeNotYet: 'még nem',
    agentTimeUnknown: 'ismeretlen időpontban',
    agentTimeJustNow: 'épp most',
    agentTimeOnDate: '{date}',
    agentErrorNotFound:
      'Ez az ügynök már nincs csatlakoztatva. A lista az aktuális állapotot mutatja.',
    agentErrorScopeUnavailable: 'Ez a hozzáférés nem érhető el ebben a munkaterületben.',
    agentErrorTooManyKeys:
      'Annyi hozzáférési kulccsal rendelkezik, amennyit a Poneva engedélyez. Előbb vonjon vissza egyet.',
    agentErrorInvalidInput: 'Ellenőrizze a kulcs nevét, és próbálja újra.',
    agentNotRecorded: 'Nincs rögzítve',
    agentMethodKey: 'Hozzáférési kulcs',
    agentMethodSignedIn: 'Bejelentkezéssel',
    agentStateReconnect: 'Újracsatlakoztatás szükséges',
    agentStateReady: 'Kész',
    agentStateKeyUnused: 'Létrehozva – még nem használt',
    agentStateWaiting: 'Engedélyezve – az első olvasásra vár',
    agentLastUsed: 'Utoljára használva: {when}',
    agentKeyExpires: 'Hozzáférési kulcs · lejár: {date}',
    agentSignInEnds: 'Bejelentkezéssel · a bejelentkezés lejár: {date}',
    agentsNone: 'Még nincs csatlakoztatott külső ügynök',
    agentsConnectedCount: 'one:csatlakoztatott ügynök|other:csatlakoztatott ügynök',
    agentsWaitingCount: 'one:várakozó|other:várakozó',
    agentsReconnectCount: 'one:újracsatlakoztatandó|other:újracsatlakoztatandó',
    addressLocalNetwork:
      'Csak ezen a hálózaton érhető el. A felhőben futó ügynökök nem használhatják ezt a címet.',
    addressLocalComputer:
      'Csak erről a számítógépről érhető el. A felhőben futó ügynökök nem használhatják ezt a címet.',
    addressUnreachable:
      'A Poneva jelenleg nem éri el ezt a címet, így a külső ügynökök valószínűleg szintén nem.',
    agentUnnamed: 'Névtelen ügynök',
    agentCompanyFallback: 'az Ön cégének',
    agentErrorUnreadable:
      'A Poneva olyan választ küldött, amelyet ez az oldal nem tud beolvasni. Próbálja újra.',
    agentsLoadFailed: 'A külső ügynökeit nem sikerült betölteni.',
    agentsLoading: 'Külső ügynökök betöltése…',
    agentsConnectedShort: 'one:csatlakoztatva|other:csatlakoztatva',
    agentsAddressTitle: 'MCP-cím',
    agentsPrivacy:
      'Az ügynökök az Ön nevében járnak el, és kizárólag {company} adatait látják. Jóváhagyott tudást olvasnak, privát csevegéseket, személyes memóriát és privát fájlokat soha. Minden más kikapcsolva marad, hacsak nem engedélyezi.',
    agentsSetupHide: 'Beállítás elrejtése',
    agentsSetupAnother: 'Másik ügynök csatlakoztatása',
    agentsHelpInChat: 'Segítség a csevegésben',
    agentsHelpStarter: 'Segíts csatlakoztatni egy külső ügynököt a Ponevához MCP-n keresztül.',
    agentNoticeReady: 'A(z) {app} olvasott a Poneva tudásából. A kapcsolat készen áll.',
    agentNoticeAuthorized:
      'A(z) {app} engedélyezve. Tegyen fel neki egy kérdést a kapcsolat ellenőrzésének befejezéséhez.',
    agentScopeUnconfirmed:
      'Nem sikerült megerősíteni a(z) {app} módosítását. Az alábbi részletek mutatják, mi van most a Ponevában.',
    agentScopeNotChanged: 'A(z) {app} hozzáférése nem változott. Próbálja újra.',
    agentConfirmRevokeKey:
      'Visszavonja a(z) „{app}” hozzáférési kulcsot? Minden ügynök, amely használja, azonnal leáll {company} tudásának olvasásával.',
    agentConfirmDisconnect:
      'Leválasztja a(z) {app} ügynököt? A(z) {app} többé nem olvashatja {company} tudását. A Poneva-bejelentkezését ez nem érinti.',
    agentKeyRevoked: 'A(z) „{app}” hozzáférési kulcs visszavonva.',
    agentDisconnected: 'A(z) {app} leválasztva.',
    agentDisconnectUnconfirmed:
      'Nem sikerült megerősíteni, hogy a(z) {app} leválasztása megtörtént. A lista az aktuális állapotot mutatja.',
    agentDisconnectFailed: 'A(z) {app} még mindig csatlakoztatva van. Próbálja újra.',
    setupTitle: 'Külső ügynök csatlakoztatása',
    setupManualLabel: 'Vagy állítsa be saját maga',
    setupAddTitle: 'A Poneva hozzáadása MCP-kiszolgálóként',
    setupAddBody:
      'Az ügynök MCP- vagy összekötő-beállításaiban adjon hozzá egy távoli MCP-kiszolgálót (Streamable HTTP) a fenti címmel.',
    setupConfigToggle: 'Konfigurációs fájlt használ?',
    setupConfigBody:
      'Azoknak az ügynököknek, amelyek JSON-fájlban sorolják fel MCP-kiszolgálóikat, ez kell:',
    setupSignInTitle: 'Bejelentkezés',
    setupSignInBody:
      'Az ügynök megnyitja a Ponevát a böngészőjében. Ellenőrizze a céget és azt, hogy mit tehet az ügynök, majd válassza az Engedélyezés lehetőséget.',
    setupSignInFallback:
      'Ha a bejelentkezési ablak magától bezárul vagy sosem jeleníti meg a Ponevát, illetve ha az ügynök tokent vagy API-kulcsot kér, használjon inkább hozzáférési kulcsot.',
    setupTryTitle: 'Próbálja ki',
    setupTryAsk: 'Kérdezze meg az ügynököt:',
    setupSampleQuestion: 'Mi a jelenlegi termékirányunk? Nevezd meg a forrást.',
    setupTryNote: 'Az első olvasás után itt Kész állapotúként jelenik meg.',
    setupPromptTitle: 'Bízza a beállítást a kódoló ügynökére',
    setupPromptBody:
      'Illessze be ezt a promptot a Claude Code, a Codex vagy egy másik kódoló ügynök ablakába. Tartalmazza az MCP-címét, és végigvezeti az ügynököt a beállításon. A hozzáférést a bejelentkezéskor hagyja jóvá.',
    copyAction: 'Másolás',
    copiedAction: 'Másolva',
    copyNamed: 'Másolás: {what}',
    copiedNamed: 'Másolva: {what}',
    copiedStatus: 'Másolva: {what}.',
    copyManualHint:
      'A másolás nem érhető el ebben a böngészőben. A szöveg ki van jelölve; másolja a billentyűzettel.',
    copyPromptShow: 'Prompt megtekintése',
    copyPromptHide: 'Prompt elrejtése',
    copyWhatAddress: 'a Poneva MCP-címe',
    copyWhatConfig: 'az MCP-kiszolgáló konfigurációja',
    copyWhatSample: 'a mintakérdés',
    copyWhatSampleFor: 'a(z) {app} mintakérdése',
    copyWhatPrompt: 'beállítási prompt',
    copyWhatKey: 'a(z) {app} hozzáférési kulcsa',
    keyCreateAction: 'Hozzáférési kulcs létrehozása',
    keySecret:
      'Kezelje jelszóként. Csak egy ügynök MCP-, összekötő- vagy titokbeállításaiba illessze be, soha ne olyan webhelyre vagy kódba, amelyet mások is megnyithatnak.',
    keyFormNote:
      'Olyan ügynökhöz, amely nem tud bejelentkezni. Az Ön nevében olvassa {company} tudását, amíg le nem jár vagy vissza nem vonja; a jelszava megváltoztatása vagy a cégből való távozása is megszünteti.',
    keyName: 'Név',
    keyNamePlaceholder: 'pl. Éjszakai jelentés',
    keyLifetime: 'Lejárat',
    keyDays: 'one:nap|other:nap',
    keyAlsoAllow: 'Engedélyezés még',
    keyCreating: 'Létrehozás…',
    keyCreate: 'Kulcs létrehozása',
    keyCancel: 'Mégse',
    keyUnconfirmed:
      'Nem sikerült megerősíteni, hogy a kulcs létrejött. Ha megjelenik a listában, vonja vissza, és hozzon létre újat.',
    keyNotCreated: 'A hozzáférési kulcs nem jött létre. Próbálja újra.',
    keyNewTitle: 'Hozzáférési kulcs ehhez: {app}',
    keyCopyNow:
      'Másolja ki most. A Poneva csak egyszer jeleníti meg, és csak az ujjlenyomatát tárolja.',
    keyTokenRule: 'A {token} vagy {apiKey} mezőbe csak a kulcs kerül.',
    keyTermToken: 'token',
    keyTermApiKey: 'API-kulcs',
    keyHeaderRule:
      'A {header} mezőbe a {name} név és a {value} érték kerül, a {key} helyére a saját kulcsát írva.',
    keyTermHeader: 'fejléc',
    keyEndsOn:
      'Megszűnik működni ekkor: {date}, illetve ha visszavonja, megváltoztatja a jelszavát vagy elhagyja a céget.',
    keyEndsExpiry:
      'Megszűnik működni, amikor lejár, illetve ha visszavonja, megváltoztatja a jelszavát vagy elhagyja a céget.',
    keyDone: 'Kész',
    scopeProposeTitle: 'Tudásfrissítések javaslása',
    scopeProposeDescribe:
      'A(z) {app} oldalmódosításokat javasolhat. A cég egyik közzétevője mindegyiket ellenőrzi, mielőtt bármi megváltozna.',
    scopeProposeConfirmOn:
      'Engedélyezi, hogy a(z) {app} frissítéseket javasoljon {company} tudásához? Minden javaslat a Tudás → Ellenőrzésre vár részben marad, amíg a cég egyik közzétevője jóvá nem hagyja. Magától semmi sem változik.',
    scopeProposeConfirmOff:
      'Leállítja, hogy a(z) {app} tudásfrissítéseket javasoljon? A már elküldött javaslatai ellenőrzésre várnak tovább.',
    scopeProposeEnabled:
      'A(z) {app} mostantól javasolhat tudásfrissítéseket. Ha a lehetőség még nem jelenik meg a(z) {app} alkalmazásban, indítsa újra.',
    scopeProposeDisabled: 'A(z) {app} többé nem javasolhat tudásfrissítéseket.',
    agentReadAccess:
      'A cég tudásának olvasása: a cégprofil és a jóváhagyott tudásoldalak. A csevegések, a fájlok, a megbeszélések és a személyes memória nem tartoznak bele.',
    agentHintKeyStale:
      'A Poneva címe megváltozott a kulcs létrehozása után, ezért a Poneva elutasítja. Vonja vissza, és hozzon létre új kulcsot.',
    agentHintStale:
      'A Poneva címe megváltozott a(z) {app} csatlakoztatása után, ezért a Poneva elutasítja. Válassza le, majd csatlakoztassa újra a(z) {app} ügynököt a fenti címmel.',
    agentHintKeyWaiting: 'Kész állapotúként jelenik meg, amint egy ügynök használja a kulcsot.',
    agentHintWaiting:
      'Tegyen fel egy kérdést a(z) {app} ügynöknek a kapcsolat ellenőrzésének befejezéséhez.',
    agentHintTryAsking: 'Kérdezze meg például:',
    agentFactCreated: 'Létrehozva',
    agentFactConnected: 'Csatlakoztatva',
    agentFactExpires: 'Lejár',
    agentFactSignInEnds: 'Bejelentkezés lejár',
    agentFactAccess: 'Hozzáférés',
    agentNameNote:
      'Ezt a nevet az ügynök adta meg; a Poneva nem tudja ellenőrizni. Válasszon le minden ügynököt, amelyet nem ismer fel.',
    agentSignInNote: 'Amikor lejár a bejelentkezése, az ügynök újbóli bejelentkezést kér Öntől.',
    agentWorking: 'Folyamatban…',
    agentRevokeKey: 'Kulcs visszavonása',
    agentDisconnect: 'Leválasztás',
    cardAgentsLabel: 'Csatlakoztatott ügynökei',
    cardMore: 'és még {more}',
    cardMoreCount: 'one:a Kapcsolatok oldalon|other:a Kapcsolatok oldalon',
    cardOpenSetup: 'Beállítás megnyitása',
    cardChecking: 'Külső ügynökei ellenőrzése…',
    cardTryAgain: 'Újrapróbálás',
    connectedAgentsSubtitleAny:
      'Ügynökök, amelyek a Ponevan keresztül olvassák az Ön cégének tudását.',
    scopeLibraryReadTitle: 'Munkája olvasása a Ponevában',
    scopeLibraryReadDescribe:
      'A(z) {app} megtalálhatja és elolvashatja a csevegéseket, a Könyvtár elemeit, a fájlokat, a megbeszéléseket, a Skilleket, az ügynököket és az alkalmazásokat, amelyeket Ön megnyithat a Ponevában, és olvashat a csatlakoztatott szolgáltatásaiból is. A vállalat vásárlási asszisztenseit és a vásárlók nekik mondott szavait is olvashatja.',
    scopeLibraryReadConfirmOn:
      'Engedélyezi, hogy a(z) {app} olvassa a Ponevában végzett munkáját a(z) {company} cégnél? Az Ön nevében megtalálja és elolvassa azt, amit Ön már most is megnyithat a Ponevában.',
    scopeLibraryReadConfirmOff:
      'Leállítja, hogy a(z) {app} olvassa a Ponevában végzett munkáját? Ezután munkát menteni és a Ponevát kérdezni sem tud.',
    scopeLibraryReadEnabled:
      'A(z) {app} mostantól olvashatja a Ponevában végzett munkáját. Ha a lehetőség még nem jelenik meg a(z) {app} alkalmazásban, indítsa újra.',
    scopeLibraryReadDisabled: 'A(z) {app} többé nem olvashatja a Ponevában végzett munkáját.',
    scopeLibraryWriteTitle: 'Munka mentése és a Poneva kérdezése',
    scopeLibraryWriteDescribe:
      'A(z) {app} munkát menthet az Ön Könyvtárába, ahol az új munka csak az Ön számára látható, és mentheti az Ön által szerkeszthető munkák új verzióit (a korábbi verziók az Előzmények részben maradnak). Piszkozatokat is menthet Skillekhez és a cég vásárlási asszisztenseinek dizájnjához; ezeket azok a kollégák látják, akik szerkeszthetik őket, a Ponevában pedig csak ember teheti közzé őket. A Ponevát privát csevegésekben is kérdezheti, ami krediteket használ. Elindíthatja a vállalat webáruházának beolvasását is egy vásárlási asszisztens javításához, és tesztkérdéseket tehet fel neki; mindkettő a vállalat kreditjeit használja.',
    scopeLibraryWriteConfirmOn:
      'Engedélyezi, hogy a(z) {app} munkát mentsen a Könyvtárába a(z) {company} cégnél, ideértve az Ön által szerkeszthető munkák új verzióit és a kollégák által látható piszkozatokat is, és az Ön nevében kérdezze a Ponevát? A kérdések krediteket használnak. A Ponevában végzett munkáját is olvashatja. Az áruház beolvasása és a vásárlási asszisztenseknek feltett tesztkérdések szintén a vállalat kreditjeit használják.',
    scopeLibraryWriteConfirmOff:
      'Leállítja, hogy a(z) {app} munkát mentsen és a Ponevát kérdezze? Amit már mentett, az a Könyvtárában marad.',
    scopeLibraryWriteEnabled:
      'A(z) {app} mostantól menthet munkát és kérdezheti a Ponevát. Ha a lehetőség még nem jelenik meg a(z) {app} alkalmazásban, indítsa újra.',
    scopeLibraryWriteDisabled: 'A(z) {app} többé nem menthet munkát, és nem kérdezheti a Ponevát.',
  },
  it: {
    mcpServersTitle: 'Connessioni personalizzate',
    mcpServersSubtitle:
      'Connetti il tuo server di strumenti. Poneva chiede prima di cambiare qualsiasi cosa.',
    mcpServersApproval:
      'Approvi ogni modifica. Per la lettura vale la scelta fatta per ciascun server.',
    mcpServersAdd: 'Aggiungi connessione',
    connectedAgentsLink: 'Agenti collegati',
    accountConnectionsUnavailable:
      'I collegamenti degli account non sono ancora disponibili in questo spazio di lavoro.',
    connectedAgentsTitle: 'Agenti collegati',
    connectedAgentsSubtitle: 'Agenti che leggono le conoscenze di {company} tramite Poneva.',
    connectedAgentsSuggest: 'Se lo consenti, suggeriscono aggiornamenti che una persona rivede.',
    connectedAgentsList: 'Collegati ora',
    connectingAgentsOff:
      'Il collegamento degli agenti non è ancora disponibile per questo spazio di lavoro.',
    checkingConnectedAgents: 'Controllo degli agenti collegati…',
    connectedAgentsLoadFailed: 'Non è stato possibile caricare gli agenti collegati.',
    connectedAgentsOpen: 'Apri Agenti collegati',
    agentTimeNotYet: 'non ancora',
    agentTimeUnknown: 'in un momento sconosciuto',
    agentTimeJustNow: 'poco fa',
    agentTimeOnDate: 'il {date}',
    agentErrorNotFound: 'Questo agente non è più collegato. L’elenco mostra lo stato attuale.',
    agentErrorScopeUnavailable: 'Questo accesso non è disponibile in questo spazio di lavoro.',
    agentErrorTooManyKeys:
      'Hai già il numero massimo di chiavi di accesso consentito da Poneva. Revocane prima una.',
    agentErrorInvalidInput: 'Controlla il nome della chiave e riprova.',
    agentNotRecorded: 'Non registrato',
    agentMethodKey: 'Chiave di accesso',
    agentMethodSignedIn: 'Accesso effettuato',
    agentStateReconnect: 'Ricollegamento necessario',
    agentStateReady: 'Pronto',
    agentStateKeyUnused: 'Creata — non ancora usata',
    agentStateWaiting: 'Autorizzato — in attesa della prima lettura',
    agentLastUsed: 'Ultimo utilizzo {when}',
    agentKeyExpires: 'Chiave di accesso · scade il {date}',
    agentSignInEnds: 'Accesso effettuato · l’accesso scade il {date}',
    agentsNone: 'Nessun agente esterno ancora collegato',
    agentsConnectedCount: 'one:agente collegato|other:agenti collegati',
    agentsWaitingCount: 'one:in attesa|other:in attesa',
    agentsReconnectCount: 'one:da ricollegare|other:da ricollegare',
    addressLocalNetwork:
      'Raggiungibile solo su questa rete. Gli agenti eseguiti nel cloud non possono usare questo indirizzo.',
    addressLocalComputer:
      'Raggiungibile solo da questo computer. Gli agenti eseguiti nel cloud non possono usare questo indirizzo.',
    addressUnreachable:
      'Poneva non riesce a raggiungere questo indirizzo al momento, quindi probabilmente nemmeno gli agenti esterni.',
    agentUnnamed: 'Agente senza nome',
    agentCompanyFallback: 'questa azienda',
    agentErrorUnreadable:
      'Poneva ha restituito una risposta che questa pagina non riesce a leggere. Riprova.',
    agentsLoadFailed: 'Non è stato possibile caricare i tuoi agenti esterni.',
    agentsLoading: 'Caricamento degli agenti esterni…',
    agentsConnectedShort: 'one:collegato|many:collegati|other:collegati',
    agentsAddressTitle: 'Indirizzo MCP',
    agentsPrivacy:
      'Gli agenti agiscono a tuo nome e vedono solo {company}. Leggono le conoscenze approvate, mai chat private, memoria personale o file privati. Tutto il resto resta disattivato, a meno che tu non lo consenta.',
    agentsSetupHide: 'Nascondi configurazione',
    agentsSetupAnother: 'Collega un altro agente',
    agentsHelpInChat: 'Chiedi aiuto in chat',
    agentsHelpStarter: 'Aiutami a collegare un agente esterno a Poneva tramite MCP.',
    agentNoticeReady: '{app} ha letto le conoscenze di Poneva. Il collegamento è pronto.',
    agentNoticeAuthorized:
      '{app} è autorizzato. Fagli una domanda per completare la verifica del collegamento.',
    agentScopeUnconfirmed:
      'Non è stato possibile confermare la modifica per {app}. I dettagli qui sotto mostrano cosa risulta ora a Poneva.',
    agentScopeNotChanged: 'L’accesso di {app} non è cambiato. Riprova.',
    agentConfirmRevokeKey:
      'Revocare la chiave di accesso “{app}”? Qualsiasi agente che la usa smette subito di leggere le conoscenze di {company}.',
    agentConfirmDisconnect:
      'Scollegare {app}? {app} non potrà più leggere le conoscenze di {company}. Il tuo accesso a Poneva non cambia.',
    agentKeyRevoked: 'La chiave di accesso “{app}” è stata revocata.',
    agentDisconnected: '{app} è stato scollegato.',
    agentDisconnectUnconfirmed:
      'Non è stato possibile confermare che {app} sia stato scollegato. L’elenco mostra lo stato attuale.',
    agentDisconnectFailed: '{app} è ancora collegato. Riprova.',
    setupTitle: 'Collega un agente esterno',
    setupManualLabel: 'Oppure configuralo tu',
    setupAddTitle: 'Aggiungi Poneva come server MCP',
    setupAddBody:
      'Nelle impostazioni MCP o dei connettori dell’agente, aggiungi un server MCP remoto (Streamable HTTP) con l’indirizzo qui sopra.',
    setupConfigToggle: 'Usi un file di configurazione?',
    setupConfigBody: 'Per gli agenti che elencano i server MCP in un file JSON:',
    setupSignInTitle: 'Accedi',
    setupSignInBody:
      'L’agente apre Poneva nel tuo browser. Controlla l’azienda e cosa potrà fare l’agente, poi scegli Consenti.',
    setupSignInFallback:
      'Se la finestra di accesso si chiude da sola o non mostra mai Poneva, oppure se l’agente chiede un token o una chiave API, usa invece una chiave di accesso.',
    setupTryTitle: 'Provalo',
    setupTryAsk: 'Chiedi all’agente:',
    setupSampleQuestion: 'Qual è l’attuale direzione del nostro prodotto? Cita la fonte.',
    setupTryNote: 'Dopo la prima lettura qui risulta Pronto.',
    setupPromptTitle: 'Fallo configurare al tuo agente di programmazione',
    setupPromptBody:
      'Incolla questo prompt in Claude Code, Codex o un altro agente di programmazione. Contiene il tuo indirizzo MCP e guida l’agente nella configurazione. Approvi l’accesso quando accedi.',
    copyAction: 'Copia',
    copiedAction: 'Copiato',
    copyNamed: 'Copia {what}',
    copiedNamed: 'Copiato: {what}',
    copiedStatus: 'Copiato: {what}.',
    copyManualHint:
      'La copia non è disponibile in questo browser. Il testo è selezionato; copialo con la tastiera.',
    copyPromptShow: 'Visualizza prompt',
    copyPromptHide: 'Nascondi prompt',
    copyWhatAddress: 'l’indirizzo MCP di Poneva',
    copyWhatConfig: 'la configurazione del server MCP',
    copyWhatSample: 'la domanda di esempio',
    copyWhatSampleFor: 'la domanda di esempio per {app}',
    copyWhatPrompt: 'il prompt di configurazione',
    copyWhatKey: 'la chiave di accesso per {app}',
    keyCreateAction: 'Crea una chiave di accesso',
    keySecret:
      'Trattala come una password. Incollala solo nelle impostazioni MCP, dei connettori o dei segreti di un agente, mai in un sito web o in codice che altri possono aprire.',
    keyFormNote:
      'Per un agente che non può accedere. Legge le conoscenze di {company} a tuo nome finché non scade o non la revochi; anche cambiare password o lasciare l’azienda la disattiva.',
    keyName: 'Nome',
    keyNamePlaceholder: 'es. Report notturno',
    keyLifetime: 'Scade dopo',
    keyDays: 'one:giorno|many:giorni|other:giorni',
    keyAlsoAllow: 'Consenti anche',
    keyCreating: 'Creazione…',
    keyCreate: 'Crea chiave',
    keyCancel: 'Annulla',
    keyUnconfirmed:
      'Non è stato possibile confermare la creazione della chiave. Se compare nell’elenco, revocala e creane una nuova.',
    keyNotCreated: 'La chiave di accesso non è stata creata. Riprova.',
    keyNewTitle: 'Chiave di accesso per {app}',
    keyCopyNow: 'Copiala ora. Poneva la mostra una sola volta e ne conserva solo un’impronta.',
    keyTokenRule: 'Un campo {token} o {apiKey} accetta solo la chiave.',
    keyTermToken: 'token',
    keyTermApiKey: 'chiave API',
    keyHeaderRule:
      'Un campo {header} accetta il nome {name} e il valore {value}, sostituendo {key} con la tua chiave.',
    keyTermHeader: 'intestazione',
    keyEndsOn:
      'Smette di funzionare il {date}, quando la revochi e quando cambi password o lasci l’azienda.',
    keyEndsExpiry:
      'Smette di funzionare quando scade, quando la revochi e quando cambi password o lasci l’azienda.',
    keyDone: 'Fatto',
    scopeProposeTitle: 'Suggerire aggiornamenti alle conoscenze',
    scopeProposeDescribe:
      '{app} può suggerire modifiche alle pagine. Un editor dell’azienda esamina ciascuna prima che cambi qualcosa.',
    scopeProposeConfirmOn:
      'Consentire a {app} di suggerire aggiornamenti alle conoscenze di {company}? Ogni suggerimento attende in Conoscenze → Da rivedere finché un editor dell’azienda non lo approva. Nulla cambia da solo.',
    scopeProposeConfirmOff:
      'Impedire a {app} di suggerire aggiornamenti alle conoscenze? I suggerimenti già inviati restano in revisione.',
    scopeProposeEnabled:
      '{app} ora può suggerire aggiornamenti alle conoscenze. Se l’opzione non compare ancora in {app}, riavvialo.',
    scopeProposeDisabled: '{app} non può più suggerire aggiornamenti alle conoscenze.',
    agentReadAccess:
      'Leggere le conoscenze aziendali: il profilo dell’azienda e le pagine di conoscenza approvate. Chat, file, riunioni e memoria personale non sono inclusi.',
    agentHintKeyStale:
      'L’indirizzo di Poneva è cambiato dopo la creazione di questa chiave, quindi Poneva la rifiuta. Revocala e crea una nuova chiave.',
    agentHintStale:
      'L’indirizzo di Poneva è cambiato dopo il collegamento di {app}, quindi Poneva lo rifiuta. Scollegalo, poi collega di nuovo {app} con l’indirizzo qui sopra.',
    agentHintKeyWaiting: 'Risulta Pronto appena un agente usa la chiave.',
    agentHintWaiting: 'Fai una domanda a {app} per completare la verifica del collegamento.',
    agentHintTryAsking: 'Prova a chiedere:',
    agentFactCreated: 'Creato',
    agentFactConnected: 'Collegato',
    agentFactExpires: 'Scade',
    agentFactSignInEnds: 'L’accesso scade',
    agentFactAccess: 'Autorizzazioni',
    agentNameNote:
      'Il nome è quello comunicato dall’agente; Poneva non può verificarlo. Scollega qualsiasi agente che non riconosci.',
    agentSignInNote: 'Quando il suo accesso scade, l’agente ti chiede di accedere di nuovo.',
    agentWorking: 'In corso…',
    agentRevokeKey: 'Revoca chiave',
    agentDisconnect: 'Scollega',
    cardAgentsLabel: 'I tuoi agenti collegati',
    cardMore: 'e {more}',
    cardMoreCount:
      'one:altro in Collegamenti|many:altri in Collegamenti|other:altri in Collegamenti',
    cardOpenSetup: 'Apri configurazione',
    cardChecking: 'Controllo dei tuoi agenti esterni…',
    cardTryAgain: 'Riprova',
    connectedAgentsSubtitleAny: 'Agenti che leggono le conoscenze dell’azienda tramite Poneva.',
    scopeLibraryReadTitle: 'Leggere il tuo lavoro in Poneva',
    scopeLibraryReadDescribe:
      '{app} può trovare e leggere le chat, la Libreria, i file, le riunioni, le Skills, gli agenti e le App che puoi aprire in Poneva, e leggere dai tuoi servizi collegati. Può anche leggere gli assistenti agli acquisti dell’azienda e ciò che gli acquirenti hanno detto loro.',
    scopeLibraryReadConfirmOn:
      'Consentire a {app} di leggere il tuo lavoro in Poneva per {company}? Trova e legge ciò che puoi già aprire in Poneva, a tuo nome.',
    scopeLibraryReadConfirmOff:
      'Impedire a {app} di leggere il tuo lavoro in Poneva? Non potrà più nemmeno salvare lavoro né fare domande a Poneva.',
    scopeLibraryReadEnabled:
      '{app} ora può leggere il tuo lavoro in Poneva. Se l’opzione non compare ancora in {app}, riavvialo.',
    scopeLibraryReadDisabled: '{app} non può più leggere il tuo lavoro in Poneva.',
    scopeLibraryWriteTitle: 'Salvare lavoro e fare domande a Poneva',
    scopeLibraryWriteDescribe:
      '{app} può salvare lavoro nella tua Libreria, dove il lavoro nuovo resta visibile solo a te, e salvare nuove versioni del lavoro che puoi modificare (le versioni precedenti restano in Cronologia). Può salvare bozze di Skills e dei design degli assistenti agli acquisti dell’azienda, visibili ai colleghi che possono modificarle; solo una persona le pubblica in Poneva. Può anche fare domande a Poneva in chat private, il che usa crediti. Può anche avviare letture del negozio online dell’azienda per riparare un assistente agli acquisti e rivolgergli domande di prova; entrambe usano crediti dell’azienda.',
    scopeLibraryWriteConfirmOn:
      'Consentire a {app} di salvare lavoro nella tua Libreria per {company}, comprese nuove versioni del lavoro che puoi modificare e bozze visibili ai colleghi, e di fare domande a Poneva per te? Le domande usano crediti. Potrà anche leggere il tuo lavoro in Poneva. Anche le letture del negozio e le domande di prova agli assistenti agli acquisti usano crediti dell’azienda.',
    scopeLibraryWriteConfirmOff:
      'Impedire a {app} di salvare lavoro e fare domande a Poneva? Ciò che ha già salvato resta nella tua Libreria.',
    scopeLibraryWriteEnabled:
      '{app} ora può salvare lavoro e fare domande a Poneva. Se l’opzione non compare ancora in {app}, riavvialo.',
    scopeLibraryWriteDisabled: '{app} non può più salvare lavoro né fare domande a Poneva.',
  },
  lt: {
    mcpServersTitle: 'Pasirinktinės jungtys',
    mcpServersSubtitle:
      'Prijunkite savo įrankių serverį. Prieš ką nors keičiant, Poneva paklausia.',
    mcpServersApproval:
      'Kiekvieną pakeitimą patvirtinate. Skaitymui taikomas kiekvienam serveriui pasirinktas būdas.',
    mcpServersAdd: 'Pridėti jungtį',
    connectedAgentsLink: 'Prijungti agentai',
    accountConnectionsUnavailable: 'Paskyrų ryšiai šioje darbo erdvėje dar nepasiekiami.',
    connectedAgentsTitle: 'Prijungti agentai',
    connectedAgentsSubtitle: 'Agentai, kurie per Poneva skaito įmonės {company} žinias.',
    connectedAgentsSuggest: 'Jei leisite, jie siūlys pakeitimus, kuriuos peržiūri žmogus.',
    connectedAgentsList: 'Prijungti dabar',
    connectingAgentsOff: 'Agentų prijungimas šioje darbo erdvėje dar nepasiekiamas.',
    checkingConnectedAgents: 'Tikrinami prijungti agentai…',
    connectedAgentsLoadFailed: 'Nepavyko įkelti prijungtų agentų.',
    connectedAgentsOpen: 'Atidaryti Prijungtus agentus',
    agentTimeNotYet: 'dar ne',
    agentTimeUnknown: 'nežinomu laiku',
    agentTimeJustNow: 'ką tik',
    agentTimeOnDate: '{date}',
    agentErrorNotFound: 'Šis agentas nebeprijungtas. Sąraše rodoma dabartinė būsena.',
    agentErrorScopeUnavailable: 'Ši prieiga šioje darbo erdvėje nepasiekiama.',
    agentErrorTooManyKeys:
      'Turite tiek prieigos raktų, kiek leidžia Poneva. Pirmiausia atšaukite vieną.',
    agentErrorInvalidInput: 'Patikrinkite rakto pavadinimą ir bandykite dar kartą.',
    agentNotRecorded: 'Neužfiksuota',
    agentMethodKey: 'Prieigos raktas',
    agentMethodSignedIn: 'Prisijungta',
    agentStateReconnect: 'Reikia prijungti iš naujo',
    agentStateReady: 'Paruošta',
    agentStateKeyUnused: 'Sukurtas — dar nenaudotas',
    agentStateWaiting: 'Įgaliotas — laukia pirmojo skaitymo',
    agentLastUsed: 'Paskutinį kartą naudota {when}',
    agentKeyExpires: 'Prieigos raktas · galioja iki {date}',
    agentSignInEnds: 'Prisijungta · prisijungimas baigiasi {date}',
    agentsNone: 'Išorinių agentų dar neprijungta',
    agentsConnectedCount:
      'one:agentas prijungtas|few:agentai prijungti|many:agento prijungta|other:agentų prijungta',
    agentsWaitingCount: 'one:laukia|few:laukia|many:laukia|other:laukia',
    agentsReconnectCount:
      'one:reikia prijungti iš naujo|few:reikia prijungti iš naujo|many:reikia prijungti iš naujo|other:reikia prijungti iš naujo',
    addressLocalNetwork:
      'Pasiekiama tik šiame tinkle. Debesyje veikiantys agentai negali naudoti šio adreso.',
    addressLocalComputer:
      'Pasiekiama tik iš šio kompiuterio. Debesyje veikiantys agentai negali naudoti šio adreso.',
    addressUnreachable:
      'Poneva šiuo metu negali pasiekti šio adreso, todėl išoriniai agentai greičiausiai irgi negali.',
    agentUnnamed: 'Agentas be pavadinimo',
    agentCompanyFallback: 'jūsų įmonės',
    agentErrorUnreadable:
      'Poneva grąžino atsakymą, kurio šis puslapis negali perskaityti. Bandykite dar kartą.',
    agentsLoadFailed: 'Nepavyko įkelti jūsų išorinių agentų.',
    agentsLoading: 'Įkeliami išoriniai agentai…',
    agentsConnectedShort: 'one:prijungtas|few:prijungti|many:prijungta|other:prijungta',
    agentsAddressTitle: 'MCP adresas',
    agentsPrivacy:
      'Agentai veikia jūsų vardu ir mato tik {company} duomenis. Jie skaito patvirtintas žinias, bet niekada privačių pokalbių, asmeninės atminties ar privačių failų. Visa kita lieka išjungta, nebent tai leisite.',
    agentsSetupHide: 'Slėpti sąranką',
    agentsSetupAnother: 'Prijungti kitą agentą',
    agentsHelpInChat: 'Gauti pagalbos pokalbyje',
    agentsHelpStarter: 'Padėk man prijungti išorinį agentą prie Poneva per MCP.',
    agentNoticeReady: '{app} perskaitė Poneva žinias. Ryšys paruoštas.',
    agentNoticeAuthorized: '{app} įgaliotas. Užduokite jam klausimą, kad baigtumėte tikrinti ryšį.',
    agentScopeUnconfirmed:
      'Nepavyko patvirtinti {app} pakeitimo. Toliau pateikta informacija rodo, ką Poneva turi dabar.',
    agentScopeNotChanged: '{app} prieiga nepasikeitė. Bandykite dar kartą.',
    agentConfirmRevokeKey:
      'Atšaukti prieigos raktą „{app}“? Visi jį naudojantys agentai iš karto nustos skaityti {company} žinias.',
    agentConfirmDisconnect:
      'Atjungti {app}? {app} nebegalės skaityti {company} žinių. Jūsų prisijungimas prie Poneva nepaveikiamas.',
    agentKeyRevoked: 'Prieigos raktas „{app}“ atšauktas.',
    agentDisconnected: '{app} atjungtas.',
    agentDisconnectUnconfirmed:
      'Nepavyko patvirtinti, kad {app} buvo atjungtas. Sąraše rodoma dabartinė būsena.',
    agentDisconnectFailed: '{app} vis dar prijungtas. Bandykite dar kartą.',
    setupTitle: 'Prijunkite išorinį agentą',
    setupManualLabel: 'Arba nustatykite patys',
    setupAddTitle: 'Pridėkite Poneva kaip MCP serverį',
    setupAddBody:
      'Agento MCP arba jungčių nustatymuose pridėkite nuotolinį MCP serverį (Streamable HTTP) su aukščiau nurodytu adresu.',
    setupConfigToggle: 'Naudojate konfigūracijos failą?',
    setupConfigBody: 'Agentams, kurie MCP serverius nurodo JSON faile, reikia šito:',
    setupSignInTitle: 'Prisijunkite',
    setupSignInBody:
      'Agentas atidaro Poneva jūsų naršyklėje. Patikrinkite įmonę ir ką agentas galės daryti, tada pasirinkite Leisti.',
    setupSignInFallback:
      'Jei prisijungimo langas užsidaro pats arba niekada nerodo Poneva, arba agentas prašo žetono ar API rakto, naudokite prieigos raktą.',
    setupTryTitle: 'Išbandykite',
    setupTryAsk: 'Paklauskite agento:',
    setupSampleQuestion: 'Kokia dabartinė mūsų produkto kryptis? Nurodyk šaltinį.',
    setupTryNote: 'Po pirmojo skaitymo čia jis rodomas kaip Paruošta.',
    setupPromptTitle: 'Leiskite programavimo agentui viską nustatyti',
    setupPromptBody:
      'Įklijuokite šią užklausą į Claude Code, Codex ar kitą programavimo agentą. Joje yra jūsų MCP adresas, ir ji padeda agentui atlikti sąranką. Prieigą patvirtinate prisijungdami.',
    copyAction: 'Kopijuoti',
    copiedAction: 'Nukopijuota',
    copyNamed: 'Kopijuoti: {what}',
    copiedNamed: 'Nukopijuota: {what}',
    copiedStatus: 'Nukopijuota: {what}.',
    copyManualHint:
      'Šioje naršyklėje kopijuoti negalima. Tekstas pažymėtas; nukopijuokite jį klaviatūra.',
    copyPromptShow: 'Peržiūrėti užklausą',
    copyPromptHide: 'Slėpti užklausą',
    copyWhatAddress: 'Poneva MCP adresas',
    copyWhatConfig: 'MCP serverio konfigūracija',
    copyWhatSample: 'pavyzdinis klausimas',
    copyWhatSampleFor: 'pavyzdinis klausimas, skirtas {app}',
    copyWhatPrompt: 'sąrankos užklausa',
    copyWhatKey: 'prieigos raktas, skirtas {app}',
    keyCreateAction: 'Sukurti prieigos raktą',
    keySecret:
      'Saugokite jį kaip slaptažodį. Įklijuokite jį tik į agento MCP, jungčių ar slaptųjų duomenų nustatymus, niekada į svetainę ar kodą, kurį gali atidaryti kiti.',
    keyFormNote:
      'Agentui, kuris negali prisijungti. Jūsų vardu jis skaito {company} žinias, kol nebaigia galioti arba kol jo neatšauksite; jis taip pat nustoja galioti pakeitus slaptažodį ar išėjus iš įmonės.',
    keyName: 'Pavadinimas',
    keyNamePlaceholder: 'pvz., Naktinė ataskaita',
    keyLifetime: 'Galiojimo trukmė',
    keyDays: 'one:diena|few:dienos|many:dienos|other:dienų',
    keyAlsoAllow: 'Taip pat leisti',
    keyCreating: 'Kuriama…',
    keyCreate: 'Sukurti raktą',
    keyCancel: 'Atšaukti',
    keyUnconfirmed:
      'Nepavyko patvirtinti, kad raktas sukurtas. Jei jis matomas sąraše, atšaukite jį ir sukurkite naują.',
    keyNotCreated: 'Prieigos raktas nesukurtas. Bandykite dar kartą.',
    keyNewTitle: 'Prieigos raktas, skirtas {app}',
    keyCopyNow:
      'Nukopijuokite jį dabar. Poneva jį rodo tik vieną kartą ir saugo tik jo kontrolinį kodą.',
    keyTokenRule: 'Į lauką {token} arba {apiKey} įrašomas tik raktas.',
    keyTermToken: 'žetonas',
    keyTermApiKey: 'API raktas',
    keyHeaderRule:
      'Į lauką {header} įrašomas pavadinimas {name} ir reikšmė {value}, vietoj {key} įrašant savo raktą.',
    keyTermHeader: 'antraštė',
    keyEndsOn:
      'Jis nustoja veikti {date}, kai jį atšaukiate ir kai pakeičiate slaptažodį ar išeinate iš įmonės.',
    keyEndsExpiry:
      'Jis nustoja veikti, kai baigiasi jo galiojimas, kai jį atšaukiate ir kai pakeičiate slaptažodį ar išeinate iš įmonės.',
    keyDone: 'Atlikta',
    scopeProposeTitle: 'Siūlyti žinių atnaujinimus',
    scopeProposeDescribe:
      '{app} gali siūlyti puslapių pakeitimus. Įmonės publikuotojas peržiūri kiekvieną, prieš ką nors pakeičiant.',
    scopeProposeConfirmOn:
      'Leisti {app} siūlyti {company} žinių atnaujinimus? Kiekvienas pasiūlymas laukia skiltyje Žinios → Laukia peržiūros, kol įmonės publikuotojas jį patvirtins. Niekas nepasikeičia savaime.',
    scopeProposeConfirmOff:
      'Neleisti {app} siūlyti žinių atnaujinimų? Jau išsiųsti pasiūlymai lieka peržiūrai.',
    scopeProposeEnabled:
      '{app} dabar gali siūlyti žinių atnaujinimus. Jei parinktis {app} dar nerodoma, paleiskite jį iš naujo.',
    scopeProposeDisabled: '{app} nebegali siūlyti žinių atnaujinimų.',
    agentReadAccess:
      'Skaityti įmonės žinias: įmonės profilį ir patvirtintus žinių puslapius. Pokalbiai, failai, susitikimai ir asmeninė atmintis neįtraukiami.',
    agentHintKeyStale:
      'Poneva adresas pasikeitė po to, kai šis raktas buvo sukurtas, todėl Poneva jį atmeta. Atšaukite jį ir sukurkite naują raktą.',
    agentHintStale:
      'Poneva adresas pasikeitė po to, kai {app} buvo prijungtas, todėl Poneva jį atmeta. Atjunkite jį, tada vėl prijunkite {app} su aukščiau nurodytu adresu.',
    agentHintKeyWaiting: 'Jis bus rodomas kaip Paruošta, kai tik agentas panaudos raktą.',
    agentHintWaiting: 'Užduokite {app} klausimą, kad baigtumėte tikrinti ryšį.',
    agentHintTryAsking: 'Pabandykite paklausti:',
    agentFactCreated: 'Sukurta',
    agentFactConnected: 'Prijungta',
    agentFactExpires: 'Galioja iki',
    agentFactSignInEnds: 'Prisijungimas baigiasi',
    agentFactAccess: 'Prieiga',
    agentNameNote:
      'Tai pavadinimas, kurį nurodė agentas; Poneva negali jo patikrinti. Atjunkite bet kurį agentą, kurio neatpažįstate.',
    agentSignInNote: 'Kai jo prisijungimas baigsis, agentas paprašys jūsų prisijungti dar kartą.',
    agentWorking: 'Vykdoma…',
    agentRevokeKey: 'Atšaukti raktą',
    agentDisconnect: 'Atjungti',
    cardAgentsLabel: 'Jūsų prijungti agentai',
    cardMore: 'ir dar {more}',
    cardMoreCount:
      'one:agentas skiltyje Ryšiai|few:agentai skiltyje Ryšiai|many:agento skiltyje Ryšiai|other:agentų skiltyje Ryšiai',
    cardOpenSetup: 'Atidaryti sąranką',
    cardChecking: 'Tikrinami jūsų išoriniai agentai…',
    cardTryAgain: 'Bandyti dar kartą',
    connectedAgentsSubtitleAny: 'Agentai, kurie per Poneva skaito jūsų įmonės žinias.',
    scopeLibraryReadTitle: 'Skaityti jūsų darbą programoje Poneva',
    scopeLibraryReadDescribe:
      '{app} gali rasti ir skaityti pokalbius, Bibliotekos elementus, failus, susitikimus, Skills, agentus ir programėles, kuriuos galite atidaryti programoje Poneva, taip pat skaityti iš jūsų prijungtų paslaugų. Taip pat gali skaityti įmonės pirkinių asistentus ir tai, ką pirkėjai jiems pasakė.',
    scopeLibraryReadConfirmOn:
      'Leisti {app} skaityti jūsų darbą programoje Poneva įmonėje {company}? Jūsų vardu jis randa ir skaito tai, ką jau galite atidaryti programoje Poneva.',
    scopeLibraryReadConfirmOff:
      'Neleisti {app} skaityti jūsų darbo programoje Poneva? Tada jis taip pat negalės išsaugoti darbo ar klausti Poneva.',
    scopeLibraryReadEnabled:
      '{app} dabar gali skaityti jūsų darbą programoje Poneva. Jei parinktis {app} dar nerodoma, paleiskite jį iš naujo.',
    scopeLibraryReadDisabled: '{app} nebegali skaityti jūsų darbo programoje Poneva.',
    scopeLibraryWriteTitle: 'Išsaugoti darbą ir klausti Poneva',
    scopeLibraryWriteDescribe:
      '{app} gali išsaugoti darbą jūsų Bibliotekoje, kurioje naujas darbas lieka privatus ir matomas tik jums, ir išsaugoti naujas darbų, kuriuos galite redaguoti, versijas (ankstesnės versijos lieka skiltyje Istorija). Jis gali išsaugoti Skills ir įmonės pirkinių asistentų dizaino juodraščius, kuriuos mato juos redaguoti galintys kolegos; programoje Poneva juos paskelbia tik žmogus. Jis taip pat gali klausti Poneva privačiuose pokalbiuose – tam naudojami kreditai. Taip pat gali pradėti įmonės internetinės parduotuvės skaitymą, kad pataisytų pirkinių asistentą, ir užduoti jam bandomųjų klausimų; abu veiksmai naudoja įmonės kreditus.',
    scopeLibraryWriteConfirmOn:
      'Leisti {app} išsaugoti darbą jūsų Bibliotekoje įmonėje {company}, įskaitant naujas darbų, kuriuos galite redaguoti, versijas ir kolegoms matomus juodraščius, ir klausti Poneva jūsų vardu? Klausimams naudojami kreditai. Jis taip pat galės skaityti jūsų darbą programoje Poneva. Parduotuvės skaitymai ir bandomieji klausimai pirkinių asistentams taip pat naudoja įmonės kreditus.',
    scopeLibraryWriteConfirmOff:
      'Neleisti {app} išsaugoti darbo ir klausti Poneva? Tai, ką jis jau išsaugojo, lieka jūsų Bibliotekoje.',
    scopeLibraryWriteEnabled:
      '{app} dabar gali išsaugoti darbą ir klausti Poneva. Jei parinktis {app} dar nerodoma, paleiskite jį iš naujo.',
    scopeLibraryWriteDisabled: '{app} nebegali išsaugoti darbo ar klausti Poneva.',
  },
  lv: {
    mcpServersTitle: 'Pielāgotie savienojumi',
    mcpServersSubtitle: 'Pievienojiet savu rīku serveri. Poneva pajautā, pirms kaut ko maina.',
    mcpServersApproval:
      'Jūs apstiprināt katru izmaiņu. Lasīšanai tiek piemērota katram serverim izvēlētā iespēja.',
    mcpServersAdd: 'Pievienot savienojumu',
    connectedAgentsLink: 'Pievienotie aģenti',
    accountConnectionsUnavailable: 'Kontu savienojumi šajā darbvietā vēl nav pieejami.',
    connectedAgentsTitle: 'Pievienotie aģenti',
    connectedAgentsSubtitle: 'Aģenti, kas caur Poneva lasa uzņēmuma {company} zināšanas.',
    connectedAgentsSuggest: 'Ja atļausiet, tie ieteiks izmaiņas, kuras pārskata cilvēks.',
    connectedAgentsList: 'Pievienoti tagad',
    connectingAgentsOff: 'Aģentu pievienošana šajā darbvietā vēl nav pieejama.',
    checkingConnectedAgents: 'Pārbauda pievienotos aģentus…',
    connectedAgentsLoadFailed: 'Pievienotos aģentus neizdevās ielādēt.',
    connectedAgentsOpen: 'Atvērt Pievienotos aģentus',
    agentTimeNotYet: 'vēl ne',
    agentTimeUnknown: 'nezināmā laikā',
    agentTimeJustNow: 'tikko',
    agentTimeOnDate: '{date}',
    agentErrorNotFound: 'Šis aģents vairs nav pievienots. Sarakstā redzams pašreizējais stāvoklis.',
    agentErrorScopeUnavailable: 'Šī piekļuve šajā darbvietā nav pieejama.',
    agentErrorTooManyKeys:
      'Jums ir tik daudz piekļuves atslēgu, cik Poneva atļauj. Vispirms atsauciet vienu.',
    agentErrorInvalidInput: 'Pārbaudiet atslēgas nosaukumu un mēģiniet vēlreiz.',
    agentNotRecorded: 'Nav reģistrēts',
    agentMethodKey: 'Piekļuves atslēga',
    agentMethodSignedIn: 'Pierakstījies',
    agentStateReconnect: 'Jāpievieno atkārtoti',
    agentStateReady: 'Gatavs',
    agentStateKeyUnused: 'Izveidota — vēl nav izmantota',
    agentStateWaiting: 'Autorizēts — gaida pirmo lasīšanu',
    agentLastUsed: 'Pēdējoreiz izmantots {when}',
    agentKeyExpires: 'Piekļuves atslēga · derīga līdz {date}',
    agentSignInEnds: 'Pierakstījies · pierakstīšanās beidzas {date}',
    agentsNone: 'Ārējie aģenti vēl nav pievienoti',
    agentsConnectedCount: 'zero:aģentu pievienots|one:aģents pievienots|other:aģenti pievienoti',
    agentsWaitingCount: 'zero:gaida|one:gaida|other:gaida',
    agentsReconnectCount:
      'zero:jāpievieno atkārtoti|one:jāpievieno atkārtoti|other:jāpievieno atkārtoti',
    addressLocalNetwork:
      'Sasniedzams tikai šajā tīklā. Mākonī darbojošies aģenti nevar izmantot šo adresi.',
    addressLocalComputer:
      'Sasniedzams tikai no šī datora. Mākonī darbojošies aģenti nevar izmantot šo adresi.',
    addressUnreachable:
      'Poneva pašlaik nevar sasniegt šo adresi, tāpēc arī ārējie aģenti, visticamāk, nevar.',
    agentUnnamed: 'Aģents bez nosaukuma',
    agentCompanyFallback: 'Jūsu uzņēmuma',
    agentErrorUnreadable: 'Poneva atgrieza atbildi, ko šī lapa nevar nolasīt. Mēģiniet vēlreiz.',
    agentsLoadFailed: 'Neizdevās ielādēt Jūsu ārējos aģentus.',
    agentsLoading: 'Ielādē ārējos aģentus…',
    agentsConnectedShort: 'zero:pievienots|one:pievienots|other:pievienoti',
    agentsAddressTitle: 'MCP adrese',
    agentsPrivacy:
      'Aģenti darbojas Jūsu vārdā un redz tikai {company} datus. Tie lasa apstiprinātas zināšanas, nekad privātas tērzēšanas, personisko atmiņu vai privātus failus. Viss pārējais paliek izslēgts, ja vien Jūs to neatļaujat.',
    agentsSetupHide: 'Paslēpt iestatīšanu',
    agentsSetupAnother: 'Pievienot citu aģentu',
    agentsHelpInChat: 'Saņemt palīdzību tērzēšanā',
    agentsHelpStarter: 'Palīdzi man pievienot ārēju aģentu Poneva, izmantojot MCP.',
    agentNoticeReady: '{app} nolasīja Poneva zināšanas. Savienojums ir gatavs.',
    agentNoticeAuthorized:
      '{app} ir autorizēts. Uzdodiet tam jautājumu, lai pabeigtu savienojuma pārbaudi.',
    agentScopeUnconfirmed:
      'Neizdevās apstiprināt {app} izmaiņas. Tālāk redzamā informācija parāda, kas Poneva ir tagad.',
    agentScopeNotChanged: '{app} piekļuve netika mainīta. Mēģiniet vēlreiz.',
    agentConfirmRevokeKey:
      'Vai atsaukt piekļuves atslēgu “{app}”? Visi aģenti, kas to izmanto, nekavējoties pārstās lasīt {company} zināšanas.',
    agentConfirmDisconnect:
      'Vai atvienot {app}? {app} vairs nevarēs lasīt {company} zināšanas. Jūsu pierakstīšanos Poneva tas neietekmē.',
    agentKeyRevoked: 'Piekļuves atslēga “{app}” ir atsaukta.',
    agentDisconnected: '{app} ir atvienots.',
    agentDisconnectUnconfirmed:
      'Neizdevās apstiprināt, ka {app} ir atvienots. Sarakstā redzams pašreizējais stāvoklis.',
    agentDisconnectFailed: '{app} joprojām ir pievienots. Mēģiniet vēlreiz.',
    setupTitle: 'Pievienojiet ārēju aģentu',
    setupManualLabel: 'Vai iestatiet to paši',
    setupAddTitle: 'Pievienojiet Poneva kā MCP serveri',
    setupAddBody:
      'Aģenta MCP vai savienotāju iestatījumos pievienojiet attālu MCP serveri (Streamable HTTP) ar iepriekš norādīto adresi.',
    setupConfigToggle: 'Izmantojat konfigurācijas failu?',
    setupConfigBody: 'Aģentiem, kas savus MCP serverus norāda JSON failā, vajag šo:',
    setupSignInTitle: 'Pierakstieties',
    setupSignInBody:
      'Aģents atver Poneva Jūsu pārlūkā. Pārbaudiet uzņēmumu un to, ko aģents drīkstēs darīt, pēc tam izvēlieties Atļaut.',
    setupSignInFallback:
      'Ja pierakstīšanās logs aizveras pats vai nekad nerāda Poneva, vai arī aģents prasa marķieri vai API atslēgu, tā vietā izmantojiet piekļuves atslēgu.',
    setupTryTitle: 'Izmēģiniet',
    setupTryAsk: 'Pajautājiet aģentam:',
    setupSampleQuestion: 'Kāds ir mūsu pašreizējais produkta virziens? Norādi avotu.',
    setupTryNote: 'Pēc pirmās lasīšanas šeit tas tiek rādīts kā Gatavs.',
    setupPromptTitle: 'Ļaujiet programmēšanas aģentam to iestatīt',
    setupPromptBody:
      'Ielīmējiet šo uzvedni Claude Code, Codex vai citā programmēšanas aģentā. Tajā ir Jūsu MCP adrese, un tā palīdz aģentam veikt iestatīšanu. Piekļuvi Jūs apstiprināt, kad pierakstāties.',
    copyAction: 'Kopēt',
    copiedAction: 'Nokopēts',
    copyNamed: 'Kopēt: {what}',
    copiedNamed: 'Nokopēts: {what}',
    copiedStatus: 'Nokopēts: {what}.',
    copyManualHint:
      'Šajā pārlūkā kopēšana nav pieejama. Teksts ir atlasīts; nokopējiet to ar tastatūru.',
    copyPromptShow: 'Skatīt uzvedni',
    copyPromptHide: 'Paslēpt uzvedni',
    copyWhatAddress: 'Poneva MCP adrese',
    copyWhatConfig: 'MCP servera konfigurācija',
    copyWhatSample: 'parauga jautājums',
    copyWhatSampleFor: 'parauga jautājums aģentam {app}',
    copyWhatPrompt: 'iestatīšanas uzvedne',
    copyWhatKey: 'piekļuves atslēga aģentam {app}',
    keyCreateAction: 'Izveidot piekļuves atslēgu',
    keySecret:
      'Glabājiet to kā paroli. Ielīmējiet to tikai aģenta MCP, savienotāju vai noslēpumu iestatījumos, nekad tīmekļa vietnē vai kodā, ko var atvērt citi.',
    keyFormNote:
      'Aģentam, kas nevar pierakstīties. Tā Jūsu vārdā lasa {company} zināšanas, līdz beidzas tās derīgums vai Jūs to atsaucat; tā darbību izbeidz arī paroles maiņa vai aiziešana no uzņēmuma.',
    keyName: 'Nosaukums',
    keyNamePlaceholder: 'piem., Nakts atskaite',
    keyLifetime: 'Derīguma termiņš',
    keyDays: 'zero:dienu|one:diena|other:dienas',
    keyAlsoAllow: 'Atļaut arī',
    keyCreating: 'Izveido…',
    keyCreate: 'Izveidot atslēgu',
    keyCancel: 'Atcelt',
    keyUnconfirmed:
      'Neizdevās apstiprināt, ka atslēga ir izveidota. Ja tā ir redzama sarakstā, atsauciet to un izveidojiet jaunu.',
    keyNotCreated: 'Piekļuves atslēga netika izveidota. Mēģiniet vēlreiz.',
    keyNewTitle: 'Piekļuves atslēga aģentam {app}',
    keyCopyNow:
      'Nokopējiet to tagad. Poneva to parāda tikai vienreiz un saglabā tikai tās nospiedumu.',
    keyTokenRule: 'Laukā {token} vai {apiKey} ievada tikai atslēgu.',
    keyTermToken: 'marķieris',
    keyTermApiKey: 'API atslēga',
    keyHeaderRule:
      'Laukā {header} ievada nosaukumu {name} un vērtību {value}, {key} vietā ierakstot savu atslēgu.',
    keyTermHeader: 'galvene',
    keyEndsOn:
      'Tā pārstāj darboties {date}, kad Jūs to atsaucat un kad nomaināt paroli vai aizejat no uzņēmuma.',
    keyEndsExpiry:
      'Tā pārstāj darboties, kad beidzas tās derīgums, kad Jūs to atsaucat un kad nomaināt paroli vai aizejat no uzņēmuma.',
    keyDone: 'Gatavs',
    scopeProposeTitle: 'Ieteikt zināšanu atjauninājumus',
    scopeProposeDescribe:
      '{app} var ieteikt lapu izmaiņas. Uzņēmuma publicētājs pārskata katru, pirms kaut kas mainās.',
    scopeProposeConfirmOn:
      'Vai atļaut {app} ieteikt {company} zināšanu atjauninājumus? Katrs ieteikums gaida sadaļā Zināšanas → Jāpārskata, līdz uzņēmuma publicētājs to apstiprina. Nekas nemainās pats no sevis.',
    scopeProposeConfirmOff:
      'Vai aizliegt {app} ieteikt zināšanu atjauninājumus? Jau nosūtītie ieteikumi paliek pārskatīšanā.',
    scopeProposeEnabled:
      '{app} tagad var ieteikt zināšanu atjauninājumus. Ja opcija {app} vēl nav redzama, restartējiet to.',
    scopeProposeDisabled: '{app} vairs nevar ieteikt zināšanu atjauninājumus.',
    agentReadAccess:
      'Lasīt uzņēmuma zināšanas: uzņēmuma profils un apstiprinātās zināšanu lapas. Tērzēšanas, faili, sapulces un personiskā atmiņa nav iekļauti.',
    agentHintKeyStale:
      'Poneva adrese mainījās pēc šīs atslēgas izveides, tāpēc Poneva to noraida. Atsauciet to un izveidojiet jaunu atslēgu.',
    agentHintStale:
      'Poneva adrese mainījās pēc tam, kad {app} tika pievienots, tāpēc Poneva to noraida. Atvienojiet to un pēc tam vēlreiz pievienojiet {app}, izmantojot iepriekš norādīto adresi.',
    agentHintKeyWaiting: 'Tas tiek rādīts kā Gatavs, tiklīdz kāds aģents izmanto atslēgu.',
    agentHintWaiting: 'Uzdodiet {app} jautājumu, lai pabeigtu savienojuma pārbaudi.',
    agentHintTryAsking: 'Pamēģiniet pajautāt:',
    agentFactCreated: 'Izveidots',
    agentFactConnected: 'Pievienots',
    agentFactExpires: 'Derīgs līdz',
    agentFactSignInEnds: 'Pierakstīšanās beidzas',
    agentFactAccess: 'Piekļuve',
    agentNameNote:
      'Šo nosaukumu norādīja aģents; Poneva to nevar pārbaudīt. Atvienojiet jebkuru aģentu, kuru neatpazīstat.',
    agentSignInNote: 'Kad tā pierakstīšanās beidzas, aģents lūdz Jūs pierakstīties vēlreiz.',
    agentWorking: 'Notiek apstrāde…',
    agentRevokeKey: 'Atsaukt atslēgu',
    agentDisconnect: 'Atvienot',
    cardAgentsLabel: 'Jūsu pievienotie aģenti',
    cardMore: 'un vēl {more}',
    cardMoreCount:
      'zero:aģentu sadaļā Savienojumi|one:aģents sadaļā Savienojumi|other:aģenti sadaļā Savienojumi',
    cardOpenSetup: 'Atvērt iestatīšanu',
    cardChecking: 'Pārbauda Jūsu ārējos aģentus…',
    cardTryAgain: 'Mēģināt vēlreiz',
    connectedAgentsSubtitleAny: 'Aģenti, kas caur Poneva lasa Jūsu uzņēmuma zināšanas.',
    scopeLibraryReadTitle: 'Lasīt Jūsu Poneva darbu',
    scopeLibraryReadDescribe:
      '{app} var atrast un lasīt tērzēšanas, Bibliotēku, failus, sapulces, Skills, aģentus un lietotnes, ko varat atvērt pakalpojumā Poneva, kā arī lasīt no Jūsu pievienotajiem pakalpojumiem. Tas var arī lasīt uzņēmuma iepirkšanās asistentus un to, ko pircēji tiem teikuši.',
    scopeLibraryReadConfirmOn:
      'Vai atļaut {app} lasīt Jūsu Poneva darbu uzņēmumā {company}? Tas Jūsu vārdā atrod un lasa to, ko jau varat atvērt pakalpojumā Poneva.',
    scopeLibraryReadConfirmOff:
      'Vai aizliegt {app} lasīt Jūsu Poneva darbu? Tad tas vairs nevarēs arī saglabāt darbu vai jautāt Poneva.',
    scopeLibraryReadEnabled:
      '{app} tagad var lasīt Jūsu Poneva darbu. Ja opcija {app} vēl nav redzama, restartējiet to.',
    scopeLibraryReadDisabled: '{app} vairs nevar lasīt Jūsu Poneva darbu.',
    scopeLibraryWriteTitle: 'Saglabāt darbu un jautāt Poneva',
    scopeLibraryWriteDescribe:
      '{app} var saglabāt darbu Jūsu Bibliotēkā, kur jauns darbs paliek privāts un redzams tikai Jums, un saglabāt jaunas versijas darbiem, kurus varat rediģēt (iepriekšējās versijas paliek sadaļā Vēsture). Tas var saglabāt Skills un uzņēmuma iepirkšanās asistentu dizainu melnrakstus, ko redz kolēģi, kuri var tos rediģēt; pakalpojumā Poneva tos publicē tikai cilvēks. Tas var arī jautāt Poneva privātās tērzēšanās, kas izmanto kredītus. Tas var arī sākt uzņēmuma tīmekļa veikala lasīšanu, lai salabotu iepirkšanās asistentu, un uzdot tam pārbaudes jautājumus; abi izmanto uzņēmuma kredītus.',
    scopeLibraryWriteConfirmOn:
      'Vai atļaut {app} saglabāt darbu Jūsu Bibliotēkā uzņēmumā {company}, tostarp jaunas versijas darbiem, kurus varat rediģēt, un melnrakstus, ko var redzēt kolēģi, kā arī jautāt Poneva Jūsu vietā? Jautājumi izmanto kredītus. Tas varēs arī lasīt Jūsu Poneva darbu. Veikala lasīšana un pārbaudes jautājumi iepirkšanās asistentiem arī izmanto uzņēmuma kredītus.',
    scopeLibraryWriteConfirmOff:
      'Vai aizliegt {app} saglabāt darbu un jautāt Poneva? Tas, ko tas jau saglabāja, paliek Jūsu Bibliotēkā.',
    scopeLibraryWriteEnabled:
      '{app} tagad var saglabāt darbu un jautāt Poneva. Ja opcija {app} vēl nav redzama, restartējiet to.',
    scopeLibraryWriteDisabled: '{app} vairs nevar saglabāt darbu vai jautāt Poneva.',
  },
  mt: {
    mcpServersTitle: 'Konnessjonijiet personalizzati',
    mcpServersSubtitle:
      'Qabbad is-server tal-għodod tiegħek. Poneva jistaqsi qabel ma jbiddel xi ħaġa.',
    mcpServersApproval:
      'Int tapprova kull bidla. Għall-qari, tapplika l-għażla li għamilt għal kull server.',
    mcpServersAdd: 'Żid konnessjoni',
    connectedAgentsLink: 'Aġenti konnessi',
    accountConnectionsUnavailable:
      'Il-konnessjonijiet tal-kontijiet għadhom mhumiex disponibbli f’dan l-ispazju tax-xogħol.',
    connectedAgentsTitle: 'Aġenti konnessi',
    connectedAgentsSubtitle: 'Aġenti li jaqraw l-għarfien ta’ {company} permezz ta’ Poneva.',
    connectedAgentsSuggest:
      'Jekk tippermetti, jissuġġerixxu aġġornamenti biex persuna tirrevedihom.',
    connectedAgentsList: 'Konnessi issa',
    connectingAgentsOff:
      'Il-konnessjoni ta’ aġenti għadha mhix disponibbli għal dan l-ispazju tax-xogħol.',
    checkingConnectedAgents: 'Qed jiċċekkja l-aġenti konnessi…',
    connectedAgentsLoadFailed: 'L-aġenti konnessi ma setgħux jitgħabbew.',
    connectedAgentsOpen: 'Iftaħ Aġenti konnessi',
    agentTimeNotYet: 'għadu le',
    agentTimeUnknown: 'f’ħin mhux magħruf',
    agentTimeJustNow: 'issa stess',
    agentTimeOnDate: 'nhar {date}',
    agentErrorNotFound: 'Dan l-aġent m’għadux konness. Il-lista turi l-istat attwali.',
    agentErrorScopeUnavailable: 'Dak l-aċċess mhux disponibbli f’dan l-ispazju tax-xogħol.',
    agentErrorTooManyKeys:
      'Għandek l-ogħla numru ta’ ċwievet tal-aċċess li jippermetti Poneva. L-ewwel irrevoka waħda.',
    agentErrorInvalidInput: 'Iċċekkja l-isem taċ-ċavetta u erġa’ pprova.',
    agentNotRecorded: 'Mhux irreġistrat',
    agentMethodKey: 'Ċavetta tal-aċċess',
    agentMethodSignedIn: 'Dħul bil-kont',
    agentStateReconnect: 'Jeħtieġ konnessjoni mill-ġdid',
    agentStateReady: 'Lest',
    agentStateKeyUnused: 'Maħluq — għadu ma ntużax',
    agentStateWaiting: 'Awtorizzat — qed jistenna l-ewwel qari',
    agentLastUsed: 'L-aħħar użu {when}',
    agentKeyExpires: 'Ċavetta tal-aċċess · tiskadi nhar {date}',
    agentSignInEnds: 'Dħul bil-kont · id-dħul jintemm nhar {date}',
    agentsNone: 'Għad m’hemm l-ebda aġent estern konness',
    agentsConnectedCount:
      'one:aġent konness|two:aġenti konnessi|few:aġenti konnessi|many:aġent konness|other:aġent konness',
    agentsWaitingCount:
      'one:qed jistenna|two:qed jistennew|few:qed jistennew|many:qed jistennew|other:qed jistennew',
    agentsReconnectCount:
      'one:għal konnessjoni mill-ġdid|two:għal konnessjoni mill-ġdid|few:għal konnessjoni mill-ġdid|many:għal konnessjoni mill-ġdid|other:għal konnessjoni mill-ġdid',
    addressLocalNetwork:
      'Jista’ jintlaħaq biss fuq dan in-netwerk. L-aġenti li jaħdmu fil-cloud ma jistgħux jużaw dan l-indirizz.',
    addressLocalComputer:
      'Jista’ jintlaħaq biss minn dan il-kompjuter. L-aġenti li jaħdmu fil-cloud ma jistgħux jużaw dan l-indirizz.',
    addressUnreachable:
      'Poneva ma jistax jilħaq dan l-indirizz bħalissa, allura probabbilment lanqas l-aġenti esterni ma jistgħu.',
    agentUnnamed: 'Aġent bla isem',
    agentCompanyFallback: 'il-kumpanija tiegħek',
    agentErrorUnreadable: 'Poneva bagħat tweġiba li din il-paġna ma setgħetx taqra. Erġa’ pprova.',
    agentsLoadFailed: 'L-aġenti esterni tiegħek ma setgħux jitgħabbew.',
    agentsLoading: 'Qed jitgħabbew l-aġenti esterni…',
    agentsConnectedShort: 'one:konness|two:konnessi|few:konnessi|many:konness|other:konness',
    agentsAddressTitle: 'Indirizz MCP',
    agentsPrivacy:
      'L-aġenti jaġixxu f’ismek u qatt ma jaraw ħlief {company}. Jaqraw l-għarfien approvat, qatt chats privati, memorja personali jew fajls privati. Kull ħaġa oħra tibqa’ mitfija sakemm ma tippermettihiex.',
    agentsSetupHide: 'Aħbi s-setup',
    agentsSetupAnother: 'Ikkonnettja aġent ieħor',
    agentsHelpInChat: 'Ikseb għajnuna fiċ-chat',
    agentsHelpStarter: 'Għinni nikkonnettja aġent estern ma’ Poneva permezz ta’ MCP.',
    agentNoticeReady: '{app} qara l-għarfien ta’ Poneva. Il-konnessjoni hija lesta.',
    agentNoticeAuthorized:
      '{app} huwa awtorizzat. Agħmillu mistoqsija biex tlesti l-verifika tal-konnessjoni.',
    agentScopeUnconfirmed:
      'Ma stajniex nikkonfermaw il-bidla għal {app}. Id-dettalji hawn taħt juru x’għandu Poneva issa.',
    agentScopeNotChanged: 'L-aċċess ta’ {app} ma nbidilx. Erġa’ pprova.',
    agentConfirmRevokeKey:
      'Tirrevoka ċ-ċavetta tal-aċċess “{app}”? Kull aġent li jużaha jieqaf jaqra l-għarfien ta’ {company} minnufih.',
    agentConfirmDisconnect:
      'Tiskonnettja {app}? {app} ma jkunx jista’ jaqra aktar l-għarfien ta’ {company}. Id-dħul tiegħek f’Poneva ma jintlaqatx.',
    agentKeyRevoked: 'Iċ-ċavetta tal-aċċess “{app}” ġiet revokata.',
    agentDisconnected: '{app} ġie skonnettjat.',
    agentDisconnectUnconfirmed:
      'Ma stajniex nikkonfermaw li {app} ġie skonnettjat. Il-lista turi l-istat attwali.',
    agentDisconnectFailed: '{app} għadu konness. Erġa’ pprova.',
    setupTitle: 'Ikkonnettja aġent estern',
    setupManualLabel: 'Jew issettjah int stess',
    setupAddTitle: 'Żid lil Poneva bħala server MCP',
    setupAddBody:
      'Fis-settings tal-MCP jew tal-konnetturi tal-aġent, żid server MCP remot (Streamable HTTP) bl-indirizz ta’ hawn fuq.',
    setupConfigToggle: 'Qed tuża fajl tal-konfigurazzjoni?',
    setupConfigBody: 'Aġenti li jniżżlu s-servers MCP tagħhom f’fajl JSON jieħdu dan:',
    setupSignInTitle: 'Idħol',
    setupSignInBody:
      'L-aġent jiftaħ Poneva fil-browser tiegħek. Iċċekkja l-kumpanija u x’jista’ jagħmel l-aġent, imbagħad agħżel Ippermetti.',
    setupSignInFallback:
      'Jekk it-tieqa tad-dħul tingħalaq waħedha jew qatt ma turi Poneva, jew jekk l-aġent jitlob token jew ċavetta API, uża ċavetta tal-aċċess minflok.',
    setupTryTitle: 'Ippruvah',
    setupTryAsk: 'Staqsi lill-aġent:',
    setupSampleQuestion: 'X’inhi d-direzzjoni attwali tal-prodott tagħna? Semmi s-sors.',
    setupTryNote: 'Wara l-ewwel qari tiegħu, jidher hawn bħala Lest.',
    setupPromptTitle: 'Ħalli l-aġent tal-kodifikazzjoni tiegħek jissettjah',
    setupPromptBody:
      'Waħħal dan il-prompt f’Claude Code, Codex jew aġent ieħor tal-kodifikazzjoni. Fih l-indirizz MCP tiegħek u jiggwida lill-aġent matul is-setup. Inti tapprova l-aċċess meta tidħol.',
    copyAction: 'Ikkopja',
    copiedAction: 'Ikkupjat',
    copyNamed: 'Ikkopja {what}',
    copiedNamed: 'Ikkupjat: {what}',
    copiedStatus: 'Ikkupjat: {what}.',
    copyManualHint:
      'L-ikkupjar mhux disponibbli f’dan il-browser. It-test huwa magħżul; ikkopjah bit-tastiera.',
    copyPromptShow: 'Ara l-prompt',
    copyPromptHide: 'Aħbi l-prompt',
    copyWhatAddress: 'l-indirizz MCP ta’ Poneva',
    copyWhatConfig: 'il-konfigurazzjoni tas-server MCP',
    copyWhatSample: 'il-mistoqsija ta’ eżempju',
    copyWhatSampleFor: 'il-mistoqsija ta’ eżempju għal {app}',
    copyWhatPrompt: 'il-prompt tas-setup',
    copyWhatKey: 'iċ-ċavetta tal-aċċess għal {app}',
    keyCreateAction: 'Oħloq ċavetta tal-aċċess',
    keySecret:
      'Ittrattaha bħal password. Waħħalha biss fis-settings tal-MCP, tal-konnetturi jew tas-sigrieti ta’ aġent, qatt f’sit web jew f’kodiċi li oħrajn jistgħu jiftħu.',
    keyFormNote:
      'Għal aġent li ma jistax jidħol. Taqra l-għarfien ta’ {company} f’ismek sakemm tiskadi jew tirrevokaha; tieqaf ukoll jekk tbiddel il-password jew titlaq mill-kumpanija.',
    keyName: 'Isem',
    keyNamePlaceholder: 'eż. Rapport ta’ billejl',
    keyLifetime: 'Tiskadi wara',
    keyDays: 'one:jum|two:ijiem|few:ijiem|many:jum|other:jum',
    keyAlsoAllow: 'Ippermetti wkoll',
    keyCreating: 'Qed tinħoloq…',
    keyCreate: 'Oħloq ċavetta',
    keyCancel: 'Ikkanċella',
    keyUnconfirmed:
      'Ma stajniex nikkonfermaw li ċ-ċavetta nħolqot. Jekk tidher fil-lista, irrevokaha u oħloq waħda ġdida.',
    keyNotCreated: 'Iċ-ċavetta tal-aċċess ma nħolqitx. Erġa’ pprova.',
    keyNewTitle: 'Ċavetta tal-aċċess għal {app}',
    keyCopyNow:
      'Ikkopjaha issa. Poneva jurihielek darba biss u jżomm biss marka tas-swaba’ tagħha.',
    keyTokenRule: 'Qasam {token} jew {apiKey} jieħu ċ-ċavetta biss.',
    keyTermToken: 'token',
    keyTermApiKey: 'ċavetta API',
    keyHeaderRule:
      'Qasam {header} jieħu l-isem {name} u l-valur {value}, biċ-ċavetta tiegħek minflok {key}.',
    keyTermHeader: 'intestatura',
    keyEndsOn:
      'Tieqaf taħdem nhar {date}, meta tirrevokaha, u meta tbiddel il-password jew titlaq mill-kumpanija.',
    keyEndsExpiry:
      'Tieqaf taħdem meta tiskadi, meta tirrevokaha, u meta tbiddel il-password jew titlaq mill-kumpanija.',
    keyDone: 'Lest',
    scopeProposeTitle: 'Issuġġerixxi aġġornamenti tal-għarfien',
    scopeProposeDescribe:
      '{app} jista’ jissuġġerixxi bidliet fil-paġni. Pubblikatur tal-kumpanija jirrevedi kull waħda qabel ma jinbidel xejn.',
    scopeProposeConfirmOn:
      'Tħalli lil {app} jissuġġerixxi aġġornamenti tal-għarfien ta’ {company}? Kull suġġeriment jistenna f’Għarfien → Għar-reviżjoni sakemm pubblikatur tal-kumpanija japprovah. Xejn ma jinbidel waħdu.',
    scopeProposeConfirmOff:
      'Twaqqaf lil {app} milli jissuġġerixxi aġġornamenti tal-għarfien? Is-suġġerimenti li diġà bagħat jibqgħu għar-reviżjoni.',
    scopeProposeEnabled:
      '{app} issa jista’ jissuġġerixxi aġġornamenti tal-għarfien. Jekk l-għażla għadha ma tidhirx f’{app}, erġa’ ibdih.',
    scopeProposeDisabled: '{app} ma jistax jibqa’ jissuġġerixxi aġġornamenti tal-għarfien.',
    agentReadAccess:
      'Aqra l-għarfien tal-kumpanija: Il-profil tal-kumpanija u l-paġni tal-għarfien approvati. Chats, fajls, laqgħat u memorja personali mhumiex inklużi.',
    agentHintKeyStale:
      'L-indirizz ta’ Poneva nbidel wara li saret din iċ-ċavetta, għalhekk Poneva jirrifjutaha. Irrevokaha u oħloq ċavetta ġdida.',
    agentHintStale:
      'L-indirizz ta’ Poneva nbidel wara li {app} ġie konness, għalhekk Poneva jirrifjutah. Skonnettjah, imbagħad erġa’ kkonnettja lil {app} bl-indirizz ta’ hawn fuq.',
    agentHintKeyWaiting: 'Jidher bħala Lest hekk kif aġent juża ċ-ċavetta.',
    agentHintWaiting: 'Agħmel mistoqsija lil {app} biex tlesti l-verifika tal-konnessjoni.',
    agentHintTryAsking: 'Ipprova staqsi:',
    agentFactCreated: 'Maħluq',
    agentFactConnected: 'Konness',
    agentFactExpires: 'Jiskadi',
    agentFactSignInEnds: 'Id-dħul jintemm',
    agentFactAccess: 'Aċċess',
    agentNameNote:
      'L-isem huwa dak li rrapporta l-aġent; Poneva ma jistax jivverifikah. Skonnettja kull aġent li ma tagħrafx.',
    agentSignInNote: 'Meta jintemm id-dħul tiegħu, l-aġent jitolbok tidħol mill-ġdid.',
    agentWorking: 'Qed jaħdem…',
    agentRevokeKey: 'Irrevoka ċ-ċavetta',
    agentDisconnect: 'Skonnettja',
    cardAgentsLabel: 'L-aġenti konnessi tiegħek',
    cardMore: 'u {more}',
    cardMoreCount:
      'one:ieħor f’Konnessjonijiet|two:oħra f’Konnessjonijiet|few:oħra f’Konnessjonijiet|many:oħra f’Konnessjonijiet|other:oħra f’Konnessjonijiet',
    cardOpenSetup: 'Iftaħ is-setup',
    cardChecking: 'Qed jiċċekkja l-aġenti esterni tiegħek…',
    cardTryAgain: 'Erġa’ pprova',
    connectedAgentsSubtitleAny:
      'Aġenti li jaqraw l-għarfien tal-kumpanija tiegħek permezz ta’ Poneva.',
    scopeLibraryReadTitle: 'Aqra x-xogħol tiegħek f’Poneva',
    scopeLibraryReadDescribe:
      '{app} jista’ jsib u jaqra ċ-chats, il-Librerija, il-fajls, il-laqgħat, il-Skills, l-aġenti u l-apps li tista’ tiftaħ f’Poneva, u jaqra mis-servizzi konnessi tiegħek. Jista’ wkoll jaqra l-assistenti tax-xiri tal-kumpanija u dak li x-xerrejja qalulhom.',
    scopeLibraryReadConfirmOn:
      'Tħalli lil {app} jaqra x-xogħol tiegħek f’Poneva f’{company}? Isib u jaqra dak li diġà tista’ tiftaħ f’Poneva, f’ismek.',
    scopeLibraryReadConfirmOff:
      'Twaqqaf lil {app} milli jaqra x-xogħol tiegħek f’Poneva? Imbagħad lanqas ma jkun jista’ jissejvja xogħol jew jistaqsi lil Poneva.',
    scopeLibraryReadEnabled:
      '{app} issa jista’ jaqra x-xogħol tiegħek f’Poneva. Jekk l-għażla għadha ma tidhirx f’{app}, erġa’ ibdih.',
    scopeLibraryReadDisabled: '{app} ma jistax jibqa’ jaqra x-xogħol tiegħek f’Poneva.',
    scopeLibraryWriteTitle: 'Issejvja x-xogħol u staqsi lil Poneva',
    scopeLibraryWriteDescribe:
      '{app} jista’ jissejvja xogħol fil-Librerija tiegħek, fejn xogħol ġdid jibqa’ privat għalik, u jissejvja verżjonijiet ġodda ta’ xogħol li tista’ teditja (il-verżjonijiet ta’ qabel jibqgħu fl-Istorja). Jista’ jissejvja abbozzi ta’ Skills u tad-disinji tal-assistenti tax-xiri tal-kumpanija, li jarawhom il-kollegi li jistgħu jeditjawhom; bniedem biss jippubblikahom f’Poneva. Jista’ wkoll jistaqsi lil Poneva f’chats privati, li juża l-krediti. Jista’ wkoll jibda qari tal-ħanut onlajn tal-kumpanija biex isewwi assistent tax-xiri u jagħmillu mistoqsijiet tat-test; it-tnejn jużaw krediti tal-kumpanija.',
    scopeLibraryWriteConfirmOn:
      'Tħalli lil {app} jissejvja xogħol fil-Librerija tiegħek f’{company}, inklużi verżjonijiet ġodda ta’ xogħol li tista’ teditja u abbozzi li jistgħu jarawhom il-kollegi, u jistaqsi lil Poneva għalik? Il-mistoqsijiet jużaw il-krediti. Jista’ wkoll jaqra x-xogħol tiegħek f’Poneva. Il-qari tal-ħanut u l-mistoqsijiet tat-test lill-assistenti tax-xiri jużaw ukoll krediti tal-kumpanija.',
    scopeLibraryWriteConfirmOff:
      'Twaqqaf lil {app} milli jissejvja xogħol u jistaqsi lil Poneva? Dak li diġà ssejvja jibqa’ fil-Librerija tiegħek.',
    scopeLibraryWriteEnabled:
      '{app} issa jista’ jissejvja xogħol u jistaqsi lil Poneva. Jekk l-għażla għadha ma tidhirx f’{app}, erġa’ ibdih.',
    scopeLibraryWriteDisabled: '{app} ma jistax jibqa’ jissejvja xogħol jew jistaqsi lil Poneva.',
  },
  nl: {
    mcpServersTitle: 'Eigen verbindingen',
    mcpServersSubtitle:
      'Verbind je eigen toolserver. Poneva vraagt het eerst voordat er iets verandert.',
    mcpServersApproval:
      'Je keurt elke wijziging goed. Voor lezen geldt wat je per server hebt gekozen.',
    mcpServersAdd: 'Verbinding toevoegen',
    connectedAgentsLink: 'Verbonden agents',
    accountConnectionsUnavailable:
      'Accountverbindingen zijn nog niet beschikbaar in deze werkruimte.',
    connectedAgentsTitle: 'Verbonden agents',
    connectedAgentsSubtitle: 'Agents die via Poneva de kennis van {company} lezen.',
    connectedAgentsSuggest:
      'Als je het toestaat, stellen ze wijzigingen voor die een persoon beoordeelt.',
    connectedAgentsList: 'Nu verbonden',
    connectingAgentsOff: 'Agents verbinden is nog niet beschikbaar voor deze werkruimte.',
    checkingConnectedAgents: 'Verbonden agents controleren…',
    connectedAgentsLoadFailed: 'Verbonden agents konden niet worden geladen.',
    connectedAgentsOpen: 'Verbonden agents openen',
    agentTimeNotYet: 'nog niet',
    agentTimeUnknown: 'op een onbekend moment',
    agentTimeJustNow: 'zojuist',
    agentTimeOnDate: 'op {date}',
    agentErrorNotFound: 'Deze agent is niet meer verbonden. De lijst toont de huidige status.',
    agentErrorScopeUnavailable: 'Die toegang is niet beschikbaar in deze werkruimte.',
    agentErrorTooManyKeys:
      'Je hebt het maximale aantal toegangssleutels dat Poneva toestaat. Trek er eerst een in.',
    agentErrorInvalidInput: 'Controleer de naam van de sleutel en probeer het opnieuw.',
    agentNotRecorded: 'Niet vastgelegd',
    agentMethodKey: 'Toegangssleutel',
    agentMethodSignedIn: 'Ingelogd',
    agentStateReconnect: 'Opnieuw verbinden nodig',
    agentStateReady: 'Klaar',
    agentStateKeyUnused: 'Aangemaakt — nog niet gebruikt',
    agentStateWaiting: 'Geautoriseerd — wacht op eerste leesactie',
    agentLastUsed: 'Laatst gebruikt {when}',
    agentKeyExpires: 'Toegangssleutel · verloopt op {date}',
    agentSignInEnds: 'Ingelogd · inlog verloopt op {date}',
    agentsNone: 'Nog geen externe agents verbonden',
    agentsConnectedCount: 'one:agent verbonden|other:agents verbonden',
    agentsWaitingCount: 'one:wachtend|other:wachtend',
    agentsReconnectCount: 'one:opnieuw te verbinden|other:opnieuw te verbinden',
    addressLocalNetwork:
      'Alleen bereikbaar op dit netwerk. Agents die in de cloud draaien, kunnen dit adres niet gebruiken.',
    addressLocalComputer:
      'Alleen bereikbaar vanaf deze computer. Agents die in de cloud draaien, kunnen dit adres niet gebruiken.',
    addressUnreachable:
      'Poneva kan dit adres nu niet bereiken, dus externe agents waarschijnlijk ook niet.',
    agentUnnamed: 'Naamloze agent',
    agentCompanyFallback: 'je bedrijf',
    agentErrorUnreadable:
      'Poneva gaf een antwoord dat deze pagina niet kon lezen. Probeer het opnieuw.',
    agentsLoadFailed: 'Je externe agents konden niet worden geladen.',
    agentsLoading: 'Externe agents laden…',
    agentsConnectedShort: 'one:verbonden|other:verbonden',
    agentsAddressTitle: 'MCP-adres',
    agentsPrivacy:
      'Agents handelen namens jou en zien alleen {company}. Ze lezen goedgekeurde kennis, nooit privéchats, persoonlijk geheugen of privébestanden. Al het andere blijft uit, tenzij je het toestaat.',
    agentsSetupHide: 'Instellen verbergen',
    agentsSetupAnother: 'Nog een agent verbinden',
    agentsHelpInChat: 'Hulp krijgen in de chat',
    agentsHelpStarter: 'Help me een externe agent via MCP met Poneva te verbinden.',
    agentNoticeReady: '{app} heeft Poneva-kennis gelezen. De verbinding is klaar.',
    agentNoticeAuthorized:
      '{app} is geautoriseerd. Stel een vraag om de controle van de verbinding af te ronden.',
    agentScopeUnconfirmed:
      'We konden de wijziging voor {app} niet bevestigen. Hieronder zie je wat Poneva nu heeft.',
    agentScopeNotChanged: 'De toegang van {app} is niet gewijzigd. Probeer het opnieuw.',
    agentConfirmRevokeKey:
      'Toegangssleutel “{app}” intrekken? Elke agent die deze sleutel gebruikt, kan de kennis van {company} meteen niet meer lezen.',
    agentConfirmDisconnect:
      'Verbinding met {app} verbreken? {app} kan de kennis van {company} dan niet meer lezen. Je inlog bij Poneva blijft ongewijzigd.',
    agentKeyRevoked: 'Toegangssleutel “{app}” is ingetrokken.',
    agentDisconnected: 'De verbinding met {app} is verbroken.',
    agentDisconnectUnconfirmed:
      'We konden niet bevestigen dat de verbinding met {app} is verbroken. De lijst toont de huidige status.',
    agentDisconnectFailed: '{app} is nog steeds verbonden. Probeer het opnieuw.',
    setupTitle: 'Een externe agent verbinden',
    setupManualLabel: 'Of stel het zelf in',
    setupAddTitle: 'Poneva als MCP-server toevoegen',
    setupAddBody:
      'Voeg in de MCP- of connectorinstellingen van de agent een externe MCP-server (Streamable HTTP) toe met het adres hierboven.',
    setupConfigToggle: 'Gebruik je een configuratiebestand?',
    setupConfigBody: 'Agents die hun MCP-servers in een JSON-bestand opgeven, gebruiken dit:',
    setupSignInTitle: 'Inloggen',
    setupSignInBody:
      'De agent opent Poneva in je browser. Controleer het bedrijf en wat de agent mag doen, en kies dan Toestaan.',
    setupSignInFallback:
      'Sluit het inlogvenster vanzelf, verschijnt Poneva er nooit in of vraagt de agent om een token of API-sleutel? Gebruik dan een toegangssleutel.',
    setupTryTitle: 'Uitproberen',
    setupTryAsk: 'Vraag de agent:',
    setupSampleQuestion: 'Wat is onze huidige productrichting? Vermeld de bron.',
    setupTryNote: 'Na de eerste leesactie staat de agent hier als Klaar.',
    setupPromptTitle: 'Laat je codeeragent het instellen',
    setupPromptBody:
      'Plak deze prompt in Claude Code, Codex of een andere codeeragent. Hij bevat je MCP-adres en begeleidt de agent bij het instellen. Je keurt de toegang goed wanneer je inlogt.',
    copyAction: 'Kopiëren',
    copiedAction: 'Gekopieerd',
    copyNamed: '{what} kopiëren',
    copiedNamed: '{what} gekopieerd',
    copiedStatus: '{what} gekopieerd.',
    copyManualHint:
      'Kopiëren is niet beschikbaar in deze browser. De tekst is geselecteerd; kopieer hem met je toetsenbord.',
    copyPromptShow: 'Prompt bekijken',
    copyPromptHide: 'Prompt verbergen',
    copyWhatAddress: 'MCP-adres van Poneva',
    copyWhatConfig: 'MCP-serverconfiguratie',
    copyWhatSample: 'Voorbeeldvraag',
    copyWhatSampleFor: 'Voorbeeldvraag voor {app}',
    copyWhatPrompt: 'Instelprompt',
    copyWhatKey: 'Toegangssleutel voor {app}',
    keyCreateAction: 'Toegangssleutel aanmaken',
    keySecret:
      'Behandel hem als een wachtwoord. Plak hem alleen in de MCP-, connector- of geheimeninstellingen van een agent, nooit in een website of code die anderen kunnen openen.',
    keyFormNote:
      'Voor een agent die niet kan inloggen. Hij leest namens jou de kennis van {company} tot hij verloopt of je hem intrekt. Hij stopt ook als je je wachtwoord wijzigt of het bedrijf verlaat.',
    keyName: 'Naam',
    keyNamePlaceholder: 'bijv. Nachtelijk rapport',
    keyLifetime: 'Verloopt na',
    keyDays: 'one:dag|other:dagen',
    keyAlsoAllow: 'Ook toestaan',
    keyCreating: 'Aanmaken…',
    keyCreate: 'Sleutel aanmaken',
    keyCancel: 'Annuleren',
    keyUnconfirmed:
      'We konden niet bevestigen dat de sleutel is aangemaakt. Staat hij in de lijst, trek hem dan in en maak een nieuwe aan.',
    keyNotCreated: 'De toegangssleutel is niet aangemaakt. Probeer het opnieuw.',
    keyNewTitle: 'Toegangssleutel voor {app}',
    keyCopyNow:
      'Kopieer hem nu. Poneva toont hem maar één keer en bewaart er alleen een vingerafdruk van.',
    keyTokenRule: 'In een veld {token} of {apiKey} komt alleen de sleutel.',
    keyTermToken: 'token',
    keyTermApiKey: 'API-sleutel',
    keyHeaderRule:
      'In een veld {header} komen de naam {name} en de waarde {value}, met je sleutel in plaats van {key}.',
    keyTermHeader: 'header',
    keyEndsOn:
      'Hij werkt niet meer vanaf {date}, als je hem intrekt en als je je wachtwoord wijzigt of het bedrijf verlaat.',
    keyEndsExpiry:
      'Hij werkt niet meer als hij verloopt, als je hem intrekt en als je je wachtwoord wijzigt of het bedrijf verlaat.',
    keyDone: 'Gereed',
    scopeProposeTitle: 'Kennisupdates voorstellen',
    scopeProposeDescribe:
      '{app} kan paginawijzigingen voorstellen. Een publicatiebeheerder van het bedrijf beoordeelt elk voorstel voordat er iets verandert.',
    scopeProposeConfirmOn:
      '{app} kennisupdates laten voorstellen voor {company}? Elk voorstel wacht in Kennis → Ter beoordeling tot een publicatiebeheerder van het bedrijf het goedkeurt. Er verandert niets vanzelf.',
    scopeProposeConfirmOff:
      '{app} geen kennisupdates meer laten voorstellen? Voorstellen die al zijn verstuurd, blijven ter beoordeling staan.',
    scopeProposeEnabled:
      '{app} kan nu kennisupdates voorstellen. Verschijnt de optie nog niet in {app}? Start het dan opnieuw.',
    scopeProposeDisabled: '{app} kan geen kennisupdates meer voorstellen.',
    agentReadAccess:
      'Bedrijfskennis lezen: het bedrijfsprofiel en de goedgekeurde kennispagina’s. Chats, bestanden, vergaderingen en persoonlijk geheugen vallen erbuiten.',
    agentHintKeyStale:
      'Het adres van Poneva is gewijzigd nadat deze sleutel is gemaakt, dus Poneva weigert hem. Trek hem in en maak een nieuwe sleutel aan.',
    agentHintStale:
      'Het adres van Poneva is gewijzigd nadat {app} verbinding maakte, dus Poneva weigert de verbinding. Verbreek de verbinding en verbind {app} daarna opnieuw met het adres hierboven.',
    agentHintKeyWaiting: 'Zodra een agent de sleutel gebruikt, staat deze hier als Klaar.',
    agentHintWaiting: 'Stel {app} een vraag om de controle van de verbinding af te ronden.',
    agentHintTryAsking: 'Vraag bijvoorbeeld:',
    agentFactCreated: 'Aangemaakt',
    agentFactConnected: 'Verbonden',
    agentFactExpires: 'Verloopt',
    agentFactSignInEnds: 'Inlog verloopt',
    agentFactAccess: 'Toegang',
    agentNameNote:
      'Dit is de naam die de agent heeft opgegeven; Poneva kan die niet controleren. Verbreek de verbinding met agents die je niet herkent.',
    agentSignInNote: 'Als de inlog verloopt, vraagt de agent je om opnieuw in te loggen.',
    agentWorking: 'Bezig…',
    agentRevokeKey: 'Sleutel intrekken',
    agentDisconnect: 'Verbinding verbreken',
    cardAgentsLabel: 'Je verbonden agents',
    cardMore: 'en nog {more}',
    cardMoreCount: 'one:in Verbindingen|other:in Verbindingen',
    cardOpenSetup: 'Instellen openen',
    cardChecking: 'Je externe agents controleren…',
    cardTryAgain: 'Opnieuw proberen',
    connectedAgentsSubtitleAny: 'Agents die via Poneva de kennis van je bedrijf lezen.',
    scopeLibraryReadTitle: 'Je werk in Poneva lezen',
    scopeLibraryReadDescribe:
      '{app} kan de chats, Bibliotheek-items, bestanden, vergaderingen, Skills, agents en apps vinden en lezen die je in Poneva kunt openen, en lezen uit je verbonden diensten. Het kan ook de winkelassistenten van het bedrijf lezen en wat kopers ertegen hebben gezegd.',
    scopeLibraryReadConfirmOn:
      '{app} je werk in Poneva laten lezen voor {company}? Het vindt en leest namens jou wat je in Poneva al kunt openen.',
    scopeLibraryReadConfirmOff:
      '{app} je werk in Poneva niet meer laten lezen? Dan kan het ook geen werk meer opslaan of Poneva vragen stellen.',
    scopeLibraryReadEnabled:
      '{app} kan nu je werk in Poneva lezen. Verschijnt de optie nog niet in {app}? Start het dan opnieuw.',
    scopeLibraryReadDisabled: '{app} kan je werk in Poneva niet meer lezen.',
    scopeLibraryWriteTitle: 'Werk opslaan en Poneva vragen stellen',
    scopeLibraryWriteDescribe:
      '{app} kan werk opslaan in je Bibliotheek, waar nieuw werk alleen voor jou zichtbaar blijft, en nieuwe versies opslaan van werk dat je kunt bewerken (eerdere versies blijven in Geschiedenis). Het kan concepten opslaan van Skills en van ontwerpen voor de winkelassistenten van het bedrijf. Collega’s die ze kunnen bewerken, zien die concepten; alleen een mens publiceert ze in Poneva. Het kan Poneva ook vragen stellen in privéchats, wat credits verbruikt. Het kan ook uitlezingen van de webwinkel van het bedrijf starten om een winkelassistent te herstellen en er testvragen aan stellen; beide gebruiken credits van het bedrijf.',
    scopeLibraryWriteConfirmOn:
      '{app} werk laten opslaan in je Bibliotheek voor {company}, waaronder nieuwe versies van werk dat je kunt bewerken en concepten die collega’s kunnen zien, en namens jou vragen laten stellen aan Poneva? Vragen stellen verbruikt credits. Het kan ook je werk in Poneva lezen. Uitlezingen van de winkel en testvragen aan winkelassistenten gebruiken ook credits van het bedrijf.',
    scopeLibraryWriteConfirmOff:
      '{app} geen werk meer laten opslaan en geen vragen meer laten stellen aan Poneva? Wat het al heeft opgeslagen, blijft in je Bibliotheek.',
    scopeLibraryWriteEnabled:
      '{app} kan nu werk opslaan en Poneva vragen stellen. Verschijnt de optie nog niet in {app}? Start het dan opnieuw.',
    scopeLibraryWriteDisabled: '{app} kan geen werk meer opslaan of Poneva vragen stellen.',
  },
  pl: {
    mcpServersTitle: 'Własne połączenia',
    mcpServersSubtitle: 'Połącz własny serwer narzędzi. Poneva pyta, zanim cokolwiek zmieni.',
    mcpServersApproval:
      'Zatwierdzasz każdą zmianę. Odczyt działa zgodnie z wyborem dokonanym dla każdego serwera.',
    mcpServersAdd: 'Dodaj połączenie',
    connectedAgentsLink: 'Połączeni agenci',
    accountConnectionsUnavailable:
      'Połączenia kont nie są jeszcze dostępne w tym obszarze roboczym.',
    connectedAgentsTitle: 'Połączeni agenci',
    connectedAgentsSubtitle: 'Agenci, którzy przez Poneva czytają wiedzę firmy {company}.',
    connectedAgentsSuggest:
      'Jeśli na to pozwolisz, proponują zmiany do sprawdzenia przez człowieka.',
    connectedAgentsList: 'Połączeni teraz',
    connectingAgentsOff: 'Łączenie agentów nie jest jeszcze dostępne w tym obszarze roboczym.',
    checkingConnectedAgents: 'Sprawdzanie połączonych agentów…',
    connectedAgentsLoadFailed: 'Nie udało się wczytać połączonych agentów.',
    connectedAgentsOpen: 'Otwórz Połączonych agentów',
    agentTimeNotYet: 'jeszcze nie',
    agentTimeUnknown: 'w nieznanym czasie',
    agentTimeJustNow: 'przed chwilą',
    agentTimeOnDate: 'dnia {date}',
    agentErrorNotFound: 'Ten agent nie jest już połączony. Lista pokazuje bieżący stan.',
    agentErrorScopeUnavailable: 'Ten dostęp nie jest dostępny w tym obszarze roboczym.',
    agentErrorTooManyKeys:
      'Masz już tyle kluczy dostępu, ile pozwala Poneva. Najpierw unieważnij jeden z nich.',
    agentErrorInvalidInput: 'Sprawdź nazwę klucza i spróbuj ponownie.',
    agentNotRecorded: 'Nie zarejestrowano',
    agentMethodKey: 'Klucz dostępu',
    agentMethodSignedIn: 'Zalogowano',
    agentStateReconnect: 'Wymaga ponownego połączenia',
    agentStateReady: 'Gotowy',
    agentStateKeyUnused: 'Utworzony — jeszcze nieużywany',
    agentStateWaiting: 'Autoryzowany — czeka na pierwszy odczyt',
    agentLastUsed: 'Ostatnio użyto {when}',
    agentKeyExpires: 'Klucz dostępu · wygasa {date}',
    agentSignInEnds: 'Zalogowano · logowanie wygasa {date}',
    agentsNone: 'Nie połączono jeszcze żadnych zewnętrznych agentów',
    agentsConnectedCount:
      'one:agent połączony|few:agenci połączeni|many:agentów połączonych|other:agenta połączonego',
    agentsWaitingCount: 'one:czeka|few:czekają|many:czeka|other:czeka',
    agentsReconnectCount:
      'one:do ponownego połączenia|few:do ponownego połączenia|many:do ponownego połączenia|other:do ponownego połączenia',
    addressLocalNetwork:
      'Dostępny tylko w tej sieci. Agenci działający w chmurze nie mogą używać tego adresu.',
    addressLocalComputer:
      'Dostępny tylko z tego komputera. Agenci działający w chmurze nie mogą używać tego adresu.',
    addressUnreachable:
      'Poneva nie może teraz połączyć się z tym adresem, więc zewnętrzni agenci prawdopodobnie też nie.',
    agentUnnamed: 'Agent bez nazwy',
    agentCompanyFallback: 'twojej firmy',
    agentErrorUnreadable:
      'Poneva zwróciła odpowiedź, której ta strona nie może odczytać. Spróbuj ponownie.',
    agentsLoadFailed: 'Nie udało się wczytać twoich zewnętrznych agentów.',
    agentsLoading: 'Wczytywanie zewnętrznych agentów…',
    agentsConnectedShort: 'one:połączony|few:połączeni|many:połączonych|other:połączonego',
    agentsAddressTitle: 'Adres MCP',
    agentsPrivacy:
      'Agenci działają w twoim imieniu i widzą wyłącznie dane {company}. Czytają zatwierdzoną wiedzę, nigdy prywatne czaty, pamięć osobistą ani prywatne pliki. Nic więcej nie jest włączone, dopóki na to nie pozwolisz.',
    agentsSetupHide: 'Ukryj konfigurację',
    agentsSetupAnother: 'Połącz kolejnego agenta',
    agentsHelpInChat: 'Uzyskaj pomoc na czacie',
    agentsHelpStarter: 'Pomóż mi połączyć zewnętrznego agenta z Poneva przez MCP.',
    agentNoticeReady: 'Agent {app} odczytał wiedzę z Poneva. Połączenie jest gotowe.',
    agentNoticeAuthorized:
      'Agent {app} ma autoryzację. Zadaj mu pytanie, aby dokończyć sprawdzanie połączenia.',
    agentScopeUnconfirmed:
      'Nie udało się potwierdzić zmiany dla {app}. Poniżej widać, co Poneva ma teraz zapisane.',
    agentScopeNotChanged: 'Dostęp agenta {app} nie zmienił się. Spróbuj ponownie.',
    agentConfirmRevokeKey:
      'Unieważnić klucz dostępu „{app}”? Każdy agent, który go używa, od razu przestanie czytać wiedzę {company}.',
    agentConfirmDisconnect:
      'Rozłączyć {app}? Agent {app} nie będzie już mógł czytać wiedzy {company}. Twoje logowanie w Poneva pozostanie bez zmian.',
    agentKeyRevoked: 'Klucz dostępu „{app}” został unieważniony.',
    agentDisconnected: 'Rozłączono {app}.',
    agentDisconnectUnconfirmed:
      'Nie udało się potwierdzić rozłączenia {app}. Lista pokazuje bieżący stan.',
    agentDisconnectFailed: 'Agent {app} jest nadal połączony. Spróbuj ponownie.',
    setupTitle: 'Połącz zewnętrznego agenta',
    setupManualLabel: 'Albo skonfiguruj to samodzielnie',
    setupAddTitle: 'Dodaj Poneva jako serwer MCP',
    setupAddBody:
      'W ustawieniach MCP lub łączników agenta dodaj zdalny serwer MCP (Streamable HTTP) z adresem podanym powyżej.',
    setupConfigToggle: 'Używasz pliku konfiguracyjnego?',
    setupConfigBody: 'Agenci, którzy zapisują swoje serwery MCP w pliku JSON, potrzebują tego:',
    setupSignInTitle: 'Zaloguj się',
    setupSignInBody:
      'Agent otworzy Poneva w przeglądarce. Sprawdź firmę i to, co agent może robić, a potem wybierz Zezwól.',
    setupSignInFallback:
      'Jeśli okno logowania samo się zamknie lub nie pokaże Poneva albo agent poprosi o token lub klucz API, użyj zamiast tego klucza dostępu.',
    setupTryTitle: 'Wypróbuj',
    setupTryAsk: 'Zapytaj agenta:',
    setupSampleQuestion: 'Jaki jest nasz obecny kierunek rozwoju produktu? Podaj źródło.',
    setupTryNote: 'Po pierwszym odczycie pojawi się tu jako Gotowy.',
    setupPromptTitle: 'Zleć konfigurację agentowi programistycznemu',
    setupPromptBody:
      'Wklej ten prompt do Claude Code, Codex lub innego agenta programistycznego. Zawiera twój adres MCP i prowadzi agenta przez konfigurację. Dostęp zatwierdzasz podczas logowania.',
    copyAction: 'Kopiuj',
    copiedAction: 'Skopiowano',
    copyNamed: 'Kopiuj {what}',
    copiedNamed: 'Skopiowano {what}',
    copiedStatus: 'Skopiowano {what}.',
    copyManualHint:
      'Kopiowanie nie jest dostępne w tej przeglądarce. Tekst jest zaznaczony; skopiuj go za pomocą klawiatury.',
    copyPromptShow: 'Pokaż prompt',
    copyPromptHide: 'Ukryj prompt',
    copyWhatAddress: 'adres MCP Poneva',
    copyWhatConfig: 'konfigurację serwera MCP',
    copyWhatSample: 'przykładowe pytanie',
    copyWhatSampleFor: 'przykładowe pytanie dla {app}',
    copyWhatPrompt: 'prompt konfiguracji',
    copyWhatKey: 'klucz dostępu dla {app}',
    keyCreateAction: 'Utwórz klucz dostępu',
    keySecret:
      'Traktuj go jak hasło. Wklejaj go tylko w ustawieniach MCP, łączników lub sekretów agenta, nigdy na stronie internetowej ani w kodzie, który mogą otworzyć inni.',
    keyFormNote:
      'Dla agenta, który nie może się zalogować. Czyta wiedzę {company} w twoim imieniu, dopóki klucz nie wygaśnie lub go nie unieważnisz; zmiana hasła lub odejście z firmy również go wyłącza.',
    keyName: 'Nazwa',
    keyNamePlaceholder: 'np. Nocny raport',
    keyLifetime: 'Wygasa po',
    keyDays: 'one:dniu|few:dniach|many:dniach|other:dnia',
    keyAlsoAllow: 'Zezwól również na',
    keyCreating: 'Tworzenie…',
    keyCreate: 'Utwórz klucz',
    keyCancel: 'Anuluj',
    keyUnconfirmed:
      'Nie udało się potwierdzić utworzenia klucza. Jeśli pojawi się na liście, unieważnij go i utwórz nowy.',
    keyNotCreated: 'Klucz dostępu nie został utworzony. Spróbuj ponownie.',
    keyNewTitle: 'Klucz dostępu dla {app}',
    keyCopyNow: 'Skopiuj go teraz. Poneva pokazuje go tylko raz i przechowuje jedynie jego odcisk.',
    keyTokenRule: 'W polu {token} lub {apiKey} wpisz sam klucz.',
    keyTermToken: 'token',
    keyTermApiKey: 'klucz API',
    keyHeaderRule:
      'W polu {header} wpisz nazwę {name} i wartość {value}, zastępując {key} swoim kluczem.',
    keyTermHeader: 'nagłówek',
    keyEndsOn:
      'Przestaje działać {date}, po unieważnieniu oraz po zmianie hasła lub odejściu z firmy.',
    keyEndsExpiry:
      'Przestaje działać po wygaśnięciu, po unieważnieniu oraz po zmianie hasła lub odejściu z firmy.',
    keyDone: 'Gotowe',
    scopeProposeTitle: 'Proponowanie aktualizacji wiedzy',
    scopeProposeDescribe:
      '{app} może proponować zmiany stron. Każdą z nich sprawdza osoba publikująca w firmie, zanim cokolwiek się zmieni.',
    scopeProposeConfirmOn:
      'Pozwolić {app} proponować aktualizacje wiedzy {company}? Każda propozycja czeka w sekcji Wiedza → Do sprawdzenia, aż zatwierdzi ją osoba publikująca w firmie. Nic nie zmienia się samo.',
    scopeProposeConfirmOff:
      'Wyłączyć proponowanie aktualizacji wiedzy przez {app}? Wysłane już propozycje pozostaną do sprawdzenia.',
    scopeProposeEnabled:
      '{app} może teraz proponować aktualizacje wiedzy. Jeśli opcja nie pojawia się jeszcze w {app}, uruchom go ponownie.',
    scopeProposeDisabled: '{app} nie może już proponować aktualizacji wiedzy.',
    agentReadAccess:
      'Odczyt wiedzy firmy: profil firmy i zatwierdzone strony wiedzy. Nie obejmuje czatów, plików, spotkań ani pamięci osobistej.',
    agentHintKeyStale:
      'Adres Poneva zmienił się po utworzeniu tego klucza, więc Poneva go odrzuca. Unieważnij go i utwórz nowy klucz.',
    agentHintStale:
      'Adres Poneva zmienił się po połączeniu {app}, więc Poneva go odrzuca. Rozłącz go, a następnie ponownie połącz {app}, używając adresu podanego powyżej.',
    agentHintKeyWaiting: 'Pojawi się jako Gotowy, gdy agent użyje klucza.',
    agentHintWaiting: 'Zadaj {app} pytanie, aby dokończyć sprawdzanie połączenia.',
    agentHintTryAsking: 'Zapytaj na przykład:',
    agentFactCreated: 'Utworzono',
    agentFactConnected: 'Połączono',
    agentFactExpires: 'Wygasa',
    agentFactSignInEnds: 'Logowanie wygasa',
    agentFactAccess: 'Dostęp',
    agentNameNote:
      'Nazwę podał sam agent; Poneva nie może jej zweryfikować. Rozłącz każdego agenta, którego nie rozpoznajesz.',
    agentSignInNote: 'Gdy logowanie wygaśnie, agent poprosi cię o ponowne zalogowanie.',
    agentWorking: 'Przetwarzanie…',
    agentRevokeKey: 'Unieważnij klucz',
    agentDisconnect: 'Rozłącz',
    cardAgentsLabel: 'Twoi połączeni agenci',
    cardMore: 'i jeszcze {more}',
    cardMoreCount:
      'one:w sekcji Połączenia|few:w sekcji Połączenia|many:w sekcji Połączenia|other:w sekcji Połączenia',
    cardOpenSetup: 'Otwórz konfigurację',
    cardChecking: 'Sprawdzanie twoich zewnętrznych agentów…',
    cardTryAgain: 'Spróbuj ponownie',
    connectedAgentsSubtitleAny: 'Agenci, którzy przez Poneva czytają wiedzę twojej firmy.',
    scopeLibraryReadTitle: 'Odczytywanie Twojej pracy w Poneva',
    scopeLibraryReadDescribe:
      '{app} może wyszukiwać i odczytywać czaty, Bibliotekę, pliki, spotkania, Skille, agentów i aplikacje, które możesz otworzyć w Poneva, a także odczytywać dane z Twoich połączonych usług. Może też czytać asystentów zakupowych firmy i to, co powiedzieli im kupujący.',
    scopeLibraryReadConfirmOn:
      'Pozwolić {app} odczytywać Twoją pracę w Poneva w firmie {company}? Wyszukuje i odczytuje to, co już możesz otworzyć w Poneva, działając jako Ty.',
    scopeLibraryReadConfirmOff:
      'Wyłączyć odczytywanie Twojej pracy w Poneva przez {app}? Nie będzie wtedy mógł też zapisywać pracy ani pytać Ponevy.',
    scopeLibraryReadEnabled:
      '{app} może teraz odczytywać Twoją pracę w Poneva. Jeśli opcja nie pojawia się jeszcze w {app}, uruchom go ponownie.',
    scopeLibraryReadDisabled: '{app} nie może już odczytywać Twojej pracy w Poneva.',
    scopeLibraryWriteTitle: 'Zapisywanie pracy i pytanie Ponevy',
    scopeLibraryWriteDescribe:
      '{app} może zapisywać pracę w Twojej Bibliotece, gdzie nowa praca pozostaje widoczna tylko dla Ciebie, oraz zapisywać nowe wersje pracy, którą możesz edytować (wcześniejsze wersje pozostają w sekcji Historia). Może zapisywać szkice Skilli i projektów asystentów zakupowych firmy, które widzą współpracownicy mogący je edytować; w Poneva publikuje je tylko człowiek. Może też pytać Ponevę w prywatnych czatach, co zużywa kredyty. Może też uruchamiać odczyty sklepu internetowego firmy, aby naprawić asystenta zakupowego, i zadawać mu pytania testowe; jedno i drugie zużywa kredyty firmy.',
    scopeLibraryWriteConfirmOn:
      'Pozwolić {app} zapisywać pracę w Twojej Bibliotece w firmie {company}, w tym nowe wersje pracy, którą możesz edytować, i szkice widoczne dla współpracowników, oraz pytać Ponevę w Twoim imieniu? Pytania zużywają kredyty. Będzie też mógł odczytywać Twoją pracę w Poneva. Odczyty sklepu i pytania testowe do asystentów zakupowych również zużywają kredyty firmy.',
    scopeLibraryWriteConfirmOff:
      'Wyłączyć zapisywanie pracy i pytanie Ponevy przez {app}? To, co już zapisał, pozostanie w Twojej Bibliotece.',
    scopeLibraryWriteEnabled:
      '{app} może teraz zapisywać pracę i pytać Ponevę. Jeśli opcja nie pojawia się jeszcze w {app}, uruchom go ponownie.',
    scopeLibraryWriteDisabled: '{app} nie może już zapisywać pracy ani pytać Ponevy.',
  },
  pt: {
    mcpServersTitle: 'Ligações personalizadas',
    mcpServersSubtitle:
      'Ligue o seu próprio servidor de ferramentas. O Poneva pergunta antes de alterar o que quer que seja.',
    mcpServersApproval:
      'Aprova cada alteração. Para a leitura, aplica-se o que escolheu para cada servidor.',
    mcpServersAdd: 'Adicionar ligação',
    connectedAgentsLink: 'Agentes ligados',
    accountConnectionsUnavailable:
      'As ligações de contas ainda não estão disponíveis neste espaço de trabalho.',
    connectedAgentsTitle: 'Agentes ligados',
    connectedAgentsSubtitle: 'Agentes que leem o conhecimento de {company} através do Poneva.',
    connectedAgentsSuggest: 'Se o permitir, sugerem atualizações para uma pessoa rever.',
    connectedAgentsList: 'Ligados agora',
    connectingAgentsOff: 'A ligação de agentes ainda não está disponível neste espaço de trabalho.',
    checkingConnectedAgents: 'A verificar os agentes ligados…',
    connectedAgentsLoadFailed: 'Não foi possível carregar os agentes ligados.',
    connectedAgentsOpen: 'Abrir Agentes ligados',
    agentTimeNotYet: 'ainda não',
    agentTimeUnknown: 'num momento desconhecido',
    agentTimeJustNow: 'agora mesmo',
    agentTimeOnDate: 'em {date}',
    agentErrorNotFound: 'Este agente já não está ligado. A lista mostra o estado atual.',
    agentErrorScopeUnavailable: 'Esse acesso não está disponível neste espaço de trabalho.',
    agentErrorTooManyKeys:
      'Já tem o número máximo de chaves de acesso que o Poneva permite. Revogue uma primeiro.',
    agentErrorInvalidInput: 'Verifique o nome da chave e tente novamente.',
    agentNotRecorded: 'Não registado',
    agentMethodKey: 'Chave de acesso',
    agentMethodSignedIn: 'Sessão iniciada',
    agentStateReconnect: 'É necessário voltar a ligar',
    agentStateReady: 'Pronto',
    agentStateKeyUnused: 'Criada — ainda não utilizada',
    agentStateWaiting: 'Autorizado — a aguardar a primeira leitura',
    agentLastUsed: 'Última utilização {when}',
    agentKeyExpires: 'Chave de acesso · expira em {date}',
    agentSignInEnds: 'Sessão iniciada · a sessão termina em {date}',
    agentsNone: 'Ainda não há agentes externos ligados',
    agentsConnectedCount: 'one:agente ligado|other:agentes ligados',
    agentsWaitingCount: 'one:em espera|other:em espera',
    agentsReconnectCount: 'one:por voltar a ligar|other:por voltar a ligar',
    addressLocalNetwork:
      'Só acessível nesta rede. Os agentes executados na nuvem não podem utilizar este endereço.',
    addressLocalComputer:
      'Só acessível a partir deste computador. Os agentes executados na nuvem não podem utilizar este endereço.',
    addressUnreachable:
      'O Poneva não consegue aceder a este endereço neste momento, por isso os agentes externos provavelmente também não.',
    agentUnnamed: 'Agente sem nome',
    agentCompanyFallback: 'sua empresa',
    agentErrorUnreadable:
      'O Poneva devolveu uma resposta que esta página não conseguiu ler. Tente novamente.',
    agentsLoadFailed: 'Não foi possível carregar os seus agentes externos.',
    agentsLoading: 'A carregar os agentes externos…',
    agentsConnectedShort: 'one:ligado|many:ligados|other:ligados',
    agentsAddressTitle: 'Endereço MCP',
    agentsPrivacy:
      'Os agentes agem em seu nome e só veem {company}. Leem conhecimento aprovado, nunca conversas privadas, memória pessoal ou ficheiros privados. Tudo o resto fica desativado, a menos que o permita.',
    agentsSetupHide: 'Ocultar configuração',
    agentsSetupAnother: 'Ligar outro agente',
    agentsHelpInChat: 'Obter ajuda no chat',
    agentsHelpStarter: 'Ajuda-me a ligar um agente externo ao Poneva através de MCP.',
    agentNoticeReady: '{app} leu conhecimento do Poneva. A ligação está pronta.',
    agentNoticeAuthorized:
      '{app} está autorizado. Faça-lhe uma pergunta para concluir a verificação da ligação.',
    agentScopeUnconfirmed:
      'Não foi possível confirmar a alteração para {app}. Os detalhes abaixo mostram o que o Poneva tem agora.',
    agentScopeNotChanged: 'O acesso de {app} não foi alterado. Tente novamente.',
    agentConfirmRevokeKey:
      'Revogar a chave de acesso «{app}»? Qualquer agente que a utilize deixa imediatamente de ler o conhecimento de {company}.',
    agentConfirmDisconnect:
      'Desligar {app}? {app} deixará de poder ler o conhecimento de {company}. A sua sessão no Poneva não é afetada.',
    agentKeyRevoked: 'A chave de acesso «{app}» foi revogada.',
    agentDisconnected: '{app} foi desligado.',
    agentDisconnectUnconfirmed:
      'Não foi possível confirmar que {app} foi desligado. A lista mostra o estado atual.',
    agentDisconnectFailed: '{app} continua ligado. Tente novamente.',
    setupTitle: 'Ligar um agente externo',
    setupManualLabel: 'Ou configure manualmente',
    setupAddTitle: 'Adicionar o Poneva como servidor MCP',
    setupAddBody:
      'Nas definições de MCP ou de conectores do agente, adicione um servidor MCP remoto (Streamable HTTP) com o endereço acima.',
    setupConfigToggle: 'Usa um ficheiro de configuração?',
    setupConfigBody: 'Os agentes que indicam os seus servidores MCP num ficheiro JSON usam isto:',
    setupSignInTitle: 'Iniciar sessão',
    setupSignInBody:
      'O agente abre o Poneva no seu navegador. Verifique a empresa e o que o agente pode fazer e, em seguida, escolha Permitir.',
    setupSignInFallback:
      'Se a janela de início de sessão se fechar sozinha ou nunca mostrar o Poneva, ou se o agente pedir um token ou uma chave de API, use antes uma chave de acesso.',
    setupTryTitle: 'Experimentar',
    setupTryAsk: 'Pergunte ao agente:',
    setupSampleQuestion: 'Qual é a nossa direção de produto atual? Indica a fonte.',
    setupTryNote: 'Aparece aqui como Pronto após a primeira leitura.',
    setupPromptTitle: 'Deixe o seu agente de programação configurá-lo',
    setupPromptBody:
      'Cole este prompt no Claude Code, no Codex ou noutro agente de programação. Inclui o seu endereço MCP e orienta o agente durante a configuração. Aprova o acesso quando inicia sessão.',
    copyAction: 'Copiar',
    copiedAction: 'Copiado',
    copyNamed: 'Copiar {what}',
    copiedNamed: 'Copiado: {what}',
    copiedStatus: 'Copiado: {what}.',
    copyManualHint:
      'Não é possível copiar neste navegador. O texto está selecionado; copie-o com o teclado.',
    copyPromptShow: 'Ver prompt',
    copyPromptHide: 'Ocultar prompt',
    copyWhatAddress: 'endereço MCP do Poneva',
    copyWhatConfig: 'configuração do servidor MCP',
    copyWhatSample: 'pergunta de exemplo',
    copyWhatSampleFor: 'pergunta de exemplo para {app}',
    copyWhatPrompt: 'prompt de configuração',
    copyWhatKey: 'chave de acesso de {app}',
    keyCreateAction: 'Criar uma chave de acesso',
    keySecret:
      'Trate-a como uma palavra-passe. Cole-a apenas nas definições de MCP, de conectores ou de segredos de um agente, nunca num site ou em código que outras pessoas possam abrir.',
    keyFormNote:
      'Para um agente que não consegue iniciar sessão. Lê o conhecimento de {company} em seu nome até expirar ou até a revogar; alterar a palavra-passe ou sair da empresa também a desativa.',
    keyName: 'Nome',
    keyNamePlaceholder: 'p. ex. Relatório noturno',
    keyLifetime: 'Expira após',
    keyDays: 'one:dia|many:dias|other:dias',
    keyAlsoAllow: 'Permitir também',
    keyCreating: 'A criar…',
    keyCreate: 'Criar chave',
    keyCancel: 'Cancelar',
    keyUnconfirmed:
      'Não foi possível confirmar que a chave foi criada. Se aparecer na lista, revogue-a e crie uma nova.',
    keyNotCreated: 'A chave de acesso não foi criada. Tente novamente.',
    keyNewTitle: 'Chave de acesso de {app}',
    keyCopyNow:
      'Copie-a agora. O Poneva só a mostra uma vez e guarda apenas uma impressão digital da mesma.',
    keyTokenRule: 'Num campo {token} ou {apiKey}, introduza apenas a chave.',
    keyTermToken: 'token',
    keyTermApiKey: 'chave de API',
    keyHeaderRule:
      'Num campo {header}, introduza o nome {name} e o valor {value}, com a sua chave no lugar de {key}.',
    keyTermHeader: 'cabeçalho',
    keyEndsOn:
      'Deixa de funcionar em {date}, quando a revogar e quando alterar a palavra-passe ou sair da empresa.',
    keyEndsExpiry:
      'Deixa de funcionar quando expirar, quando a revogar e quando alterar a palavra-passe ou sair da empresa.',
    keyDone: 'Concluído',
    scopeProposeTitle: 'Sugerir atualizações de conhecimento',
    scopeProposeDescribe:
      '{app} pode sugerir alterações a páginas. Um publicador da empresa revê cada uma antes de qualquer alteração.',
    scopeProposeConfirmOn:
      'Permitir que {app} sugira atualizações de conhecimento para {company}? Cada sugestão aguarda em Conhecimento → Para rever até que um publicador da empresa a aprove. Nada muda sozinho.',
    scopeProposeConfirmOff:
      'Impedir que {app} sugira atualizações de conhecimento? As sugestões já enviadas continuam em revisão.',
    scopeProposeEnabled:
      '{app} já pode sugerir atualizações de conhecimento. Se a opção ainda não aparecer em {app}, reinicie-o.',
    scopeProposeDisabled: '{app} já não pode sugerir atualizações de conhecimento.',
    agentReadAccess:
      'Ler o conhecimento da empresa: o perfil da empresa e as páginas de conhecimento aprovadas. Não inclui conversas, ficheiros, reuniões nem memória pessoal.',
    agentHintKeyStale:
      'O endereço do Poneva mudou depois de esta chave ser criada, por isso o Poneva recusa-a. Revogue-a e crie uma nova chave.',
    agentHintStale:
      'O endereço do Poneva mudou depois de {app} se ligar, por isso o Poneva recusa-o. Desligue-o e volte a ligar {app} com o endereço acima.',
    agentHintKeyWaiting: 'Aparece como Pronto quando um agente usar a chave.',
    agentHintWaiting: 'Faça uma pergunta a {app} para concluir a verificação da ligação.',
    agentHintTryAsking: 'Experimente perguntar:',
    agentFactCreated: 'Criado',
    agentFactConnected: 'Ligado',
    agentFactExpires: 'Expira',
    agentFactSignInEnds: 'A sessão termina',
    agentFactAccess: 'Acesso',
    agentNameNote:
      'O nome é o que o agente indicou; o Poneva não o pode verificar. Desligue qualquer agente que não reconheça.',
    agentSignInNote: 'Quando a sessão terminar, o agente pede-lhe que inicie sessão novamente.',
    agentWorking: 'A processar…',
    agentRevokeKey: 'Revogar chave',
    agentDisconnect: 'Desligar',
    cardAgentsLabel: 'Os seus agentes ligados',
    cardMore: 'e mais {more}',
    cardMoreCount: 'one:em Ligações|many:em Ligações|other:em Ligações',
    cardOpenSetup: 'Abrir configuração',
    cardChecking: 'A verificar os seus agentes externos…',
    cardTryAgain: 'Tentar novamente',
    connectedAgentsSubtitleAny: 'Agentes que leem o conhecimento da sua empresa através do Poneva.',
    scopeLibraryReadTitle: 'Ler o seu trabalho na Poneva',
    scopeLibraryReadDescribe:
      '{app} pode encontrar e ler os chats, a Biblioteca, os ficheiros, as reuniões, as Skills, os agentes e as Apps que pode abrir na Poneva, e ler dos seus serviços ligados. Também pode ler os assistentes de compras da empresa e o que os compradores lhes disseram.',
    scopeLibraryReadConfirmOn:
      'Permitir que {app} leia o seu trabalho na Poneva em {company}? Encontra e lê o que já pode abrir na Poneva, em seu nome.',
    scopeLibraryReadConfirmOff:
      'Impedir que {app} leia o seu trabalho na Poneva? Deixa também de poder guardar trabalho ou perguntar à Poneva.',
    scopeLibraryReadEnabled:
      '{app} já pode ler o seu trabalho na Poneva. Se a opção ainda não aparecer em {app}, reinicie-o.',
    scopeLibraryReadDisabled: '{app} já não pode ler o seu trabalho na Poneva.',
    scopeLibraryWriteTitle: 'Guardar trabalho e perguntar à Poneva',
    scopeLibraryWriteDescribe:
      '{app} pode guardar trabalho na sua Biblioteca, onde o trabalho novo fica visível só para si, e guardar novas versões de trabalho que pode editar (as versões anteriores ficam no Histórico). Pode guardar rascunhos de Skills e de designs dos assistentes de compras da empresa, visíveis para os colegas que os podem editar; só uma pessoa os publica na Poneva. Também pode perguntar à Poneva em chats privados, o que usa créditos. Também pode iniciar leituras da loja online da empresa para reparar um assistente de compras e fazer-lhe perguntas de teste; ambas usam créditos da empresa.',
    scopeLibraryWriteConfirmOn:
      'Permitir que {app} guarde trabalho na sua Biblioteca em {company}, incluindo novas versões de trabalho que pode editar e rascunhos visíveis para os colegas, e pergunte à Poneva por si? As perguntas usam créditos. Também pode ler o seu trabalho na Poneva. As leituras da loja e as perguntas de teste aos assistentes de compras também usam créditos da empresa.',
    scopeLibraryWriteConfirmOff:
      'Impedir que {app} guarde trabalho e pergunte à Poneva? O que já guardou fica na sua Biblioteca.',
    scopeLibraryWriteEnabled:
      '{app} já pode guardar trabalho e perguntar à Poneva. Se a opção ainda não aparecer em {app}, reinicie-o.',
    scopeLibraryWriteDisabled: '{app} já não pode guardar trabalho nem perguntar à Poneva.',
  },
  ro: {
    mcpServersTitle: 'Conexiuni personalizate',
    mcpServersSubtitle:
      'Conectează-ți propriul server de instrumente. Poneva întreabă înainte să schimbe ceva.',
    mcpServersApproval:
      'Aprobi fiecare modificare. Pentru citire se aplică ce ai ales pentru fiecare server.',
    mcpServersAdd: 'Adaugă conexiune',
    connectedAgentsLink: 'Agenți conectați',
    accountConnectionsUnavailable:
      'Conexiunile de cont nu sunt încă disponibile în acest spațiu de lucru.',
    connectedAgentsTitle: 'Agenți conectați',
    connectedAgentsSubtitle: 'Agenți care citesc cunoștințele {company} prin Poneva.',
    connectedAgentsSuggest: 'Dacă permiți, propun actualizări pe care le verifică o persoană.',
    connectedAgentsList: 'Conectați acum',
    connectingAgentsOff:
      'Conectarea agenților nu este încă disponibilă pentru acest spațiu de lucru.',
    checkingConnectedAgents: 'Se verifică agenții conectați…',
    connectedAgentsLoadFailed: 'Agenții conectați nu au putut fi încărcați.',
    connectedAgentsOpen: 'Deschide Agenți conectați',
    agentTimeNotYet: 'încă nu',
    agentTimeUnknown: 'la un moment necunoscut',
    agentTimeJustNow: 'chiar acum',
    agentTimeOnDate: 'pe {date}',
    agentErrorNotFound: 'Acest agent nu mai este conectat. Lista arată starea actuală.',
    agentErrorScopeUnavailable: 'Acest acces nu este disponibil în acest spațiu de lucru.',
    agentErrorTooManyKeys:
      'Aveți atâtea chei de acces câte permite Poneva. Revocați mai întâi una.',
    agentErrorInvalidInput: 'Verificați numele cheii și încercați din nou.',
    agentNotRecorded: 'Neînregistrat',
    agentMethodKey: 'Cheie de acces',
    agentMethodSignedIn: 'Autentificat',
    agentStateReconnect: 'Necesită reconectare',
    agentStateReady: 'Pregătit',
    agentStateKeyUnused: 'Creat — încă nefolosit',
    agentStateWaiting: 'Autorizat — se așteaptă prima citire',
    agentLastUsed: 'Folosit ultima dată {when}',
    agentKeyExpires: 'Cheie de acces · expiră pe {date}',
    agentSignInEnds: 'Autentificat · autentificarea expiră pe {date}',
    agentsNone: 'Niciun agent extern conectat încă',
    agentsConnectedCount: 'one:agent conectat|few:agenți conectați|other:de agenți conectați',
    agentsWaitingCount: 'one:în așteptare|few:în așteptare|other:în așteptare',
    agentsReconnectCount: 'one:de reconectat|few:de reconectat|other:de reconectat',
    addressLocalNetwork:
      'Accesibilă doar în această rețea. Agenții care rulează în cloud nu pot folosi această adresă.',
    addressLocalComputer:
      'Accesibilă doar de pe acest computer. Agenții care rulează în cloud nu pot folosi această adresă.',
    addressUnreachable:
      'Poneva nu poate accesa această adresă acum, deci probabil nici agenții externi nu pot.',
    agentUnnamed: 'Agent fără nume',
    agentCompanyFallback: 'companiei dvs.',
    agentErrorUnreadable:
      'Poneva a trimis un răspuns pe care această pagină nu l-a putut citi. Încercați din nou.',
    agentsLoadFailed: 'Agenții dvs. externi nu au putut fi încărcați.',
    agentsLoading: 'Se încarcă agenții externi…',
    agentsConnectedShort: 'one:conectat|few:conectați|other:conectați',
    agentsAddressTitle: 'Adresă MCP',
    agentsPrivacy:
      'Agenții acționează în numele dvs. și văd doar datele {company}. Citesc cunoștințe aprobate, niciodată chaturi private, memoria personală sau fișiere private. Orice altceva rămâne dezactivat, dacă nu permiteți.',
    agentsSetupHide: 'Ascunde configurarea',
    agentsSetupAnother: 'Conectează alt agent',
    agentsHelpInChat: 'Cere ajutor în chat',
    agentsHelpStarter: 'Ajută-mă să conectez un agent extern la Poneva prin MCP.',
    agentNoticeReady: '{app} a citit cunoștințe din Poneva. Conexiunea este pregătită.',
    agentNoticeAuthorized:
      '{app} este autorizat. Puneți-i o întrebare pentru a finaliza verificarea conexiunii.',
    agentScopeUnconfirmed:
      'Nu am putut confirma modificarea pentru {app}. Detaliile de mai jos arată ce are Poneva acum.',
    agentScopeNotChanged: 'Accesul pentru {app} nu s-a modificat. Încercați din nou.',
    agentConfirmRevokeKey:
      'Revocați cheia de acces „{app}”? Orice agent care o folosește încetează imediat să citească cunoștințele {company}.',
    agentConfirmDisconnect:
      'Deconectați {app}? {app} nu va mai putea citi cunoștințele {company}. Autentificarea dvs. în Poneva nu este afectată.',
    agentKeyRevoked: 'Cheia de acces „{app}” a fost revocată.',
    agentDisconnected: '{app} a fost deconectat.',
    agentDisconnectUnconfirmed:
      'Nu am putut confirma că {app} a fost deconectat. Lista arată starea actuală.',
    agentDisconnectFailed: '{app} este încă conectat. Încercați din nou.',
    setupTitle: 'Conectează un agent extern',
    setupManualLabel: 'Sau configurați singur',
    setupAddTitle: 'Adaugă Poneva ca server MCP',
    setupAddBody:
      'În setările MCP sau de conectori ale agentului, adăugați un server MCP la distanță (Streamable HTTP) cu adresa de mai sus.',
    setupConfigToggle: 'Folosiți un fișier de configurare?',
    setupConfigBody: 'Agenții care își listează serverele MCP într-un fișier JSON folosesc asta:',
    setupSignInTitle: 'Autentificare',
    setupSignInBody:
      'Agentul deschide Poneva în browser. Verificați compania și ce poate face agentul, apoi alegeți Permite.',
    setupSignInFallback:
      'Dacă fereastra de autentificare se închide singură sau nu afișează niciodată Poneva ori dacă agentul cere un token sau o cheie API, folosiți în schimb o cheie de acces.',
    setupTryTitle: 'Încearcă',
    setupTryAsk: 'Întrebați agentul:',
    setupSampleQuestion: 'Care este direcția actuală a produsului nostru? Citează sursa.',
    setupTryNote: 'Apare aici ca Pregătit după prima citire.',
    setupPromptTitle: 'Lăsați agentul de programare să facă configurarea',
    setupPromptBody:
      'Lipiți acest prompt în Claude Code, Codex sau alt agent de programare. Conține adresa dvs. MCP și ghidează agentul prin configurare. Aprobați accesul când vă autentificați.',
    copyAction: 'Copiază',
    copiedAction: 'Copiat',
    copyNamed: 'Copiază {what}',
    copiedNamed: 'S-a copiat {what}',
    copiedStatus: 'S-a copiat {what}.',
    copyManualHint:
      'Copierea nu este disponibilă în acest browser. Textul este selectat; copiați-l cu tastatura.',
    copyPromptShow: 'Vezi promptul',
    copyPromptHide: 'Ascunde promptul',
    copyWhatAddress: 'adresa MCP Poneva',
    copyWhatConfig: 'configurația serverului MCP',
    copyWhatSample: 'întrebarea de exemplu',
    copyWhatSampleFor: 'întrebarea de exemplu pentru {app}',
    copyWhatPrompt: 'promptul de configurare',
    copyWhatKey: 'cheia de acces pentru {app}',
    keyCreateAction: 'Creează o cheie de acces',
    keySecret:
      'Tratați-o ca pe o parolă. Lipiți-o doar în setările MCP, de conectori sau de secrete ale unui agent, niciodată pe un site web sau în cod pe care îl pot deschide alții.',
    keyFormNote:
      'Pentru un agent care nu se poate autentifica. Citește cunoștințele {company} în numele dvs. până expiră sau până o revocați; schimbarea parolei sau plecarea din companie o dezactivează, de asemenea.',
    keyName: 'Nume',
    keyNamePlaceholder: 'ex.: Raport de noapte',
    keyLifetime: 'Expiră după',
    keyDays: 'one:zi|few:zile|other:de zile',
    keyAlsoAllow: 'Permite și',
    keyCreating: 'Se creează…',
    keyCreate: 'Creează cheia',
    keyCancel: 'Anulează',
    keyUnconfirmed:
      'Nu am putut confirma crearea cheii. Dacă apare în listă, revocați-o și creați una nouă.',
    keyNotCreated: 'Cheia de acces nu a fost creată. Încercați din nou.',
    keyNewTitle: 'Cheie de acces pentru {app}',
    keyCopyNow: 'Copiați-o acum. Poneva o afișează o singură dată și stochează doar amprenta ei.',
    keyTokenRule: 'Într-un câmp {token} sau {apiKey} se introduce doar cheia.',
    keyTermToken: 'token',
    keyTermApiKey: 'cheie API',
    keyHeaderRule:
      'Într-un câmp {header} se introduc numele {name} și valoarea {value}, cu cheia dvs. în locul lui {key}.',
    keyTermHeader: 'antet',
    keyEndsOn:
      'Nu mai funcționează începând cu {date}, când o revocați și când vă schimbați parola sau părăsiți compania.',
    keyEndsExpiry:
      'Nu mai funcționează când expiră, când o revocați și când vă schimbați parola sau părăsiți compania.',
    keyDone: 'Gata',
    scopeProposeTitle: 'Propune actualizări de cunoștințe',
    scopeProposeDescribe:
      '{app} poate propune modificări ale paginilor. Un publicator din companie verifică fiecare propunere înainte să se modifice ceva.',
    scopeProposeConfirmOn:
      'Permiteți ca {app} să propună actualizări ale cunoștințelor {company}? Fiecare propunere așteaptă în Cunoștințe → De verificat până când un publicator din companie o aprobă. Nimic nu se modifică de la sine.',
    scopeProposeConfirmOff:
      'Opriți propunerile de actualizări de cunoștințe de la {app}? Propunerile trimise deja rămân în verificare.',
    scopeProposeEnabled:
      '{app} poate acum propune actualizări de cunoștințe. Dacă opțiunea nu apare încă în {app}, reporniți-l.',
    scopeProposeDisabled: '{app} nu mai poate propune actualizări de cunoștințe.',
    agentReadAccess:
      'Citire cunoștințe companie: profilul companiei și paginile de cunoștințe aprobate. Chaturile, fișierele, întâlnirile și memoria personală nu sunt incluse.',
    agentHintKeyStale:
      'Adresa Poneva s-a schimbat după crearea acestei chei, așa că Poneva o refuză. Revocați-o și creați o cheie nouă.',
    agentHintStale:
      'Adresa Poneva s-a schimbat după ce {app} s-a conectat, așa că Poneva îl refuză. Deconectați-l, apoi conectați din nou {app} cu adresa de mai sus.',
    agentHintKeyWaiting: 'Apare ca Pregătit după ce un agent folosește cheia.',
    agentHintWaiting: 'Adresați o întrebare în {app} pentru a finaliza verificarea conexiunii.',
    agentHintTryAsking: 'Încercați să întrebați:',
    agentFactCreated: 'Creat',
    agentFactConnected: 'Conectat',
    agentFactExpires: 'Expiră',
    agentFactSignInEnds: 'Autentificarea expiră',
    agentFactAccess: 'Acces',
    agentNameNote:
      'Numele este cel raportat de agent; Poneva nu îl poate verifica. Deconectați orice agent pe care nu îl recunoașteți.',
    agentSignInNote: 'Când autentificarea expiră, agentul vă cere să vă autentificați din nou.',
    agentWorking: 'Se procesează…',
    agentRevokeKey: 'Revocă cheia',
    agentDisconnect: 'Deconectează',
    cardAgentsLabel: 'Agenții dvs. conectați',
    cardMore: 'și încă {more}',
    cardMoreCount: 'one:în Conexiuni|few:în Conexiuni|other:în Conexiuni',
    cardOpenSetup: 'Deschide configurarea',
    cardChecking: 'Se verifică agenții dvs. externi…',
    cardTryAgain: 'Încearcă din nou',
    connectedAgentsSubtitleAny: 'Agenți care citesc cunoștințele companiei dvs. prin Poneva.',
    scopeLibraryReadTitle: 'Citește munca dvs. din Poneva',
    scopeLibraryReadDescribe:
      '{app} poate găsi și citi chaturile, Biblioteca, fișierele, întâlnirile, Skill-urile, agenții și aplicațiile pe care le puteți deschide în Poneva și poate citi din serviciile dvs. conectate. Poate citi și asistenții pentru cumpărături ai companiei și ce le-au spus cumpărătorii.',
    scopeLibraryReadConfirmOn:
      'Permiteți ca {app} să citească munca dvs. din Poneva pentru {company}? Găsește și citește ceea ce puteți deja deschide în Poneva, în numele dvs.',
    scopeLibraryReadConfirmOff:
      'Opriți citirea muncii dvs. din Poneva de către {app}? Atunci nu va mai putea nici să salveze lucrări, nici să întrebe Poneva.',
    scopeLibraryReadEnabled:
      '{app} poate acum citi munca dvs. din Poneva. Dacă opțiunea nu apare încă în {app}, reporniți-l.',
    scopeLibraryReadDisabled: '{app} nu mai poate citi munca dvs. din Poneva.',
    scopeLibraryWriteTitle: 'Salvează lucrări și întreabă Poneva',
    scopeLibraryWriteDescribe:
      '{app} poate salva lucrări în Biblioteca dvs., unde lucrările noi rămân vizibile doar pentru dvs., și poate salva versiuni noi ale lucrărilor pe care le puteți edita (versiunile anterioare rămân în Istoric). Poate salva ciorne de Skill-uri și de designuri ale asistenților pentru cumpărături ai companiei, pe care le văd colegii care le pot edita; doar o persoană le publică în Poneva. De asemenea, poate întreba Poneva în chaturi private, ceea ce consumă credite. Poate porni și citiri ale magazinului web al companiei pentru a repara un asistent pentru cumpărături și îi poate pune întrebări de test; ambele folosesc creditele companiei.',
    scopeLibraryWriteConfirmOn:
      'Permiteți ca {app} să salveze lucrări în Biblioteca dvs. din {company}, inclusiv versiuni noi ale lucrărilor pe care le puteți edita și ciorne pe care le pot vedea colegii, și să întrebe Poneva pentru dvs.? Întrebările consumă credite. De asemenea, poate citi munca dvs. din Poneva. Citirile magazinului și întrebările de test către asistenții pentru cumpărături folosesc și ele creditele companiei.',
    scopeLibraryWriteConfirmOff:
      'Opriți salvarea de lucrări și întrebările către Poneva de la {app}? Ce a salvat deja rămâne în Biblioteca dvs.',
    scopeLibraryWriteEnabled:
      '{app} poate acum salva lucrări și întreba Poneva. Dacă opțiunea nu apare încă în {app}, reporniți-l.',
    scopeLibraryWriteDisabled: '{app} nu mai poate salva lucrări și nici întreba Poneva.',
  },
  sk: {
    mcpServersTitle: 'Vlastné pripojenia',
    mcpServersSubtitle:
      'Pripojte vlastný server s nástrojmi. Poneva sa opýta, skôr než niečo zmení.',
    mcpServersApproval:
      'Každú zmenu schvaľujete. Pri čítaní platí to, čo ste zvolili pre každý server.',
    mcpServersAdd: 'Pridať pripojenie',
    connectedAgentsLink: 'Pripojení agenti',
    accountConnectionsUnavailable:
      'Pripojenia účtov zatiaľ v tomto pracovnom priestore nie sú k dispozícii.',
    connectedAgentsTitle: 'Pripojení agenti',
    connectedAgentsSubtitle: 'Agenti, ktorí cez Poneva čítajú znalosti spoločnosti {company}.',
    connectedAgentsSuggest: 'Ak to povolíte, navrhujú zmeny na kontrolu človekom.',
    connectedAgentsList: 'Pripojené teraz',
    connectingAgentsOff:
      'Pripájanie agentov zatiaľ nie je pre tento pracovný priestor k dispozícii.',
    checkingConnectedAgents: 'Kontrolujú sa pripojení agenti…',
    connectedAgentsLoadFailed: 'Pripojených agentov sa nepodarilo načítať.',
    connectedAgentsOpen: 'Otvoriť Pripojených agentov',
    agentTimeNotYet: 'zatiaľ nie',
    agentTimeUnknown: 'v neznámom čase',
    agentTimeJustNow: 'práve teraz',
    agentTimeOnDate: 'dňa {date}',
    agentErrorNotFound: 'Tento agent už nie je pripojený. Zoznam zobrazuje aktuálny stav.',
    agentErrorScopeUnavailable: 'Tento prístup nie je v tomto pracovnom priestore k dispozícii.',
    agentErrorTooManyKeys:
      'Máte toľko prístupových kľúčov, koľko Poneva povoľuje. Najprv jeden zrušte.',
    agentErrorInvalidInput: 'Skontrolujte názov kľúča a skúste to znova.',
    agentNotRecorded: 'Nezaznamenané',
    agentMethodKey: 'Prístupový kľúč',
    agentMethodSignedIn: 'Prihlásené',
    agentStateReconnect: 'Treba znova pripojiť',
    agentStateReady: 'Pripravené',
    agentStateKeyUnused: 'Vytvorené — zatiaľ nepoužité',
    agentStateWaiting: 'Autorizované — čaká na prvé čítanie',
    agentLastUsed: 'Naposledy použité {when}',
    agentKeyExpires: 'Prístupový kľúč · platnosť vyprší {date}',
    agentSignInEnds: 'Prihlásené · prihlásenie vyprší {date}',
    agentsNone: 'Zatiaľ nie sú pripojení žiadni externí agenti',
    agentsConnectedCount:
      'one:agent pripojený|few:agenti pripojení|many:agenta pripojeného|other:agentov pripojených',
    agentsWaitingCount: 'one:čaká|few:čakajú|many:čaká|other:čaká',
    agentsReconnectCount:
      'one:na opätovné pripojenie|few:na opätovné pripojenie|many:na opätovné pripojenie|other:na opätovné pripojenie',
    addressLocalNetwork:
      'Dostupné len v tejto sieti. Agenti spustení v cloude túto adresu nemôžu použiť.',
    addressLocalComputer:
      'Dostupné len z tohto počítača. Agenti spustení v cloude túto adresu nemôžu použiť.',
    addressUnreachable:
      'Poneva sa k tejto adrese teraz nevie pripojiť, takže externí agenti pravdepodobne tiež nie.',
    agentUnnamed: 'Agent bez názvu',
    agentCompanyFallback: 'vašej spoločnosti',
    agentErrorUnreadable:
      'Poneva vrátila odpoveď, ktorú táto stránka nevie prečítať. Skúste to znova.',
    agentsLoadFailed: 'Vašich externých agentov sa nepodarilo načítať.',
    agentsLoading: 'Načítavajú sa externí agenti…',
    agentsConnectedShort: 'one:pripojený|few:pripojení|many:pripojeného|other:pripojených',
    agentsAddressTitle: 'MCP adresa',
    agentsPrivacy:
      'Agenti konajú vo vašom mene a vidia iba údaje {company}. Čítajú schválené znalosti, nikdy nie súkromné chaty, osobnú pamäť ani súkromné súbory. Nič ďalšie nie je zapnuté, kým to nepovolíte.',
    agentsSetupHide: 'Skryť nastavenie',
    agentsSetupAnother: 'Pripojiť ďalšieho agenta',
    agentsHelpInChat: 'Získať pomoc v chate',
    agentsHelpStarter: 'Pomôž mi pripojiť externého agenta k Poneva cez MCP.',
    agentNoticeReady: 'Agent {app} prečítal znalosti z Poneva. Pripojenie je pripravené.',
    agentNoticeAuthorized:
      'Agent {app} je autorizovaný. Položte mu otázku a dokončite tak kontrolu pripojenia.',
    agentScopeUnconfirmed:
      'Zmenu pre {app} sa nepodarilo potvrdiť. Podrobnosti nižšie ukazujú, čo má Poneva teraz.',
    agentScopeNotChanged: 'Prístup pre {app} sa nezmenil. Skúste to znova.',
    agentConfirmRevokeKey:
      'Zrušiť prístupový kľúč „{app}“? Každý agent, ktorý ho používa, okamžite prestane čítať znalosti {company}.',
    agentConfirmDisconnect:
      'Odpojiť {app}? Agent {app} už nebude môcť čítať znalosti {company}. Vaše prihlásenie do Poneva to neovplyvní.',
    agentKeyRevoked: 'Prístupový kľúč „{app}“ je zrušený.',
    agentDisconnected: 'Agent {app} je odpojený.',
    agentDisconnectUnconfirmed:
      'Nepodarilo sa potvrdiť, že agent {app} je odpojený. Zoznam zobrazuje aktuálny stav.',
    agentDisconnectFailed: 'Agent {app} je stále pripojený. Skúste to znova.',
    setupTitle: 'Pripojiť externého agenta',
    setupManualLabel: 'Alebo to nastavte sami',
    setupAddTitle: 'Pridať Poneva ako MCP server',
    setupAddBody:
      'V nastaveniach MCP alebo konektorov agenta pridajte vzdialený MCP server (Streamable HTTP) s adresou uvedenou vyššie.',
    setupConfigToggle: 'Používate konfiguračný súbor?',
    setupConfigBody: 'Agenti, ktorí uvádzajú svoje MCP servery v súbore JSON, použijú toto:',
    setupSignInTitle: 'Prihlásenie',
    setupSignInBody:
      'Agent otvorí Poneva vo vašom prehliadači. Skontrolujte spoločnosť a to, čo agent smie robiť, a potom vyberte Povoliť.',
    setupSignInFallback:
      'Ak sa prihlasovacie okno samo zavrie alebo v ňom Poneva vôbec nezobrazí, prípadne ak agent žiada token alebo kľúč API, použite namiesto toho prístupový kľúč.',
    setupTryTitle: 'Vyskúšať',
    setupTryAsk: 'Opýtajte sa agenta:',
    setupSampleQuestion: 'Aké je naše aktuálne smerovanie produktu? Uveď zdroj.',
    setupTryNote: 'Po prvom čítaní sa tu zobrazí ako Pripravené.',
    setupPromptTitle: 'Nechajte nastavenie na programovacom agentovi',
    setupPromptBody:
      'Vložte tento prompt do Claude Code, Codex alebo iného programovacieho agenta. Obsahuje vašu MCP adresu a prevedie agenta nastavením. Prístup schválite pri prihlásení.',
    copyAction: 'Kopírovať',
    copiedAction: 'Skopírované',
    copyNamed: 'Kopírovať {what}',
    copiedNamed: 'Skopírované: {what}',
    copiedStatus: 'Skopírované: {what}.',
    copyManualHint:
      'Kopírovanie v tomto prehliadači nie je k dispozícii. Text je označený; skopírujte ho klávesnicou.',
    copyPromptShow: 'Zobraziť prompt',
    copyPromptHide: 'Skryť prompt',
    copyWhatAddress: 'odkaz na MCP server Poneva',
    copyWhatConfig: 'nastavenie MCP servera',
    copyWhatSample: 'vzorový dotaz',
    copyWhatSampleFor: 'vzorový dotaz pre {app}',
    copyWhatPrompt: 'prompt na nastavenie',
    copyWhatKey: 'prístupový kľúč pre {app}',
    keyCreateAction: 'Vytvoriť prístupový kľúč',
    keySecret:
      'Zaobchádzajte s ním ako s heslom. Vkladajte ho iba do nastavení MCP, konektorov alebo tajných údajov agenta, nikdy nie na webovú stránku ani do kódu, ktorý môžu otvoriť iní.',
    keyFormNote:
      'Pre agenta, ktorý sa nevie prihlásiť. Číta znalosti {company} vo vašom mene, kým nevyprší alebo ho nezrušíte; ukončí ho aj zmena hesla alebo odchod zo spoločnosti.',
    keyName: 'Názov',
    keyNamePlaceholder: 'napr. Nočná zostava',
    keyLifetime: 'Platnosť vyprší po',
    keyDays: 'one:dni|few:dňoch|many:dňa|other:dňoch',
    keyAlsoAllow: 'Povoliť aj',
    keyCreating: 'Vytvára sa…',
    keyCreate: 'Vytvoriť kľúč',
    keyCancel: 'Zrušiť',
    keyUnconfirmed:
      'Nepodarilo sa potvrdiť, či bol kľúč vytvorený. Ak sa zobrazí v zozname, zrušte ho a vytvorte nový.',
    keyNotCreated: 'Prístupový kľúč sa nevytvoril. Skúste to znova.',
    keyNewTitle: 'Prístupový kľúč pre {app}',
    keyCopyNow: 'Skopírujte ho teraz. Poneva ho zobrazí iba raz a uchováva len jeho odtlačok.',
    keyTokenRule: 'Do poľa {token} alebo {apiKey} patrí iba samotný kľúč.',
    keyTermToken: 'token',
    keyTermApiKey: 'kľúč API',
    keyHeaderRule:
      'Do poľa {header} patrí názov {name} a hodnota {value}, pričom {key} nahradíte svojím kľúčom.',
    keyTermHeader: 'hlavička',
    keyEndsOn:
      'Prestane fungovať {date}, keď ho zrušíte, a keď si zmeníte heslo alebo odídete zo spoločnosti.',
    keyEndsExpiry:
      'Prestane fungovať, keď vyprší, keď ho zrušíte, a keď si zmeníte heslo alebo odídete zo spoločnosti.',
    keyDone: 'Hotovo',
    scopeProposeTitle: 'Navrhovať aktualizácie znalostí',
    scopeProposeDescribe:
      '{app} môže navrhovať zmeny stránok. Každú zmenu najprv skontroluje publikujúci v spoločnosti.',
    scopeProposeConfirmOn:
      'Povoliť {app} navrhovať aktualizácie znalostí {company}? Každý návrh čaká v sekcii Znalosti → Na kontrolu, kým ho neschváli publikujúci v spoločnosti. Nič sa nezmení samo.',
    scopeProposeConfirmOff:
      'Zakázať {app} navrhovať aktualizácie znalostí? Už odoslané návrhy zostanú na kontrole.',
    scopeProposeEnabled:
      '{app} teraz môže navrhovať aktualizácie znalostí. Ak sa možnosť v {app} ešte nezobrazuje, reštartujte ho.',
    scopeProposeDisabled: '{app} už nemôže navrhovať aktualizácie znalostí.',
    agentReadAccess:
      'Čítanie znalostí spoločnosti: profil spoločnosti a schválené stránky znalostí. Chaty, súbory, stretnutia a osobná pamäť nie sú zahrnuté.',
    agentHintKeyStale:
      'Adresa Poneva sa zmenila po vytvorení tohto kľúča, preto ho Poneva odmieta. Zrušte ho a vytvorte nový kľúč.',
    agentHintStale:
      'Adresa Poneva sa zmenila po pripojení {app}, preto ho Poneva odmieta. Odpojte ho a potom {app} znova pripojte s adresou uvedenou vyššie.',
    agentHintKeyWaiting: 'Keď agent použije kľúč, zobrazí sa ako Pripravené.',
    agentHintWaiting: 'Položte {app} otázku a dokončite tak kontrolu pripojenia.',
    agentHintTryAsking: 'Skúste sa opýtať:',
    agentFactCreated: 'Vytvorené',
    agentFactConnected: 'Pripojené',
    agentFactExpires: 'Platnosť vyprší',
    agentFactSignInEnds: 'Prihlásenie vyprší',
    agentFactAccess: 'Prístup',
    agentNameNote:
      'Názov uviedol samotný agent; Poneva ho nevie overiť. Odpojte každého agenta, ktorého nepoznáte.',
    agentSignInNote: 'Keď prihlásenie vyprší, agent vás požiada o opätovné prihlásenie.',
    agentWorking: 'Spracúva sa…',
    agentRevokeKey: 'Zrušiť kľúč',
    agentDisconnect: 'Odpojiť',
    cardAgentsLabel: 'Vaši pripojení agenti',
    cardMore: 'a ešte {more}',
    cardMoreCount: 'one:v Pripojeniach|few:v Pripojeniach|many:v Pripojeniach|other:v Pripojeniach',
    cardOpenSetup: 'Otvoriť nastavenie',
    cardChecking: 'Kontrolujú sa vaši externí agenti…',
    cardTryAgain: 'Skúsiť znova',
    connectedAgentsSubtitleAny: 'Agenti, ktorí cez Poneva čítajú znalosti vašej spoločnosti.',
    scopeLibraryReadTitle: 'Čítať vašu prácu v Poneva',
    scopeLibraryReadDescribe:
      '{app} môže vyhľadávať a čítať chaty, Knižnicu, súbory, stretnutia, Skilly, agentov a aplikácie, ktoré môžete otvoriť v Poneva, a čítať z vašich pripojených služieb. Môže tiež čítať nákupných asistentov spoločnosti a to, čo im povedali nakupujúci.',
    scopeLibraryReadConfirmOn:
      'Povoliť {app} čítať vašu prácu v Poneva v spoločnosti {company}? Vyhľadáva a číta to, čo už v Poneva môžete otvoriť, a koná pritom ako vy.',
    scopeLibraryReadConfirmOff:
      'Zakázať {app} čítať vašu prácu v Poneva? Potom už nebude môcť ani ukladať prácu, ani sa pýtať Ponevy.',
    scopeLibraryReadEnabled:
      '{app} teraz môže čítať vašu prácu v Poneva. Ak sa možnosť v {app} ešte nezobrazuje, reštartujte ho.',
    scopeLibraryReadDisabled: '{app} už nemôže čítať vašu prácu v Poneva.',
    scopeLibraryWriteTitle: 'Ukladať prácu a pýtať sa Ponevy',
    scopeLibraryWriteDescribe:
      '{app} môže ukladať prácu do vašej Knižnice, kde nová práca zostáva súkromná, len pre vás, a ukladať nové verzie práce, ktorú môžete upravovať (staršie verzie zostávajú v sekcii História). Môže ukladať koncepty Skillov a dizajnov nákupných asistentov spoločnosti, ktoré vidia kolegovia, ktorí ich môžu upravovať; v Poneva ich zverejňuje iba človek. Môže sa tiež pýtať Ponevy v súkromných chatoch, čo spotrebúva kredity. Môže tiež spustiť čítanie webového obchodu spoločnosti, aby opravil nákupného asistenta, a klásť mu testovacie otázky; obe využívajú kredity spoločnosti.',
    scopeLibraryWriteConfirmOn:
      'Povoliť {app} ukladať prácu do vašej Knižnice v spoločnosti {company}, vrátane nových verzií práce, ktorú môžete upravovať, a konceptov, ktoré môžu vidieť kolegovia, a pýtať sa Ponevy za vás? Otázky spotrebúvajú kredity. Bude môcť aj čítať vašu prácu v Poneva. Čítanie obchodu a testovacie otázky pre nákupných asistentov tiež využívajú kredity spoločnosti.',
    scopeLibraryWriteConfirmOff:
      'Zakázať {app} ukladať prácu a pýtať sa Ponevy? To, čo už uložil, zostane vo vašej Knižnici.',
    scopeLibraryWriteEnabled:
      '{app} teraz môže ukladať prácu a pýtať sa Ponevy. Ak sa možnosť v {app} ešte nezobrazuje, reštartujte ho.',
    scopeLibraryWriteDisabled: '{app} už nemôže ukladať prácu ani sa pýtať Ponevy.',
  },
  sl: {
    mcpServersTitle: 'Povezave po meri',
    mcpServersSubtitle: 'Povežite svoj strežnik z orodji. Poneva vpraša, preden karkoli spremeni.',
    mcpServersApproval:
      'Vsako spremembo odobrite. Za branje velja, kar ste izbrali za vsak strežnik.',
    mcpServersAdd: 'Dodaj povezavo',
    connectedAgentsLink: 'Povezani agenti',
    accountConnectionsUnavailable: 'Povezave z računi v tem delovnem prostoru še niso na voljo.',
    connectedAgentsTitle: 'Povezani agenti',
    connectedAgentsSubtitle: 'Agenti, ki prek Poneve berejo znanje podjetja {company}.',
    connectedAgentsSuggest: 'Če dovolite, predlagajo posodobitve, ki jih pregleda človek.',
    connectedAgentsList: 'Povezani zdaj',
    connectingAgentsOff: 'Povezovanje agentov za ta delovni prostor še ni na voljo.',
    checkingConnectedAgents: 'Preverjanje povezanih agentov…',
    connectedAgentsLoadFailed: 'Povezanih agentov ni bilo mogoče naložiti.',
    connectedAgentsOpen: 'Odpri Povezane agente',
    agentTimeNotYet: 'še ne',
    agentTimeUnknown: 'ob neznanem času',
    agentTimeJustNow: 'pravkar',
    agentTimeOnDate: 'dne {date}',
    agentErrorNotFound: 'Ta agent ni več povezan. Seznam prikazuje trenutno stanje.',
    agentErrorScopeUnavailable: 'Ta dostop v tem delovnem prostoru ni na voljo.',
    agentErrorTooManyKeys:
      'Imate toliko dostopnih ključev, kot jih Poneva dovoli. Najprej enega prekličite.',
    agentErrorInvalidInput: 'Preverite ime ključa in poskusite znova.',
    agentNotRecorded: 'Ni zabeleženo',
    agentMethodKey: 'Dostopni ključ',
    agentMethodSignedIn: 'Prijavljeno',
    agentStateReconnect: 'Potrebna je ponovna povezava',
    agentStateReady: 'Pripravljeno',
    agentStateKeyUnused: 'Ustvarjeno — še ni uporabljeno',
    agentStateWaiting: 'Pooblaščeno — čaka na prvo branje',
    agentLastUsed: 'Nazadnje uporabljeno {when}',
    agentKeyExpires: 'Dostopni ključ · poteče {date}',
    agentSignInEnds: 'Prijavljeno · prijava poteče {date}',
    agentsNone: 'Povezan še ni noben zunanji agent',
    agentsConnectedCount:
      'one:agent povezan|two:agenta povezana|few:agenti povezani|other:agentov povezanih',
    agentsWaitingCount: 'one:čaka|two:čakata|few:čakajo|other:čaka',
    agentsReconnectCount:
      'one:za ponovno povezavo|two:za ponovno povezavo|few:za ponovno povezavo|other:za ponovno povezavo',
    addressLocalNetwork:
      'Dosegljivo samo v tem omrežju. Agenti, ki delujejo v oblaku, tega naslova ne morejo uporabiti.',
    addressLocalComputer:
      'Dosegljivo samo s tega računalnika. Agenti, ki delujejo v oblaku, tega naslova ne morejo uporabiti.',
    addressUnreachable:
      'Poneva tega naslova trenutno ne more doseči, zato ga zunanji agenti verjetno tudi ne.',
    agentUnnamed: 'Neimenovan agent',
    agentCompanyFallback: 'vašega podjetja',
    agentErrorUnreadable:
      'Poneva je vrnila odgovor, ki ga ta stran ne more prebrati. Poskusite znova.',
    agentsLoadFailed: 'Vaših zunanjih agentov ni bilo mogoče naložiti.',
    agentsLoading: 'Nalaganje zunanjih agentov…',
    agentsConnectedShort: 'one:povezan|two:povezana|few:povezani|other:povezanih',
    agentsAddressTitle: 'Naslov MCP',
    agentsPrivacy:
      'Agenti delujejo v vašem imenu in vidijo samo podatke {company}. Berejo odobreno znanje, nikoli zasebnih klepetov, osebnega spomina ali zasebnih datotek. Vse drugo ostane izklopljeno, dokler tega ne dovolite.',
    agentsSetupHide: 'Skrij nastavitev',
    agentsSetupAnother: 'Poveži še enega agenta',
    agentsHelpInChat: 'Pomoč v klepetu',
    agentsHelpStarter: 'Pomagaj mi povezati zunanjega agenta s Ponevo prek MCP.',
    agentNoticeReady: 'Agent {app} je prebral znanje iz Poneve. Povezava je pripravljena.',
    agentNoticeAuthorized:
      'Agent {app} je pooblaščen. Postavite mu vprašanje, da dokončate preverjanje povezave.',
    agentScopeUnconfirmed:
      'Spremembe za {app} nismo mogli potrditi. Spodnje podrobnosti kažejo, kaj ima Poneva zdaj.',
    agentScopeNotChanged: 'Dostop za {app} se ni spremenil. Poskusite znova.',
    agentConfirmRevokeKey:
      'Želite preklicati dostopni ključ »{app}«? Vsak agent, ki ga uporablja, takoj preneha brati znanje {company}.',
    agentConfirmDisconnect:
      'Želite prekiniti povezavo z agentom {app}? Agent {app} ne bo več mogel brati znanja {company}. To ne vpliva na vašo prijavo v Ponevo.',
    agentKeyRevoked: 'Dostopni ključ »{app}« je preklican.',
    agentDisconnected: 'Povezava z agentom {app} je prekinjena.',
    agentDisconnectUnconfirmed:
      'Nismo mogli potrditi, da je povezava z agentom {app} prekinjena. Seznam prikazuje trenutno stanje.',
    agentDisconnectFailed: 'Agent {app} je še vedno povezan. Poskusite znova.',
    setupTitle: 'Poveži zunanjega agenta',
    setupManualLabel: 'Ali pa ga nastavite sami',
    setupAddTitle: 'Dodaj Ponevo kot strežnik MCP',
    setupAddBody:
      'V nastavitvah MCP ali povezovalnikov agenta dodajte oddaljeni strežnik MCP (Streamable HTTP) z zgornjim naslovom.',
    setupConfigToggle: 'Uporabljate konfiguracijsko datoteko?',
    setupConfigBody: 'Agenti, ki svoje strežnike MCP navajajo v datoteki JSON, uporabijo tole:',
    setupSignInTitle: 'Prijava',
    setupSignInBody:
      'Agent odpre Ponevo v vašem brskalniku. Preverite podjetje in kaj agent sme početi, nato izberite Dovoli.',
    setupSignInFallback:
      'Če se okno za prijavo samo zapre ali v njem nikoli ni Poneve oziroma če agent zahteva žeton ali ključ API, namesto tega uporabite dostopni ključ.',
    setupTryTitle: 'Preizkusite',
    setupTryAsk: 'Vprašajte agenta:',
    setupSampleQuestion: 'Kakšna je naša trenutna usmeritev izdelkov? Navedi vir.',
    setupTryNote: 'Po prvem branju bo tukaj prikazan kot Pripravljeno.',
    setupPromptTitle: 'Naj ga nastavi vaš agent za programiranje',
    setupPromptBody:
      'Prilepite ta poziv v Claude Code, Codex ali drugega agenta za programiranje. Vsebuje vaš naslov MCP in agenta vodi skozi nastavitev. Dostop odobrite ob prijavi.',
    copyAction: 'Kopiraj',
    copiedAction: 'Kopirano',
    copyNamed: 'Kopiraj {what}',
    copiedNamed: 'Kopirano: {what}',
    copiedStatus: 'Kopirano: {what}.',
    copyManualHint:
      'Kopiranje v tem brskalniku ni na voljo. Besedilo je izbrano; kopirajte ga s tipkovnico.',
    copyPromptShow: 'Prikaži poziv',
    copyPromptHide: 'Skrij poziv',
    copyWhatAddress: 'naslov MCP Poneve',
    copyWhatConfig: 'nastavitve strežnika MCP',
    copyWhatSample: 'vzorčno vprašanje',
    copyWhatSampleFor: 'vzorčno vprašanje za {app}',
    copyWhatPrompt: 'poziv za nastavitev',
    copyWhatKey: 'dostopni ključ za {app}',
    keyCreateAction: 'Ustvari dostopni ključ',
    keySecret:
      'Ravnajte z njim kot z geslom. Prilepite ga samo v nastavitve MCP, povezovalnikov ali skrivnosti agenta, nikoli na spletno mesto ali v kodo, ki jo lahko odprejo drugi.',
    keyFormNote:
      'Za agenta, ki se ne more prijaviti. V vašem imenu bere znanje {company}, dokler ne poteče ali ga ne prekličete; preneha tudi, ko spremenite geslo ali zapustite podjetje.',
    keyName: 'Ime',
    keyNamePlaceholder: 'npr. Nočno poročilo',
    keyLifetime: 'Poteče po',
    keyDays: 'one:dnevu|two:dneh|few:dneh|other:dneh',
    keyAlsoAllow: 'Dovoli tudi',
    keyCreating: 'Ustvarjanje…',
    keyCreate: 'Ustvari ključ',
    keyCancel: 'Prekliči',
    keyUnconfirmed:
      'Nismo mogli potrditi, da je bil ključ ustvarjen. Če je na seznamu, ga prekličite in ustvarite novega.',
    keyNotCreated: 'Dostopni ključ ni bil ustvarjen. Poskusite znova.',
    keyNewTitle: 'Dostopni ključ za {app}',
    keyCopyNow:
      'Kopirajte ga zdaj. Poneva ga prikaže samo enkrat in shrani le njegov prstni odtis.',
    keyTokenRule: 'V polje {token} ali {apiKey} vnesite samo ključ.',
    keyTermToken: 'žeton',
    keyTermApiKey: 'ključ API',
    keyHeaderRule:
      'V polje {header} vnesite ime {name} in vrednost {value}, pri čemer {key} zamenjate s svojim ključem.',
    keyTermHeader: 'glava',
    keyEndsOn:
      'Preneha delovati {date}, ko ga prekličete in ko spremenite geslo ali zapustite podjetje.',
    keyEndsExpiry:
      'Preneha delovati, ko poteče, ko ga prekličete in ko spremenite geslo ali zapustite podjetje.',
    keyDone: 'Končano',
    scopeProposeTitle: 'Predlaganje posodobitev znanja',
    scopeProposeDescribe:
      '{app} lahko predlaga spremembe strani. Urednik podjetja pregleda vsako, preden se karkoli spremeni.',
    scopeProposeConfirmOn:
      'Želite, da {app} predlaga posodobitve znanja {company}? Vsak predlog čaka v razdelku Znanje → V pregled, dokler ga ne odobri urednik podjetja. Nič se ne spremeni samo od sebe.',
    scopeProposeConfirmOff:
      'Želite, da {app} ne predlaga več posodobitev znanja? Že poslani predlogi ostanejo v pregledu.',
    scopeProposeEnabled:
      '{app} lahko zdaj predlaga posodobitve znanja. Če se možnost v {app} še ne prikaže, ga znova zaženite.',
    scopeProposeDisabled: '{app} ne more več predlagati posodobitev znanja.',
    agentReadAccess:
      'Branje znanja podjetja: profil podjetja in odobrene strani znanja. Klepeti, datoteke, sestanki in osebni spomin niso vključeni.',
    agentHintKeyStale:
      'Naslov Poneve se je spremenil, potem ko je bil ta ključ ustvarjen, zato ga Poneva zavrne. Prekličite ga in ustvarite nov ključ.',
    agentHintStale:
      'Naslov Poneve se je spremenil, potem ko se je povezal agent {app}, zato ga Poneva zavrne. Prekinite povezavo in nato agenta {app} znova povežite z zgornjim naslovom.',
    agentHintKeyWaiting: 'Ko agent uporabi ključ, bo prikazan kot Pripravljeno.',
    agentHintWaiting: 'Postavite agentu {app} vprašanje, da dokončate preverjanje povezave.',
    agentHintTryAsking: 'Poskusite vprašati:',
    agentFactCreated: 'Ustvarjeno',
    agentFactConnected: 'Povezano',
    agentFactExpires: 'Poteče',
    agentFactSignInEnds: 'Prijava poteče',
    agentFactAccess: 'Dostop',
    agentNameNote:
      'To je ime, ki ga je sporočil agent; Poneva ga ne more preveriti. Prekinite povezavo z vsakim agentom, ki ga ne prepoznate.',
    agentSignInNote: 'Ko prijava poteče, vas agent prosi, da se znova prijavite.',
    agentWorking: 'Obdelava…',
    agentRevokeKey: 'Prekliči ključ',
    agentDisconnect: 'Prekini povezavo',
    cardAgentsLabel: 'Vaši povezani agenti',
    cardMore: 'in še {more}',
    cardMoreCount: 'one:v Povezavah|two:v Povezavah|few:v Povezavah|other:v Povezavah',
    cardOpenSetup: 'Odpri nastavitev',
    cardChecking: 'Preverjanje vaših zunanjih agentov…',
    cardTryAgain: 'Poskusi znova',
    connectedAgentsSubtitleAny: 'Agenti, ki prek Poneve berejo znanje vašega podjetja.',
    scopeLibraryReadTitle: 'Branje vašega dela v Ponevi',
    scopeLibraryReadDescribe:
      '{app} lahko najde in bere klepete, Knjižnico, datoteke, sestanke, Skille, agente in aplikacije, ki jih lahko odprete v Ponevi, ter bere iz vaših povezanih storitev. Bere lahko tudi nakupovalne pomočnike podjetja in kar so jim povedali kupci.',
    scopeLibraryReadConfirmOn:
      'Želite, da {app} bere vaše delo v Ponevi v podjetju {company}? Najde in bere to, kar že lahko odprete v Ponevi, in pri tem deluje kot vi.',
    scopeLibraryReadConfirmOff:
      'Želite, da {app} ne bere več vašega dela v Ponevi? Potem tudi ne bo mogel več shranjevati dela ali spraševati Poneve.',
    scopeLibraryReadEnabled:
      '{app} lahko zdaj bere vaše delo v Ponevi. Če se možnost v {app} še ne prikaže, ga znova zaženite.',
    scopeLibraryReadDisabled: '{app} ne more več brati vašega dela v Ponevi.',
    scopeLibraryWriteTitle: 'Shranjevanje dela in spraševanje Poneve',
    scopeLibraryWriteDescribe:
      '{app} lahko shranjuje delo v vašo Knjižnico, kjer novo delo ostane zasebno, samo za vas, in shranjuje nove različice dela, ki ga lahko urejate (prejšnje različice ostanejo v razdelku Zgodovina). Lahko shranjuje osnutke Skillov in videza nakupovalnih pomočnikov podjetja; vidijo jih sodelavci, ki jih lahko urejajo, v Ponevi pa jih objavi samo človek. Lahko tudi sprašuje Ponevo v zasebnih klepetih, kar porablja kredite. Lahko tudi sproži branje spletne trgovine podjetja, da popravi nakupovalnega pomočnika, in mu zastavlja preizkusna vprašanja; oboje porablja kredite podjetja.',
    scopeLibraryWriteConfirmOn:
      'Želite, da {app} shranjuje delo v vašo Knjižnico v podjetju {company}, vključno z novimi različicami dela, ki ga lahko urejate, in osnutki, ki jih lahko vidijo sodelavci, ter namesto vas sprašuje Ponevo? Vprašanja porabljajo kredite. Bral bo lahko tudi vaše delo v Ponevi. Branje trgovine in preizkusna vprašanja nakupovalnim pomočnikom prav tako porabljajo kredite podjetja.',
    scopeLibraryWriteConfirmOff:
      'Želite, da {app} ne shranjuje več dela in ne sprašuje Poneve? Kar je že shranil, ostane v vaši Knjižnici.',
    scopeLibraryWriteEnabled:
      '{app} lahko zdaj shranjuje delo in sprašuje Ponevo. Če se možnost v {app} še ne prikaže, ga znova zaženite.',
    scopeLibraryWriteDisabled: '{app} ne more več shranjevati dela ali spraševati Poneve.',
  },
  sv: {
    mcpServersTitle: 'Egna anslutningar',
    mcpServersSubtitle: 'Anslut din egen verktygsserver. Poneva frågar innan något ändras.',
    mcpServersApproval:
      'Du godkänner varje ändring. För läsning gäller det du valt för varje server.',
    mcpServersAdd: 'Lägg till anslutning',
    connectedAgentsLink: 'Anslutna agenter',
    accountConnectionsUnavailable:
      'Kontoanslutningar är inte tillgängliga i den här arbetsytan än.',
    connectedAgentsTitle: 'Anslutna agenter',
    connectedAgentsSubtitle: 'Agenter som läser {company}s kunskap via Poneva.',
    connectedAgentsSuggest: 'Om du tillåter det föreslår de uppdateringar som en person granskar.',
    connectedAgentsList: 'Anslutna nu',
    connectingAgentsOff: 'Det går inte att ansluta agenter i den här arbetsytan än.',
    checkingConnectedAgents: 'Kontrollerar anslutna agenter…',
    connectedAgentsLoadFailed: 'Anslutna agenter kunde inte läsas in.',
    connectedAgentsOpen: 'Öppna Anslutna agenter',
    agentTimeNotYet: 'inte än',
    agentTimeUnknown: 'vid okänd tidpunkt',
    agentTimeJustNow: 'nyss',
    agentTimeOnDate: 'den {date}',
    agentErrorNotFound: 'Den här agenten är inte längre ansluten. Listan visar det aktuella läget.',
    agentErrorScopeUnavailable: 'Den åtkomsten är inte tillgänglig i den här arbetsytan.',
    agentErrorTooManyKeys:
      'Du har så många åtkomstnycklar som Poneva tillåter. Återkalla en först.',
    agentErrorInvalidInput: 'Kontrollera nyckelns namn och försök igen.',
    agentNotRecorded: 'Inte registrerat',
    agentMethodKey: 'Åtkomstnyckel',
    agentMethodSignedIn: 'Inloggad',
    agentStateReconnect: 'Behöver anslutas igen',
    agentStateReady: 'Redo',
    agentStateKeyUnused: 'Skapad — inte använd än',
    agentStateWaiting: 'Godkänd — väntar på första läsningen',
    agentLastUsed: 'Senast använd {when}',
    agentKeyExpires: 'Åtkomstnyckel · går ut {date}',
    agentSignInEnds: 'Inloggad · inloggningen går ut {date}',
    agentsNone: 'Inga externa agenter anslutna än',
    agentsConnectedCount: 'one:agent ansluten|other:agenter anslutna',
    agentsWaitingCount: 'one:väntar|other:väntar',
    agentsReconnectCount: 'one:behöver anslutas igen|other:behöver anslutas igen',
    addressLocalNetwork:
      'Kan bara nås i det här nätverket. Agenter som körs i molnet kan inte använda den här adressen.',
    addressLocalComputer:
      'Kan bara nås från den här datorn. Agenter som körs i molnet kan inte använda den här adressen.',
    addressUnreachable:
      'Poneva når inte den här adressen just nu, så externa agenter kan troligen inte heller göra det.',
    agentUnnamed: 'Namnlös agent',
    agentCompanyFallback: 'ditt företag',
    agentErrorUnreadable:
      'Poneva skickade ett svar som den här sidan inte kunde läsa. Försök igen.',
    agentsLoadFailed: 'Det gick inte att läsa in dina externa agenter.',
    agentsLoading: 'Läser in externa agenter…',
    agentsConnectedShort: 'one:ansluten|other:anslutna',
    agentsAddressTitle: 'MCP-adress',
    agentsPrivacy:
      'Agenter agerar som du och ser bara {company}. De läser godkänd kunskap, aldrig privata chattar, personligt minne eller privata filer. Allt annat är avstängt om du inte tillåter det.',
    agentsSetupHide: 'Dölj konfigurationen',
    agentsSetupAnother: 'Anslut en agent till',
    agentsHelpInChat: 'Få hjälp i chatten',
    agentsHelpStarter: 'Hjälp mig att ansluta en extern agent till Poneva via MCP.',
    agentNoticeReady: '{app} har läst kunskap från Poneva. Anslutningen är klar.',
    agentNoticeAuthorized:
      '{app} är godkänd. Ställ en fråga till den för att slutföra kontrollen av anslutningen.',
    agentScopeUnconfirmed:
      'Vi kunde inte bekräfta ändringen för {app}. Uppgifterna nedan visar vad Poneva har nu.',
    agentScopeNotChanged: 'Åtkomsten för {app} ändrades inte. Försök igen.',
    agentConfirmRevokeKey:
      'Återkalla åtkomstnyckeln ”{app}”? Alla agenter som använder den slutar direkt att läsa kunskap från {company}.',
    agentConfirmDisconnect:
      'Koppla från {app}? {app} kommer inte längre att kunna läsa kunskap från {company}. Din inloggning i Poneva påverkas inte.',
    agentKeyRevoked: 'Åtkomstnyckeln ”{app}” har återkallats.',
    agentDisconnected: '{app} har kopplats från.',
    agentDisconnectUnconfirmed:
      'Vi kunde inte bekräfta att {app} kopplades från. Listan visar det aktuella läget.',
    agentDisconnectFailed: '{app} är fortfarande ansluten. Försök igen.',
    setupTitle: 'Anslut en extern agent',
    setupManualLabel: 'Eller konfigurera själv',
    setupAddTitle: 'Lägg till Poneva som MCP-server',
    setupAddBody:
      'Lägg till en fjärransluten MCP-server (Streamable HTTP) med adressen ovan i agentens inställningar för MCP eller kopplingar.',
    setupConfigToggle: 'Använder du en konfigurationsfil?',
    setupConfigBody: 'Agenter som listar sina MCP-servrar i en JSON-fil använder det här:',
    setupSignInTitle: 'Logga in',
    setupSignInBody:
      'Agenten öppnar Poneva i din webbläsare. Kontrollera företaget och vad agenten får göra och välj sedan Tillåt.',
    setupSignInFallback:
      'Om inloggningsfönstret stängs av sig självt eller aldrig visar Poneva, eller om agenten ber om en token eller API-nyckel, använder du en åtkomstnyckel i stället.',
    setupTryTitle: 'Testa',
    setupTryAsk: 'Fråga agenten:',
    setupSampleQuestion: 'Vilken är vår nuvarande produktinriktning? Ange källan.',
    setupTryNote: 'Den visas som Redo här efter första läsningen.',
    setupPromptTitle: 'Låt din kodagent konfigurera det',
    setupPromptBody:
      'Klistra in den här prompten i Claude Code, Codex eller en annan kodagent. Den innehåller din MCP-adress och guidar agenten genom konfigurationen. Du godkänner åtkomsten när du loggar in.',
    copyAction: 'Kopiera',
    copiedAction: 'Kopierat',
    copyNamed: 'Kopiera {what}',
    copiedNamed: 'Kopierat: {what}',
    copiedStatus: 'Kopierade {what}.',
    copyManualHint:
      'Det går inte att kopiera i den här webbläsaren. Texten är markerad, så du kan kopiera den med tangentbordet.',
    copyPromptShow: 'Visa prompten',
    copyPromptHide: 'Dölj prompten',
    copyWhatAddress: 'Ponevas MCP-adress',
    copyWhatConfig: 'MCP-serverkonfigurationen',
    copyWhatSample: 'exempelfrågan',
    copyWhatSampleFor: 'exempelfrågan för {app}',
    copyWhatPrompt: 'konfigurationsprompten',
    copyWhatKey: 'åtkomstnyckeln för {app}',
    keyCreateAction: 'Skapa en åtkomstnyckel',
    keySecret:
      'Hantera den som ett lösenord. Klistra bara in den i en agents inställningar för MCP, kopplingar eller hemligheter, aldrig på en webbplats eller i kod som andra kan öppna.',
    keyFormNote:
      'För en agent som inte kan logga in. Den läser kunskap från {company} som du tills den går ut eller du återkallar den. Den slutar också gälla om du byter lösenord eller lämnar företaget.',
    keyName: 'Namn',
    keyNamePlaceholder: 't.ex. Nattlig rapportkörning',
    keyLifetime: 'Går ut efter',
    keyDays: 'one:dag|other:dagar',
    keyAlsoAllow: 'Tillåt även',
    keyCreating: 'Skapar…',
    keyCreate: 'Skapa nyckel',
    keyCancel: 'Avbryt',
    keyUnconfirmed:
      'Vi kunde inte bekräfta att nyckeln skapades. Om den visas i listan återkallar du den och skapar en ny.',
    keyNotCreated: 'Åtkomstnyckeln skapades inte. Försök igen.',
    keyNewTitle: 'Åtkomstnyckel för {app}',
    keyCopyNow:
      'Kopiera den nu. Poneva visar den bara en gång och sparar bara ett fingeravtryck av den.',
    keyTokenRule: 'Ett fält för {token} eller {apiKey} ska bara innehålla nyckeln.',
    keyTermToken: 'token',
    keyTermApiKey: 'API-nyckel',
    keyHeaderRule:
      'Ett fält för {header} ska innehålla namnet {name} och värdet {value}, med din nyckel i stället för {key}.',
    keyTermHeader: 'header',
    keyEndsOn:
      'Den slutar fungera {date}, när du återkallar den och när du byter lösenord eller lämnar företaget.',
    keyEndsExpiry:
      'Den slutar fungera när den går ut, när du återkallar den och när du byter lösenord eller lämnar företaget.',
    keyDone: 'Klar',
    scopeProposeTitle: 'Föreslå kunskapsuppdateringar',
    scopeProposeDescribe:
      '{app} kan föreslå ändringar på sidor. En publicerare på företaget granskar varje förslag innan något ändras.',
    scopeProposeConfirmOn:
      'Låta {app} föreslå kunskapsuppdateringar för {company}? Varje förslag väntar i Kunskap → Att granska tills en publicerare på företaget godkänner det. Inget ändras av sig självt.',
    scopeProposeConfirmOff:
      'Hindra {app} från att föreslå kunskapsuppdateringar? Förslag som redan har skickats ligger kvar för granskning.',
    scopeProposeEnabled:
      '{app} kan nu föreslå kunskapsuppdateringar. Om alternativet inte syns i {app} än startar du om det.',
    scopeProposeDisabled: '{app} kan inte längre föreslå kunskapsuppdateringar.',
    agentReadAccess:
      'Läsa företagets kunskap: företagsprofilen och de godkända kunskapssidorna. Chattar, filer, möten och personligt minne ingår inte.',
    agentHintKeyStale:
      'Ponevas adress ändrades efter att nyckeln skapades, så Poneva nekar den. Återkalla den och skapa en ny nyckel.',
    agentHintStale:
      'Ponevas adress ändrades efter att {app} anslöts, så Poneva nekar den. Koppla från den och anslut sedan {app} igen med adressen ovan.',
    agentHintKeyWaiting: 'Den visas som Redo när en agent använder nyckeln.',
    agentHintWaiting: 'Ställ en fråga till {app} för att slutföra kontrollen av anslutningen.',
    agentHintTryAsking: 'Fråga till exempel:',
    agentFactCreated: 'Skapad',
    agentFactConnected: 'Ansluten',
    agentFactExpires: 'Går ut',
    agentFactSignInEnds: 'Inloggningen går ut',
    agentFactAccess: 'Åtkomst',
    agentNameNote:
      'Namnet är det som agenten själv har uppgett, och Poneva kan inte verifiera det. Koppla från agenter som du inte känner igen.',
    agentSignInNote: 'När inloggningen går ut ber agenten dig att logga in igen.',
    agentWorking: 'Arbetar…',
    agentRevokeKey: 'Återkalla nyckel',
    agentDisconnect: 'Koppla från',
    cardAgentsLabel: 'Dina anslutna agenter',
    cardMore: 'och {more}',
    cardMoreCount: 'one:till i Anslutningar|other:till i Anslutningar',
    cardOpenSetup: 'Öppna konfigurationen',
    cardChecking: 'Kontrollerar dina externa agenter…',
    cardTryAgain: 'Försök igen',
    connectedAgentsSubtitleAny: 'Agenter som läser ditt företags kunskap via Poneva.',
    scopeLibraryReadTitle: 'Läsa ditt arbete i Poneva',
    scopeLibraryReadDescribe:
      '{app} kan hitta och läsa de chattar, objekt i Bibliotek, filer, möten, skills, agenter och appar du kan öppna i Poneva, och läsa från dina anslutna tjänster. Den kan också läsa företagets shoppingassistenter och vad kunderna har sagt till dem.',
    scopeLibraryReadConfirmOn:
      'Låta {app} läsa ditt arbete i Poneva för {company}? Den hittar och läser det du redan kan öppna i Poneva, som du.',
    scopeLibraryReadConfirmOff:
      'Hindra {app} från att läsa ditt arbete i Poneva? Då kan den inte heller spara arbete eller fråga Poneva.',
    scopeLibraryReadEnabled:
      '{app} kan nu läsa ditt arbete i Poneva. Om alternativet inte syns i {app} än startar du om det.',
    scopeLibraryReadDisabled: '{app} kan inte längre läsa ditt arbete i Poneva.',
    scopeLibraryWriteTitle: 'Spara arbete och fråga Poneva',
    scopeLibraryWriteDescribe:
      '{app} kan spara arbete i ditt Bibliotek, där nytt arbete förblir privat för dig, och spara nya versioner av arbete du kan redigera (tidigare versioner finns kvar i Historik). Den kan spara utkast till skills och till design för företagets shoppingassistenter, och de syns för kollegor som kan redigera dem; bara en människa publicerar dem i Poneva. Den kan också fråga Poneva i privata chattar, vilket använder krediter. Den kan också starta läsningar av företagets webbutik för att åtgärda en shoppingassistent och ställa testfrågor till den; båda använder företagets krediter.',
    scopeLibraryWriteConfirmOn:
      'Låta {app} spara arbete i ditt Bibliotek för {company}, inklusive nya versioner av arbete du kan redigera och utkast som kollegor kan se, och fråga Poneva åt dig? Frågor använder krediter. Den kan också läsa ditt arbete i Poneva. Läsningar av butiken och testfrågor till shoppingassistenter använder också företagets krediter.',
    scopeLibraryWriteConfirmOff:
      'Hindra {app} från att spara arbete och fråga Poneva? Det den redan har sparat ligger kvar i ditt Bibliotek.',
    scopeLibraryWriteEnabled:
      '{app} kan nu spara arbete och fråga Poneva. Om alternativet inte syns i {app} än startar du om det.',
    scopeLibraryWriteDisabled: '{app} kan inte längre spara arbete eller fråga Poneva.',
  },
};

const languages = [
  'en',
  'bg',
  'cs',
  'da',
  'de',
  'el',
  'es',
  'et',
  'fi',
  'fr',
  'ga',
  'hr',
  'hu',
  'it',
  'lt',
  'lv',
  'mt',
  'nl',
  'pl',
  'pt',
  'ro',
  'sk',
  'sl',
  'sv',
] as const;

const copy = Object.fromEntries(
  languages.map((language) => [language, language === 'en' ? { ...en } : { ...others[language] }]),
) as NamespaceCatalog<ConnectionsCopyKey>;

export default copy;
